app.component('tyroMedipassWidget', {
    templateUrl: 'app/src/js/components/tyroMedipassWidget/tyro-medipass-widget.html',
    controller: 'tyroMedipassWidgetController',
    bindings: {
        patient: '<',
        providerId: '<',
        accounts: '<',
        // Items already recorded against the patient, used to seed the claim so the admin building it does
        // not re-key what the clinician picked.
        pendingItems: '<',
        // An assessed quote from the claims list, to be finished rather than built: the widget opens straight on
        // the lodgement step with this row loaded. See resumeQuotedClaim().
        resumeTransaction: '<',
        onComplete: '&'
    }
});

app.controller('tyroMedipassWidgetController', [
    '$scope', '$modal', '$filter', '$q', '$timeout', 'Session', 'TyroAPI', 'TyroClaimableItems', 'ReferralsAPI', 'ExceptionAPI', 'BillingAPI', 'TIMEZONE_TYPE', 'REFERRAL_PERIOD_TYPE', 'REFERENCE_TYPE',
    'AUSTRALIAN_HEALTH_FUND_TYPE', 'AUSTRALIAN_HEALTH_FUND_TYPE_TEST', 'AUSTRALIAN_HEALTH_FUND_TYPE_UNSUPPORTED',
    'AU_OVERSEAS_COVER_TYPE', 'ECLIPSE_PRESENTING_ILLNESS_TYPE', 'PatientRecordLiveUpdateService', 'InvoiceLiveUpdateService',
    function ($scope, $modal, $filter, $q, $timeout, Session, TyroAPI, TyroClaimableItems, ReferralsAPI, ExceptionAPI, BillingAPI, TIMEZONE_TYPE, REFERRAL_PERIOD_TYPE, REFERENCE_TYPE,
              AUSTRALIAN_HEALTH_FUND_TYPE, AUSTRALIAN_HEALTH_FUND_TYPE_TEST, AUSTRALIAN_HEALTH_FUND_TYPE_UNSUPPORTED,
              AU_OVERSEAS_COVER_TYPE, ECLIPSE_PRESENTING_ILLNESS_TYPE, PatientRecordLiveUpdateService, InvoiceLiveUpdateService) {

        var $ctrl = this;

        $ctrl.funderSelected = false;

        // The ECLIPSE (in-patient) flow runs as three steps, in the order ECLIPSE imposes - each is built out
        // of what the step before produced:
        //   VERIFY - OPVW patient verification, fired as the view opens, so the Medicare / health fund
        //            details can be corrected before anything is claimed.
        //   CLAIM  - the episode of care and its MBS items, and the OECW eligibility check over them. The
        //            backend persists the quote and returns its transaction_id, so we create no transaction
        //            here. Lodges nothing.
        //   LODGE  - the assessment, and the lodgement (IMCW) that promotes the quote in place to an
        //            ECLIPSE_CLAIM. Settles asynchronously by webhook.
        // Lodgement is only reachable through the check, as the quote is the only thing carrying the episode
        // of care to the server - see lodgeEclipseClaim(). All of this is served by the backend
        // calling the Medipass ECLIPSE API; the SDK is only used for Medicare and DVA.
        //
        // An overseas (OVSW) claim runs the same flow with the middle step removed, because overseas claiming
        // has no eligibility check to run: no part of the claim is processed by Medicare, so there is nothing
        // to quote against and the claim is persisted and lodged in one call. The rest of the form - items,
        // referral, informed financial consent, the processing class - is the same, so this is one flow with a
        // mode rather than two, and the mode is decided by the patient's cover rather than chosen.
        $ctrl.eclipseSetupActive = false;
        // Set when the flow was opened on a quote from the claims list rather than built from scratch. The step
        // is the same; what differs is that the benefit split was never stored, so the tiles show one figure.
        $ctrl.resumedFromClaimsList = false;
        $ctrl.INPATIENT_STEPS = [
            { key: 'VERIFY', label: 'Verify patient' },
            { key: 'CLAIM', label: 'Claim items & eligibility' },
            { key: 'LODGE', label: 'Lodge claim' }
        ];
        $ctrl.OVERSEAS_STEPS = [
            { key: 'VERIFY', label: 'Verify patient' },
            { key: 'CLAIM', label: 'Claim items & lodge' }
        ];
        // INPATIENT (IMCW) or OVERSEAS (OVSW). Set from the patient's health fund cover when the view opens.
        $ctrl.eclipseMode = 'INPATIENT';
        $ctrl.eclipseStep = 'VERIFY';
        // A patient with no health fund on their record. Everything ECLIPSE claims from a fund is then unavailable
        // to them, and what is left is Medicare only (MO) claiming - which is exactly the claim for a patient who
        // holds Medicare and nothing else. Set when the flow opens; see applySelectedPhiPolicy().
        $ctrl.medicareOnlyPatient = false;

        // The processing classes this claim can be lodged under. Rebuilt in place rather than returned fresh from a
        // function, for the same reason the policy options are: ng-options over a new array every digest never
        // settles.
        $ctrl.claimTypeOptions = [];

        // Which classes apply, which the channel and the patient's cover decide between them.
        //
        // The patient claim (PC) is on both. It is the one class where the funder pays the patient rather than the
        // practice: the patient settles the account in full and claims the benefit back themselves, so the practice
        // collects the whole charge and no benefit is ever recorded against the invoice.
        //
        // Medicare only (MO) is in-patient alone, and only for a patient with no fund. An overseas member has no
        // Medicare entitlement - that is the whole reason their claim is settled by the fund - so there is no
        // Medicare benefit for them to claim.
        function refreshClaimTypeOptions() {
            const options = $ctrl.isOverseas()
                ? [
                    { value: 'AG', name: 'Agreement (AG)' },
                    { value: 'SC', name: 'Scheme (SC)' },
                    { value: 'PC', name: 'Patient Claim (PC)' },
                    // MB is a different arrangement on each channel: in-patient it claims the Medicare benefit and
                    // assigns it to an agent, overseas there is no Medicare benefit and it directs the fund's
                    // payment to one instead.
                    { value: 'MB', name: 'Billing Agent (MB)' }
                ]
                : $ctrl.medicareOnlyPatient
                    ? [{ value: 'MO', name: 'Medicare Only (MO)' }]
                    : [
                        { value: 'AG', name: 'Agreement (AG)' },
                        { value: 'SC', name: 'Scheme (SC)' },
                        { value: 'PC', name: 'Patient Claim (PC)' },
                        { value: 'MB', name: 'Medicare Benefit only (MB)' },
                        { value: 'MO', name: 'Medicare Only (MO)' }
                    ];

            $ctrl.claimTypeOptions.length = 0;
            options.forEach(option => $ctrl.claimTypeOptions.push(option));

            // A class the new list does not carry cannot be lodged, so it is not left showing as though it could.
            // A single option is the answer rather than a question, so it is applied.
            if (!$ctrl.claimTypeOptions.some(option => option.value === $ctrl.eclipseConfig.claimTypeCode)) {
                $ctrl.eclipseConfig.claimTypeCode = $ctrl.claimTypeOptions.length === 1
                    ? $ctrl.claimTypeOptions[0].value
                    : '';
                $ctrl.onClaimTypeChanged();
            }
        }

        $ctrl.isOverseas = function () {
            return $ctrl.eclipseMode === 'OVERSEAS';
        };

        $ctrl.eclipseSteps = function () {
            return $ctrl.isOverseas() ? $ctrl.OVERSEAS_STEPS : $ctrl.INPATIENT_STEPS;
        };

        // Drives the stepper's tick / highlight / dimmed states off the one step value, so the indicator
        // cannot drift out of step with the view.
        $ctrl.eclipseStepState = function (key) {
            const steps = $ctrl.eclipseSteps();
            const current = steps.findIndex(step => step.key === $ctrl.eclipseStep);
            const position = steps.findIndex(step => step.key === key);
            if (position < current) return 'done';
            if (position === current) return 'active';
            return 'todo';
        };

        // What ECLIPSE accepts in sender_contact_name. Declared up here rather than beside the other field
        // maxima below because eclipseConfig is seeded from it, and a const read before its declaration is a
        // throw rather than an undefined.
        const SENDER_CONTACT_NAME_MAX_LENGTH = 40;

        $ctrl.eclipseConfig = {
            claimTypeCode: '',                 // AG / SC / PC / MB / MO (processing class)
            serviceTypeCode: 'S',              // O = General, P = Pathology, S = Specialist
            facilityId: practiceFacilityId(),  // the hospital / day surgery the practice operates out of
            providerNumber: '',                // principal provider; defaulted from the logged-in user in $onInit
            servicingProviderNumber: '',       // who rendered the service, when that is not the principal
            fundPayeeId: '',
            admissionDate: todayDate(),
            dischargeDate: '',
            sameDay: true,
            lengthOfStay: '',                  // expected nights, required by ECLIPSE when same-day is not set
            preExistingAilment: false,
            emergencyAdmission: false,
            compensationClaim: false,
            accident: false,
            accidentDate: '',
            presentingIllnessType: 'item',     // 'item' (MBS item number) or 'code' (presenting illness code)
            presentingIllnessItemNumber: '',
            presentingIllnessCode: '',
            accountReferenceId: '',            // the practice's own reference; defaults to the patient id
            // The claimant, on a patient claim: who settled the account and is owed the Medicare benefit back.
            // All blank is the ordinary case - the patient claims for themselves - and the server then sends the
            // patient as their own claimant. Filled in only when that cannot be true.
            claimantFirstName: '',
            claimantLastName: '',
            claimantDateOfBirth: '',
            claimantMedicareNumber: '',
            claimantMedicareIrn: '',
            invoiceReference: '',              // the practice's own transaction reference, traced to settlement
            ifcIssueCode: '',                  // V / W / X - how informed financial consent was obtained
            financialInterestDisclosure: false, // provider disclosed any financial interest (scheme claims)
            referralType: 'REFERRAL',          // REFERRAL / OVERRIDE / SELF_DEEMED
            referringProviderNumber: '',
            referralDate: '',
            referralPeriod: 'S',
            referralPeriodMonths: '',          // months a non-standard referral runs for, 1-97
            referralOverrideCode: '',
            selfDeemedCode: '',
            consent: false,                    // patient consent must be obtained before the check

            // Overseas (OVSW) only.
            hospital: true,                    // Y = admitted patient, N = out of hospital. Overseas claims
                                               // can carry either, which is why it is asked rather than assumed.
            billingAgentProviderNumber: practiceBillingAgentId(), // where the benefit is paid on an MB or MO claim
            benefitAssignmentAuthorised: false, // patient assigned their benefit to that billing agent
            senderContactName: practiceContactName(),   // who the fund calls with a question about the claim
            senderContactPhone: practiceContactPhone(),
            senderContactEmail: practiceContactEmail(),
            // Overseas patient (PC) claim only: whether the patient settled the account in full at the desk
            // before the claim went. A declaration rather than a figure read off an invoice, because an
            // overseas claim has no invoice until its own lodgement raises one - see buildOverseasRequest().
            accountPaid: false
        };

        // Referral validity. The form holds the code ECLIPSE takes; `apiValue` is what a stored
        // referral carries, so a referral loaded off the patient's record can fill this drop-down.
        // Taken from the shared constant, as the document uploader writes the values this reads.
        // How a manually recorded account payment was taken - the same list the claims screen offers, because it
        // is the same question and the same Xero field.
        $ctrl.REFERENCE_TYPES = REFERENCE_TYPE;

        $ctrl.REFERRAL_PERIODS = REFERRAL_PERIOD_TYPE.map(function (period) {
            return { value: period.code, apiValue: period.value, name: period.name };
        });

        // Used when no referral can be supplied.
        $ctrl.REFERRAL_OVERRIDE_CODES = [
            { value: 'L', name: 'Lost referral' },
            { value: 'H', name: 'Hospital / emergency admission' },
            { value: 'E', name: 'Emergency' },
            { value: 'N', name: 'Not required' }
        ];

        // Where the specialist determined the service was needed without a referral.
        $ctrl.SELF_DEEMED_CODES = [
            { value: 'SD', name: 'Self-determined' },
            { value: 'SS', name: 'Self-deemed second opinion' }
        ];

        // How informed financial consent was obtained. ECLIPSE requires this on agreement (AG) and scheme (SC)
        // claims, and a scheme claim will not accept verbal consent.
        $ctrl.IFC_ISSUE_CODES = [
            { value: 'W', name: 'Written consent obtained' },
            { value: 'V', name: 'Verbal consent obtained' },
            { value: 'X', name: 'Not obtained / not issued' }
        ];

        // What the eligibility check is quoted against, where the practice gives it as an illness code rather
        // than as the MBS item number of the principal procedure. The code leads each label because it is the
        // code the fund's policy names, so it is what the practice is reading off the policy and looking for.
        $ctrl.PRESENTING_ILLNESS_CODES = ECLIPSE_PRESENTING_ILLNESS_TYPE.map(function (illness) {
            return { value: illness.value, name: illness.value + ' - ' + illness.name };
        });

        $ctrl.eclipseItems = [newEclipseItem()];

        // Loaded on the first ECLIPSE open rather than in $onInit, as Medicare and DVA never need it. A
        // failed or empty load falls the form back to free-text item entry so it stays usable.
        $ctrl.claimableItems = [];
        $ctrl.claimableItemsLoading = false;
        $ctrl.claimableItemsError = null;

        // The referrals held for the patient, newest first, loaded on the first ECLIPSE open so the
        // referring provider details do not have to be read off the letter and re-keyed. A failed
        // or empty load leaves the referral fields to be filled in by hand.
        $ctrl.patientReferrals = [];
        $ctrl.referralsLoading = false;
        $ctrl.referralsError = null;
        // Which referral the form was loaded from, held as its key rather than the object. Every other select
        // on this form binds a primitive the same way, and a <select> over object values needs `track by` to
        // match the model back to an option - which is what made this one stop tracking selections after the
        // first. A string needs none of that machinery.
        $ctrl.selectedReferralKey = null;

        // Step 1 (OPVW) state.
        $ctrl.verification = null;
        $ctrl.verificationLoading = false;
        $ctrl.verificationError = null;
        $ctrl.medicareChanges = [];
        $ctrl.phiChanges = [];
        // The legs the funder matched on details other than the ones sent - return code 8005. Held apart from
        // the difference tables above because this one is carried through to lodgement; see buildPartialMatches().
        $ctrl.partialMatches = [];

        // Step 2, eligibility (OECW) state. The quote carries the transaction_id of the row the backend
        // persisted, which is what the lodgement step promotes.
        $ctrl.eclipseQuote = null;
        $ctrl.eclipseTotals = null;
        $ctrl.eclipseLoading = false;
        $ctrl.eclipseError = null;

        // Lodgement state, shared by the in-patient (IMCW) step 3 and the overseas (OVSW) single-step lodge.
        $ctrl.lodgedTransaction = null;
        $ctrl.lodgeLoading = false;
        $ctrl.lodgeError = null;

        let baseTransactionOptions = {};
        let pendingItemsSeeded = false;
        let referralsLoaded = false;
        // The presenting illness item number we filled in ourselves, so a number the practice typed is never
        // overwritten when the claim items change. See syncPresentingIllnessItemNumber().
        let autoFilledPresentingIllnessItemNumber = '';
        // The referral fields as we last filled them in, so the referral loaded on open never
        // overwrites what the practice has since typed. See referralFieldsUntouched().
        let autoFilledReferralFields = null;
        $ctrl.isLoading = true;
        $scope.generalModal = null;

        // `selected` is the drop-down choice; item_code and name are copied off it so the payload has the same
        // shape whether the item was picked or typed. `service_text` is the STUB code / supporting text for
        // the line, which is per-claim rather than a property of the item, so it is never seeded.
        function newEclipseItem() {
            return {
                selected: null, item_code: '', name: '', charge_amount: null, service_date: todayDate(),
                service_text: '',
                // Advanced per-service attributes, hidden behind the row's own toggle. Left unset rather than
                // defaulted: each one asserts something to Medicare about the service, so an untouched row has
                // to say nothing at all.
                reference: '', service_time: '', time_duration: null, number_of_patients_seen: null,
                duplicate_service_override: false, multiple_procedure_override: false, aftercare_override: false,
                service_quantity: null, self_deemed_code: '', lsp_number: '',
                scp_id: '', rule3_exempt: false, s4b3_exempt: false,
                accession_date: '', accession_time: '', collection_date: '', collection_time: '',
                advancedOpen: false
            };
        }

        // ECLIPSE caps service text at 50 characters and rejects the claim rather than truncating.
        $ctrl.SERVICE_TEXT_MAX_LENGTH = 50;
        $ctrl.SENDER_CONTACT_NAME_MAX_LENGTH = SENDER_CONTACT_NAME_MAX_LENGTH;

        // ECLIPSE takes 1-20 characters of account reference, except on a patient claim, which takes 1-9. The
        // shorter limit belongs to the class rather than to the channel, so it follows the processing class rather
        // than being applied to every claim.
        $ctrl.ACCOUNT_REFERENCE_MAX_LENGTH = 20;
        $ctrl.PATIENT_CLAIM_ACCOUNT_REFERENCE_MAX_LENGTH = 9;

        $ctrl.accountReferenceLimit = function () {
            return $ctrl.isPatientClaim()
                ? $ctrl.PATIENT_CLAIM_ACCOUNT_REFERENCE_MAX_LENGTH
                : $ctrl.ACCOUNT_REFERENCE_MAX_LENGTH;
        };

        $ctrl.isAccountReferenceTooLong = function () {
            return ($ctrl.eclipseConfig.accountReferenceId || '').trim().length > $ctrl.accountReferenceLimit();
        };

        // Measures the trimmed value, which is what the request carries, so the inline error and the
        // claim-blocking check cannot disagree.
        $ctrl.isServiceTextTooLong = function (item) {
            return serviceTextOf(item).length > $ctrl.SERVICE_TEXT_MAX_LENGTH;
        };

        function serviceTextOf(item) {
            return ((item && item.service_text) || '').trim();
        }

        // The facility the practice usually operates out of, set once rather than re-keyed on every claim.
        // Left editable, as a surgeon can operate at more than one hospital. Blank when unrecorded, which the
        // form then requires.
        function practiceFacilityId() {
            return (Session && Session.practice && Session.practice.tyro_facility_id) || '';
        }

        // The billing agent the practice's ECLIPSE benefits are paid to, set once on the practice rather than
        // re-keyed on every claim - the same arrangement as the facility above. Left editable for the same reason
        // too: it is the practice's usual agent, not a fact about this claim. Blank when unrecorded, which the form
        // then requires on the classes that cannot go without it.
        function practiceBillingAgentId() {
            return (Session && Session.practice && Session.practice.billing_agent_id) || '';
        }

        $ctrl.hasPracticeBillingAgentId = function () {
            return !!practiceBillingAgentId();
        }

        // Who the fund rings about an overseas claim, defaulted from the practice's own details rather than
        // re-keyed on every one - the same arrangement as the facility and the billing agent above, and left
        // editable for the same reason: a particular claim can be somebody else's to answer.
        //
        // The practice record carries no named billing contact, so the practice itself is named and its
        // administration email and phone are given. Those are where a billing query is answered, and a fund
        // holding a claim needs something to ask for. Blank where the practice has not recorded them, which is
        // allowed: ECLIPSE takes these as optional.
        //
        // Truncated to what ECLIPSE accepts. The input's maxlength stops a person typing past it but does
        // nothing to a value put in the model here, and a name one character over is refused on lodgement.
        function practiceContactName() {
            const name = (Session && Session.practice && Session.practice.name) || '';
            return String(name).trim().slice(0, SENDER_CONTACT_NAME_MAX_LENGTH);
        }

        function practiceContactPhone() {
            return String((Session && Session.practice && Session.practice.phoneNumber) || '').trim();
        }

        function practiceContactEmail() {
            return String((Session && Session.practice && Session.practice.email) || '').trim();
        }

        $ctrl.hasPracticeFacilityId = function () {
            return !!practiceFacilityId();
        };

        $ctrl.hasUserProviderId = function () {
            return !!asText($ctrl.providerId);
        };

        // The practice's IANA timezone, resolved from the session enum the same way the calendar does it.
        // Falls back to UTC rather than the browser clock: a claim date has to be the practice's calendar
        // date, not the date wherever the machine is sitting.
        function practiceTimezoneName() {
            const tzEnum = Session && Session.practice && Session.practice.timezone;

            const candidate = TIMEZONE_TYPE && TIMEZONE_TYPE.find(function (zone) {
                return zone && (zone.value === tzEnum || zone.name === tzEnum);
            });

            if (candidate && candidate.name && moment.tz.zone(candidate.name)) {
                return candidate.name;
            }
            if (tzEnum && moment.tz && moment.tz.zone(tzEnum)) {
                return tzEnum;
            }
            return 'UTC';
        }

        // Today in the practice's timezone, as a Date at browser midnight: `input[type=date]` renders its Date
        // in browser-local time, so carrying the practice's wall-clock time in would shift the day back.
        function todayDate() {
            const today = moment().tz(practiceTimezoneName()).format('YYYY-MM-DD');
            return moment(today, 'YYYY-MM-DD').toDate();
        }

        // ECLIPSE will not accept an admission or a service dated more than this many days before the day it
        // receives the request: "The Admission Date must not be more than 7 days before the Date of Receipt."
        // There is no ceiling to match it - a pre-op quote is dated in the future by design - so this is a floor
        // only.
        $ctrl.CLAIM_BACKDATE_LIMIT_DAYS = 7;

        // The floor as the YYYY-MM-DD the request carries, in the practice's timezone so that it moves at the
        // practice's midnight rather than the browser's. Recomputed on each call rather than cached at open:
        // this form is left open across a working day, and a claim that was inside the window when the screen
        // was opened can fall outside it before the practice presses the button.
        function earliestClaimableDateParam() {
            return moment().tz(practiceTimezoneName())
                    .subtract($ctrl.CLAIM_BACKDATE_LIMIT_DAYS, 'days')
                    .format('YYYY-MM-DD');
        }

        // Whether a date is behind that floor. Blank is not too early - it is missing, which is a different
        // complaint with a different message, and reporting both for one empty field says neither clearly.
        function isBeforeBackdateLimit(value) {
            const date = toDateParam(value);
            return !!date && date < earliestClaimableDateParam();
        }

        // Live hints on the fields themselves, the same way the admission-after-service rule is shown. Both are
        // rejections Medicare would otherwise send back days later, so they are said while the claim is still
        // being built rather than after it has gone.
        // In-patient only. The rule is one ECLIPSE applies on Medicare's behalf, and an overseas claim is
        // processed by no part of Medicare - so whether the same window binds it is not something this form
        // knows. An overseas claim is left to the server's answer rather than blocked here on a rule that may
        // not govern it: a claim wrongly refused on this screen cannot be lodged at all, while one wrongly sent
        // is refused the way it is today.
        $ctrl.admissionDateTooEarly = function () {
            return !$ctrl.isOverseas() && $ctrl.requiresAdmissionDate()
                    && isBeforeBackdateLimit($ctrl.eclipseConfig.admissionDate);
        };

        $ctrl.itemServiceDateTooEarly = function (item) {
            return !$ctrl.isOverseas() && !!item && isBeforeBackdateLimit(item.service_date);
        };

        $ctrl.anyItemServiceDateTooEarly = function () {
            return claimItems().some($ctrl.itemServiceDateTooEarly);
        };

        // Plain YYYY-MM-DD for the API. Serialising the Date straight into the payload would send it as UTC,
        // and Australian local midnight is the previous day in UTC - every date would reach Medicare a day
        // early.
        function toDateParam(value) {
            return value ? $filter('date')(value, 'yyyy-MM-dd') : null;
        }

        // `negative` is { label, onClick } for a dialog that offers a second button. Set or cleared on every call:
        // a dialog closed by its X never reaches onGeneralClick(), so relying on that to clear the button left it
        // on the next, unrelated dialog - offering an override that ran the check past every validation.
        function showValidationError(title, message, negative) {
            $scope.negativeButton = negative ? negative.label : null;
            $scope.onNegativeButtonClick = negative ? negative.onClick : null;
            $scope.generalModal = $modal({
                scope: $scope,
                templateUrl: "app/src/views/templates/modal.general.tpl.html",
                show: true,
                title: title,
                content: message,
                backdrop: true,
                keyboard: false
            });
        }

        $scope.onGeneralClick = function () {
            if ($scope.generalModal) $scope.generalModal.hide();
            // Dropped on the way out, so the next dialog does not inherit this one's second button.
            $scope.negativeButton = null;
        }

        // The server's own sentence where it has one, with the REST layer's wrapper taken off it - a refused
        // lodgement is a business answer ("This eligibility check was rejected: ...") and reads as a crash
        // shown through "Internal error while processing your request: [details: ...]". See
        // ExceptionAPI.serverMessage().
        function errorMessage(error, fallback) {
            return ExceptionAPI.serverMessage(error) || fallback;
        }

        // The host switches to this view and passes the row in the same digest, so the resume is driven off the
        // binding changing rather than off $onInit - which can run before the row has arrived.
        $ctrl.$onChanges = function (changes) {
            if (changes.resumeTransaction && $ctrl.resumeTransaction && !$ctrl.resumedFromClaimsList) {
                $ctrl.resumeQuotedClaim($ctrl.resumeTransaction);
            }
        };

        $ctrl.$onInit = function () {
            if ($ctrl.patient) {
                $ctrl.gender = ($ctrl.patient.gender === 'MALE') ? 'M' : ($ctrl.patient.gender === 'FEMALE') ? 'F' : 'O';
            }

            // The principal provider the benefit is paid to. Defaulted to the logged-in user's, which is right
            // on nearly every claim, and left editable because it is not always: a practice can have several
            // registered providers and the claim goes under whoever did the work.
            // Kept where it is already set: the first $onChanges runs before this, so a quote resumed from the
            // claims list has already put its own stored provider here.
            $ctrl.eclipseConfig.providerNumber = $ctrl.eclipseConfig.providerNumber || asText($ctrl.providerId) || '';

            if ($ctrl.accounts && $ctrl.accounts.length > 0) {
                const bankAccount = $ctrl.accounts.find(acc => acc.account_class === 'BANK');
                const revenueAccount = $ctrl.accounts.find(acc => acc.account_class !== 'BANK');
                $ctrl.accountPaymentId = bankAccount ? bankAccount.account_id : null;
                $ctrl.accountItemsId = revenueAccount ? revenueAccount.account_id : null;
            }

            TyroAPI.getAccessToken().then(function (resp) {
                $ctrl.accessToken = resp.data.access_token;
                $ctrl.hasError = false;
                baseTransactionOptions = {
                    providerNumber: asText($ctrl.providerId),
                    patient: {
                        refId: $ctrl.patient.id.toString(),
                        firstName: $ctrl.patient.firstName,
                        lastName: $ctrl.patient.lastName,
                        dob: asText($ctrl.patient.dateOfBirth),
                        sex: $ctrl.gender,
                    }
                };
            }).catch(function (error) {
                $ctrl.hasError = true;
            }).finally(function () {
                $ctrl.isLoading = false;
            });
        };

        // The Medipass SDK validates account fields with string regexes, so a number reaching it throws inside
        // the SDK's own prop validation ("$t.match is not a function") - the IRN is an integer on the patient
        // record, which is exactly that case. Missing values are left alone so an absent field stays absent.
        function roundToCents(amount) {
            return Math.round(amount * 100) / 100;
        }

        function asText(value) {
            return (value === null || value === undefined) ? value : String(value);
        }

        // The recorded items in the shape the Medipass SDK takes them: a dollar-formatted price string and a
        // plain calendar service date. Handed over as the transaction's starting items; the SDK's own item
        // editor stays open (disableModifyServiceItems is off) so they can still be corrected.
        function pendingClaimableItems() {
            return ($ctrl.pendingItems || [])
                .filter(item => item.item_code)
                .map(function (item) {
                    const claimableItem = {
                        itemCode: item.item_code,
                        serviceDateString: serviceDateString(item)
                    };

                    // An unpriced item goes without a price rather than a made-up figure, leaving it to be
                    // keyed into the SDK's item editor.
                    const fee = feeOrNull(item);
                    if (fee !== null) {
                        claimableItem.price = '$' + fee.toFixed(2);
                    }
                    if (item.name) {
                        claimableItem.serviceText = item.name;
                    }

                    return claimableItem;
                });
        }

        // The day the item was recorded, read in the practice's timezone - the claim has to carry the
        // practice's calendar date, not the browser's.
        function serviceDateString(item) {
            const recorded = item.date_created ? moment(item.date_created) : null;
            return (recorded && recorded.isValid())
                ? recorded.tz(practiceTimezoneName()).format('YYYY-MM-DD')
                : toDateParam(todayDate());
        }

        $ctrl.pendingClaimableItemCount = function () {
            return ($ctrl.pendingItems || []).filter(item => item.item_code).length;
        };

        // A plain string rather than the list itself: binding a function that rebuilds an array on every
        // digest would never settle.
        $ctrl.pendingItemCodes = function () {
            return ($ctrl.pendingItems || []).filter(item => item.item_code).map(item => item.item_code).join(', ');
        };

        // Sent when the transaction is created so the server takes exactly those records rather than guessing
        // from item codes - the codes the claim ends up with only reach us on the funder's response, and the
        // practice can change them in the SDK before then. Rebuilt in place, as the directive binds one-way.
        $ctrl.pendingClaimableItemIds = [];
        function refreshPendingClaimableItemIds() {
            const ids = ($ctrl.pendingItems || [])
                .map(item => item.tyro_claimable_item_id)
                .filter(Boolean);
            $ctrl.pendingClaimableItemIds.length = 0;
            ids.forEach(id => $ctrl.pendingClaimableItemIds.push(id));
        }

        // Standard Funder Logic (Medicare & DVA)
        $ctrl.selectFunder = function (funderType) {
            let finalOptions = angular.copy(baseTransactionOptions);
            finalOptions.funderData = { platform: 'funder' };

            // Selecting the funder creates the transaction, so the ids the claim is taking have to be in
            // place before the render options are set.
            refreshPendingClaimableItemIds();

            // Medicare and DVA take their items this way; ECLIPSE does not use the SDK and seeds its own form.
            const claimableItems = pendingClaimableItems();
            if (claimableItems.length > 0) {
                finalOptions.claimableItems = claimableItems;
            }

            if (funderType === 'medicare') {
                if (!$ctrl.patient.medicare_details || !$ctrl.patient.medicare_details.card_number) {
                    return showValidationError("Missing Information", "Valid Medicare card required.");
                }
                $ctrl.transactionType = 'MEDICARE_CLAIM';
                finalOptions.funder = 'medicare';
                finalOptions.patient.accountNumber = asText($ctrl.patient.medicare_details.card_number);
                finalOptions.patient.reference = asText($ctrl.patient.medicare_details.irn) || '1';
                finalOptions.funderData.medicare = {
                    bulkBilled: false, claimType: "patient", isInHospital: false
                };
                $ctrl.transactionOptions = finalOptions;
                $ctrl.funderSelected = true;

            } else if (funderType === 'dva') {
                if (!$ctrl.patient.dva_details || !$ctrl.patient.dva_details.file_number) {
                    return showValidationError("Missing Information", "DVA file number required.");
                }
                $ctrl.transactionType = 'DVA_CLAIM';
                finalOptions.funder = 'dva';
                finalOptions.patient.accountNumber = asText($ctrl.patient.dva_details.file_number);
                $ctrl.transactionOptions = finalOptions;
                $ctrl.funderSelected = true;
            }
        };

        // --- ECLIPSE in-patient view ---

        // Which of the patient's health fund policies this claim is verified and lodged against.
        //
        // ECLIPSE works one membership at a time and has no way of being given two: an OPVW verification carries
        // a single health fund account, and a claim is assessed by the one fund that account belongs to. A
        // patient can hold more than one policy, so which one is being claimed is a choice the practice makes -
        // and it is a consequential one, because the selected policy also decides the channel the claim goes
        // down and the fund that assesses it.
        //
        // Keyed by position, deliberately not by anything read off the policy, for the same reason the referral
        // drop-down below is: nothing on a policy is guaranteed unique, and a <select> whose options share a
        // value cannot tell them apart. The request carries the fund and membership number instead - see
        // phiPolicySelector().
        $ctrl.phiPolicyOptions = [];
        $ctrl.selectedPhiPolicyKey = null;
        // The policy the flow is actually built against, as against what the drop-down happens to be showing.
        // Held as the policy itself so it survives the options being rebuilt: a positional key remembered
        // across a policy being added or removed on the patient record would point at a different policy.
        let appliedPhiPolicy = null;

        // Rebuilt in place and holding precomputed labels, as ng-options over a function returning a new list
        // every digest would never settle.
        function refreshPhiPolicyOptions() {
            const policies = ($ctrl.patient && $ctrl.patient.au_private_health_insurance) || [];
            $ctrl.phiPolicyOptions.length = 0;
            policies.forEach(function (policy, index) {
                $ctrl.phiPolicyOptions.push({
                    key: String(index),
                    label: $ctrl.phiPolicyLabel(policy),
                    policy: policy
                });
            });
            // Keeps the choice already made rather than silently reverting it, and drops it when the policy it
            // was made on is no longer on the record.
            $ctrl.selectedPhiPolicyKey = keyOfPolicy(appliedPhiPolicy);
        }

        function keyOfPolicy(policy) {
            const option = policy && $ctrl.phiPolicyOptions.find(candidate => candidate.policy === policy);
            return option ? option.key : null;
        }

        $ctrl.phiPolicy = function () {
            const option = $ctrl.phiPolicyOptions.find(candidate => candidate.key === $ctrl.selectedPhiPolicyKey);
            return option ? option.policy : null;
        };

        // Every fund a patient record can carry, for the purpose of naming one. Wider than the funds that can be
        // claimed for: the staging-only test fund and the funds ECLIPSE no longer reaches are never offered as a
        // choice, but a patient already carrying one should read as that fund rather than as a blank.
        const ALL_HEALTH_FUNDS = AUSTRALIAN_HEALTH_FUND_TYPE
            .concat(AUSTRALIAN_HEALTH_FUND_TYPE_TEST)
            .concat(AUSTRALIAN_HEALTH_FUND_TYPE_UNSUPPORTED);

        function fundOf(policy) {
            return (policy && ALL_HEALTH_FUNDS.find(option => option.value === policy.health_fund_name)) || null;
        }

        // Which channel a policy's cover belongs to. OVHC and OSHC members are settled entirely by their fund
        // and hold no Medicare entitlement; DOMESTIC cover is claimed in-patient, alongside Medicare.
        function isOverseasPolicy(policy) {
            return !!policy &&
                (policy.overseas_cover_type === 'OVHC' || policy.overseas_cover_type === 'OSHC');
        }

        // One line per policy for the drop-down that claims against it: the fund and membership number the claim
        // is made under, then the cover type, which is what decides the channel and so tells two policies at the
        // same fund apart. A policy that is not active is said to be, since it is a reason not to claim on it;
        // an active one is not labelled, as that is the unremarkable case.
        $ctrl.phiPolicyLabel = function (policy) {
            if (!policy) {
                return '';
            }
            const fund = fundOf(policy);
            const cover = AU_OVERSEAS_COVER_TYPE.find(option => option.value === policy.overseas_cover_type);

            let label = (fund ? fund.name : policy.health_fund_name) || 'Unrecorded fund';
            if (policy.membership_number) {
                label += ' — #' + policy.membership_number;
            }
            label += ' — ' + (cover ? cover.name : 'cover type not recorded');
            if (policy.policy_status && policy.policy_status !== 'ACTIVE') {
                label += ' — ' + policy.policy_status;
            }
            return label;
        };

        // Sets the channel from the selected policy and reports what stops it being claimed on. The channel is
        // decided here rather than offered as a choice, because it is a fact about the cover rather than a
        // preference: an in-patient claim splits its settlement with Medicare, so it can only be made for
        // someone with a Medicare entitlement. Overseas visitor and student members have none, and their claims
        // are settled entirely by the fund. Lodging one down the other's channel does not fail safely - it is
        // rejected days later, by which time the patient has usually been discharged.
        function applySelectedPhiPolicy() {
            const policy = $ctrl.phiPolicy();

            // No fund on the record at all. That is not a half-filled claim, it is a patient who holds Medicare and
            // nothing else - claimable here as Medicare only (MO), on the in-patient channel, with no fund data in
            // it. The Medicare card is what such a claim needs instead, so that is what is asked for.
            if (!policy && $ctrl.phiPolicyOptions.length === 0) {
                const medicare = $ctrl.patient.medicare_details || {};
                if (!medicare.card_number) {
                    showValidationError("Missing Information",
                        "This patient has neither health fund details nor a Medicare card on file. ECLIPSE claims " +
                        "against one or the other: record the health fund policy, or the Medicare card for a " +
                        "Medicare only claim.");
                    return false;
                }
                $ctrl.medicareOnlyPatient = true;
                $ctrl.eclipseMode = 'INPATIENT';
                appliedPhiPolicy = null;
                refreshClaimTypeOptions();
                return true;
            }

            if (!policy) {
                showValidationError("Missing Information", "Select which of the patient's health fund policies this claim is made against.");
                return false;
            }
            $ctrl.medicareOnlyPatient = false;
            // A fund no ECLIPSE request can be made against, either because Tyro does not transact with it or
            // because the record names a fund this build no longer knows. Reported here rather than left to the
            // claim, because there is no code to claim under: the request would go without one and come back
            // declined minutes or days later, with nothing on it naming the fund as the reason.
            const fund = fundOf(policy);
            if (!fund || !fund.code) {
                showValidationError("Fund Not Supported",
                    (fund ? fund.name : "The fund on this policy") + " cannot be claimed for through ECLIPSE. " +
                    "Claim it outside ECLIPSE, or record the policy this patient holds with a participating fund " +
                    "and claim against that one.");
                return false;
            }
            // Not defaulted to domestic. A policy recorded before the distinction was captured looks exactly
            // like a domestic one, so guessing would send overseas patients down the path that cannot pay them.
            if (!policy.overseas_cover_type) {
                showValidationError("Cover Type Required",
                    "This policy's cover is not recorded as domestic, overseas visitor (OVHC) or overseas " +
                    "student (OSHC). Set it on the patient's health fund details before claiming - it decides whether " +
                    "the claim is lodged as an in-patient claim or an overseas one, and the two are not interchangeable.");
                return false;
            }
            // The Medicare card the ECLIPSE button no longer asks for up front, asked for here instead - where
            // it is the claim's own requirement rather than the channel's. An in-patient claim is settled partly
            // by Medicare, so a domestic policy cannot be claimed on without one. A patient holding an overseas
            // policy alongside a domestic one reaches this by choosing the domestic policy, and the answer is
            // either to record the card or to claim against the overseas policy instead.
            if (!isOverseasPolicy(policy) && !$ctrl.hasMedicareOnRecord()) {
                showValidationError("Medicare Card Required",
                    "This policy is domestic cover, and an in-patient ECLIPSE claim is settled partly by " +
                    "Medicare. Record the patient's Medicare card before claiming against it. Overseas visitor " +
                    "(OVHC) and student (OSHC) policies are claimed without one.");
                return false;
            }
            $ctrl.eclipseMode = isOverseasPolicy(policy) ? 'OVERSEAS' : 'INPATIENT';
            appliedPhiPolicy = policy;
            // The channel decides which classes apply, and it has just been set.
            refreshClaimTypeOptions();
            return true;
        }

        // Everything downstream of the policy: the verification of it, the quote made against it, and the claim
        // lodged from that quote. Cleared together, because each is only meaningful for the fund it was made
        // against.
        function resetEclipseFlowState() {
            // Cleared here so re-running a check from a resumed flow puts it back on the normal footing, with the
            // fresh quote's own benefit split. resumeQuotedClaim() sets it again after calling this.
            $ctrl.resumedFromClaimsList = false;
            $ctrl.lodgementIssues = [];
            $ctrl.lodgementIssuesLoading = false;
            $ctrl.eclipseStep = 'VERIFY';
            $ctrl.verification = null;
            $ctrl.verificationError = null;
            $ctrl.medicareChanges = [];
            $ctrl.phiChanges = [];
            $ctrl.partialMatches = [];
            $ctrl.eclipseQuote = null;
            $ctrl.eclipseTotals = null;
            $ctrl.eclipseError = null;
            $ctrl.lodgedTransaction = null;
            $ctrl.lodgeError = null;
        }

        // Opens the ECLIPSE view and verifies the patient's account details. PHI details are required, as the
        // health fund membership is what is verified and claimed against.
        $ctrl.initEclipseSetup = function () {
            refreshPhiPolicyOptions();
            // Defaulted so that a patient holding one policy - nearly all of them - opens straight onto a
            // verification rather than an unanswered question. Where there is more than one the drop-down is
            // shown alongside it, so the default is visible and changeable before anything is claimed.
            //
            // The default is the first policy that can actually be claimed on rather than simply the first: a
            // patient can hold a half-recorded policy alongside a good one, and opening on the unusable one
            // would report a problem the practice does not have and give them no way to reach the policy they
            // do. Falls back to the first when none qualify, so what is reported is a real policy's real problem.
            if (!$ctrl.phiPolicy() && $ctrl.phiPolicyOptions.length > 0) {
                const claimable = $ctrl.phiPolicyOptions.find(option => {
                    const optionFund = fundOf(option.policy);
                    return !!option.policy.overseas_cover_type && !!(optionFund && optionFund.code) &&
                        (isOverseasPolicy(option.policy) || $ctrl.hasMedicareOnRecord());
                });
                $ctrl.selectedPhiPolicyKey = (claimable || $ctrl.phiPolicyOptions[0]).key;
            }
            if (!applySelectedPhiPolicy()) {
                return;
            }

            resetEclipseFlowState();
            $ctrl.eclipseSetupActive = true;

            // Seeded either way: a failed reference load leaves the form on free-text entry, which the pending
            // items can still fill in.
            loadClaimableItems().finally(seedItemsFromPending);
            loadPatientReferrals().then(seedReferralFromLatest);
            $ctrl.runPatientVerification();
        };

        /**
         * Opens the flow on its last step for a quote the practice already ran, so finishing a claim is not
         * walking back through verification and a second eligibility check.
         *
         * Only display state is rebuilt here, and deliberately so: lodgement promotes the stored row server side
         * (see promoteEclipseEligibility), so what the claim actually carries - the episode of care, the referral,
         * the consent codes - comes off the database rather than from anything reconstructed in the browser. What
         * this needs is only enough to show the step and to gate it correctly:
         *
         *   - the processing class, because the patient-claim account gate hangs off it;
         *   - the items, for the account total that gate is measured against;
         *   - the quote's id and status, which is what the Lodge button lodges and what decides it may;
         *   - the totals, for the summary tiles and the gap prompt.
         *
         * The fund and Medicare legs of the benefit are not stored per item - the quote response splits them, the
         * transaction row keeps one figure - so a resumed quote shows the funder benefit as one number rather than
         * inventing a split. See resumedQuoteTotals().
         */
        $ctrl.resumeQuotedClaim = function (transaction) {
            if (!transaction || !transaction.transaction_id) {
                return;
            }

            resetEclipseFlowState();

            $ctrl.eclipseSetupActive = true;
            $ctrl.resumedFromClaimsList = true;
            $ctrl.eclipseMode = transaction.transaction_type === 'ECLIPSE_OVERSEAS_CLAIM' ? 'OVERSEAS' : 'INPATIENT';

            if (transaction.processing_class) {
                $ctrl.eclipseConfig.claimTypeCode = transaction.processing_class;
            }
            refreshClaimTypeOptions();

            // The rest of what the row actually stores. Not needed to lodge - the server rebuilds the claim from
            // the row - but the step reads some of it, and a config that is emptier than the claim it describes is
            // how a resumed quote came to be refused for a presenting illness it had recorded all along.
            const cfg = $ctrl.eclipseConfig;
            cfg.serviceTypeCode = transaction.service_type_code || cfg.serviceTypeCode;
            cfg.facilityId = transaction.facility_id || '';
            cfg.providerNumber = transaction.provider_number || cfg.providerNumber;
            cfg.admissionDate = transaction.admission_date || '';
            cfg.dischargeDate = transaction.discharge_date || '';
            cfg.accountReferenceId = transaction.account_reference_id || '';
            cfg.invoiceReference = transaction.invoice_reference || '';
            cfg.presentingIllnessItemNumber = transaction.presenting_illness_item_number || '';
            cfg.presentingIllnessCode = transaction.presenting_illness_code || '';
            cfg.presentingIllnessType = transaction.presenting_illness_item_number ? 'item' : 'code';
            cfg.ifcIssueCode = transaction.ifc_issue_code || '';

            // The items as they were quoted. Rebuilt into the shape the form's own rows use, so claimItemsTotal()
            // and everything reading off it works on a resumed claim exactly as it does on a fresh one.
            $ctrl.eclipseItems.length = 0;
            (transaction.transaction_items || []).forEach(function (item) {
                $ctrl.eclipseItems.push({
                    selected: null,
                    item_code: item.item_code,
                    name: item.name,
                    charge_amount: item.charge_amount,
                    service_date: item.service_date,
                    service_text: item.service_text,
                });
            });

            // Enough of a quote for isQuoteLodgeable() and quotedTransactionId() to answer. Status is carried
            // through so a quote the funder refused, or has not answered, is refused here as it would be there.
            $ctrl.eclipseQuote = {
                transaction_id: transaction.transaction_id,
                status: transaction.status,
                assessment_code: transaction.health_fund_assessment_code,
                services: [],
            };
            $ctrl.eclipseTotals = resumedQuoteTotals(transaction);

            // The account as it already stands. Without this a patient claim resumed after its account was
            // settled came back reading as unpaid - the panel offered to raise an invoice that already existed,
            // and the Lodge button stayed disabled on money that had in fact been taken.
            //
            // The claims list attaches the invoice to every row it returns, so this is the same figures the
            // account panel would have read for itself.
            if (transaction.invoice_id) {
                $ctrl.patientClaimInvoice = transaction.invoice || null;
                $ctrl.patientClaimTerminalTarget = {
                    invoice_id: transaction.invoice_id,
                    transaction_id: transaction.transaction_id,
                };
            }

            $ctrl.eclipseStep = 'LODGE';
            $ctrl.refreshLodgementIssues();
        };

        /**
         * Asks the server whether this quote can actually be lodged, and shows what is stopping it.
         *
         * The lodgement step is built from the stored row, not from the form, so the form cannot vouch for it -
         * an item saved without its MBS code, a missing admission date, a consent code never recorded, all sit
         * outside anything this screen displays. Lodging would surface them one at a time, and on a patient claim
         * only after the money had been taken. So they are read up front, all of them, and the Lodge button waits.
         */
        /**
         * Re-reads everything the lodgement step gates on: the account, and whether the stored row can be lodged.
         *
         * Both, because they answer the same question from different sides and the practice pressing refresh is
         * asking that question, not either half of it.
         */
        $ctrl.refreshAccount = function () {
            $ctrl.raisePatientClaimAccount();
            $ctrl.refreshLodgementIssues();
        };

        $ctrl.refreshLodgementIssues = function () {
            const quotedTransactionId = $ctrl.quotedTransactionId();
            if (!quotedTransactionId) {
                return;
            }
            $ctrl.lodgementIssuesLoading = true;
            TyroAPI.getLodgementIssues(quotedTransactionId, $ctrl.patient.id).then(function (response) {
                $ctrl.lodgementIssues = response.data || [];
            }).catch(function () {
                // Left empty rather than blocking: the server refuses the lodgement itself if anything is wrong,
                // so a failed pre-check costs the practice a clearer message, not the claim.
                $ctrl.lodgementIssues = [];
            }).finally(function () {
                $ctrl.lodgementIssuesLoading = false;
            });
        };

        /**
         * The summary figures for a resumed quote, off the stored row.
         *
         * charge and the funder's benefit are both recorded per item; the gap is what is left of the charge once
         * the benefit is paid. The fund and Medicare split is not recorded, so healthFundBenefit and
         * medicareBenefit are left null and the tiles that show them are dropped rather than guessed at - see the
         * summary in the template.
         */
        function resumedQuoteTotals(transaction) {
            const items = transaction.transaction_items || [];
            if (items.length === 0) {
                return null;
            }
            const charge = items.reduce((total, item) => total + (item.charge_amount || 0), 0);
            const benefit = items.reduce((total, item) => total + (item.benefit_amount || 0), 0);
            return {
                charge: charge,
                funderBenefit: benefit,
                healthFundBenefit: null,
                medicareBenefit: null,
                gap: Math.max(roundToCents(charge - benefit), 0),
            };
        }


        // Switching policy switches the account every step of the flow is built on, so the flow starts again
        // rather than carrying a verification of one fund, or a quote from it, into a claim against another.
        // The quote is discarded with it: it was recorded server side as a lodgeable transaction against the
        // fund it was quoted from, and left behind it would sit in the claims list as a claim for this episode
        // of care that no longer matches the policy on the form.
        $ctrl.onPhiPolicySelected = function () {
            if (!applySelectedPhiPolicy()) {
                // Put the drop-down back to the policy the rest of the view is still describing. Left showing
                // the rejected one, it would read as the policy being claimed against while the verification,
                // the quote and the channel all still belong to the previous one.
                $ctrl.selectedPhiPolicyKey = keyOfPolicy(appliedPhiPolicy);
                return;
            }
            discardSupersededQuote($ctrl.eclipseQuote ? $ctrl.eclipseQuote.transaction_id : null);
            resetEclipseFlowState();
            $ctrl.runPatientVerification();
        };

        // The policy an ECLIPSE request is made against. Sent on every one of them because ECLIPSE verifies and
        // claims a single membership at a time, so each has to say which of the patient's policies it means.
        // Only selects: the account details themselves are read off the patient record server side, so a claim
        // can never be lodged against numbers the browser made up.
        function phiPolicySelector() {
            const policy = $ctrl.phiPolicy() || {};
            return {
                phi_health_fund_name: policy.health_fund_name || null,
                phi_membership_number: policy.membership_number || null
            };
        }

        // Notifies the host screen so it can refresh the claims list and show the new transaction.
        $ctrl.finishEclipse = function () {
            $ctrl.eclipseSetupActive = false;
            if ($ctrl.onComplete) {
                $ctrl.onComplete({ status: 'success', payload: $ctrl.lodgedTransaction || $ctrl.eclipseQuote });
            }
        };

        // A claim the funder refused outright on lodgement, rather than one it took away to assess. It went to
        // Medicare and came straight back, so nothing further is coming for it - no webhook, no assessment, no
        // benefit - and it must not be shown as lodged and pending. The practice sees the refusal here, while the
        // patient is still in front of them and the claim is still correctable, instead of finding it in the
        // claims list days later having waited for an answer that already arrived.
        $ctrl.lodgedClaimWasRefused = function () {
            const lodged = $ctrl.lodgedTransaction;
            return !!lodged && (lodged.status === 'REJECTED' || lodged.status === 'FAILED');
        };

        /**
         * Which of the two ways an overseas claim gets refused this one was, because what it means for the money
         * is the opposite in each.
         *
         * Refused on submission, the ECLIPSE gateway turned the claim away before any fund saw it. No invoice was
         * raised - the server skips invoicing a REJECTED or FAILED lodgement, see raiseClaimInvoice() - and no
         * processing report will ever be published for it.
         *
         * Refused in the processing report, the claim did reach the fund, was invoiced as it went, and the fund
         * refused it afterwards. The server voids that invoice when the report is read, see
         * voidInvoiceOfOverseasClaimTheReportRefused() - except on a patient claim the patient had already
         * settled, where the practice was paid for the work and the refusal is the patient's benefit to lose.
         *
         * Told apart by the report status, which is why the server keeps Medipass's own verdicts in Medipass's own
         * words rather than dressing them up as fund refusals: REJECTED and DECLINED are the gateway's answer, and
         * HEALTH_FUND_REJECTED and MEDICARE_REJECTED come out of a report.
         */
        $ctrl.overseasRefusedOnSubmission = function () {
            if (!$ctrl.lodgedClaimWasRefused()) {
                return false;
            }
            const status = $ctrl.lodgedTransaction.overseas_report_status;
            // No status at all is a refusal that came back carrying nothing this recognises, which is the gateway's
            // doing rather than a fund's: a fund's refusal arrives in a report, and a report has a status.
            return !status || status === 'REJECTED' || status === 'DECLINED';
        };

        // The lines the funder refused, with the reason it gave against each. The claim-level message only says
        // the claim was declined; this is what says which item and why, and so whether it is worth correcting
        // and re-running or writing off.
        $ctrl.refusedClaimItems = function () {
            const lodged = $ctrl.lodgedTransaction;
            if (!lodged || !lodged.transaction_items) {
                return [];
            }
            return lodged.transaction_items.filter(function (item) {
                return item.status === 'REJECTED' || item.status === 'FAILED';
            });
        };

        // Whether the claim just lodged still leaves money to collect from the patient, here and now.
        //
        // A scheme claim is invoiced at the full charge, so the funder's benefit settles only part of that invoice
        // and the gap stays owing on it. Nothing in CatTrax chases that gap: once this screen closes, it is only
        // found by somebody going looking at the claims list. So it is said here, at the moment the claim goes and
        // while the patient is still in front of them, with the way to take it one click away.
        //
        // Only where there is an invoice to take it against - a claim refused on lodgement raised none, and one
        // whose invoice could not be raised has nothing to pay into until the funder's approval raises it.
        //
        $ctrl.lodgedClaimNeedsPatientPayment = function () {
            const lodged = $ctrl.lodgedTransaction;
            if (!lodged || !lodged.invoice_id
                    || lodged.status === 'REJECTED' || lodged.status === 'FAILED') {
                return false;
            }
            // A patient claim only where it is an overseas one. Overseas carries no Medicare component and no
            // cheque: its invoice is raised by the lodgement, so the payment could not be taken before the claim
            // went and is taken here instead - which is what the lodge step said would happen.
            //
            // An in-patient patient claim is never this. Its account is settled before the claim goes, and one
            // that went unpaid is waiting on a cheque rather than on a card - see
            // lodgedPatientClaimAwaitingCheque().
            if ($ctrl.isPatientClaim()) {
                return $ctrl.isOverseas() && !lodged.account_paid && $ctrl.claimItemsTotal() > 0;
            }
            // Otherwise only where the gap is a known figure. A quote that priced it at nothing leaves nothing to
            // collect; a claim with no quote at all - Medicare only, or overseas - leaves nobody able to say what
            // the gap is until the funder assesses it, and the claims screen refuses a payment until then. Offering
            // one here would send the practice to a screen that turns them away. See lodgedClaimGapNotKnownYet().
            return !!$ctrl.eclipseTotals && $ctrl.eclipseTotals.gap > 0;
        };

        /**
         * A patient claim that went as unpaid, and is now waiting on a cheque rather than on a payment.
         *
         * This is the unpaid pathway arriving where it leads. Lodged unpaid, Medicare and the fund do not pay the
         * practice and do not pay the patient's bank account either: they issue a cheque made payable to the
         * provider and post it to the patient, who has to bring it in. So there is nothing to take at the desk
         * today, and offering to take it invites a payment that leaves the practice paid twice when the cheque
         * turns up - which is why this replaces the payment prompt rather than sitting beside it.
         *
         * Read off the lodged claim rather than off this screen: accountPaidInd was derived server side from what
         * was actually collected, so the row is what knows which pathway the claim took.
         */
        $ctrl.lodgedPatientClaimAwaitingCheque = function () {
            const lodged = $ctrl.lodgedTransaction;
            if (!lodged || !lodged.invoice_id
                    || lodged.status === 'REJECTED' || lodged.status === 'FAILED') {
                return false;
            }
            // In-patient only. An overseas claim is not processed by Medicare at all, so nothing about it
            // produces a cheque - it takes the payment prompt above instead.
            return $ctrl.isPatientClaim() && !$ctrl.isOverseas()
                && !lodged.account_paid && $ctrl.claimItemsTotal() > 0;
        };

        // The other half of that: a claim that will leave the patient a gap, but not one anybody can put a figure
        // on yet. Said rather than left silent, because the practice still has to collect it - just not today.
        // A patient claim is never this: what the patient owes is the charge, which is known before the claim goes.
        $ctrl.lodgedClaimGapNotKnownYet = function () {
            const lodged = $ctrl.lodgedTransaction;
            if (!lodged || !lodged.invoice_id
                    || lodged.status === 'REJECTED' || lodged.status === 'FAILED') {
                return false;
            }
            return !$ctrl.isPatientClaim() && !$ctrl.eclipseTotals;
        };

        // A patient claim the practice recorded as already settled. Said as plainly as the amount owing is, so a
        // claim with no prompt beside it does not read as one nobody has looked at.
        $ctrl.lodgedPatientClaimAlreadyPaid = function () {
            const lodged = $ctrl.lodgedTransaction;
            if (!lodged || !lodged.invoice_id
                    || lodged.status === 'REJECTED' || lodged.status === 'FAILED') {
                return false;
            }
            return $ctrl.isPatientClaim() && !!lodged.account_paid;
        };

        // What the patient still owes the practice on the lodged claim. On a patient claim that is the whole charge,
        // because the funder's benefit is paid to the patient rather than into this invoice; on every other class it
        // is the gap the eligibility check quoted, and an overseas claim has no check to quote from - so the prompt
        // names no figure rather than an invented one.
        $ctrl.quotedPatientGap = function () {
            if ($ctrl.isPatientClaim()) {
                return $ctrl.claimItemsTotal() > 0 ? $ctrl.claimItemsTotal() : null;
            }
            return ($ctrl.eclipseTotals && $ctrl.eclipseTotals.gap > 0) ? $ctrl.eclipseTotals.gap : null;
        };

        /**
         * Leaves the claiming screen when the lodgement finished the job.
         *
         * The claim is away and there is nothing on this screen left to do with it - no money to collect, and no
         * refusal to read - so it closes rather than parking the practice on a summary they can only dismiss. The
         * lodged claim is on the claims list behind it either way.
         *
         * Deliberately not on every lodgement. A claim the funder refused on the spot has to be read, a claim that
         * leaves the patient owing something has a payment to take, and a patient claim that went unpaid leaves the
         * practice waiting on a cheque they have to be told about - all of those keep the screen open.
         *
         * An unpriced gap is not one of them on the overseas channel. Every overseas claim has one - there is no
         * eligibility check to quote a gap from, so eclipseTotals is never set - which held the outcome panel
         * open on every single lodgement to say the one thing that is always true of them. The note still stands
         * where it means something: an in-patient claim that went without a quote. The gap is collected off the
         * claims list either way, once the fund has assessed it.
         */
        function closeIfLodgementNeedsNothingFurther() {
            if ($ctrl.lodgedClaimWasRefused()
                    || $ctrl.lodgedClaimNeedsPatientPayment()
                    || $ctrl.lodgedPatientClaimAwaitingCheque()
                    || ($ctrl.lodgedClaimGapNotKnownYet() && !$ctrl.isOverseas())) {
                return;
            }
            $ctrl.eclipseSetupActive = false;
            if ($ctrl.onComplete) {
                $ctrl.onComplete({ status: 'LODGED_COMPLETE', payload: $ctrl.lodgedTransaction });
            }
        }

        // Hands the lodged claim to the claims screen to take the payment against, rather than closing the widget
        // and leaving the practice to find the claim in the list again.
        $ctrl.takePatientPayment = function () {
            $ctrl.eclipseSetupActive = false;
            if ($ctrl.onComplete) {
                $ctrl.onComplete({ status: 'TAKE_PAYMENT', payload: $ctrl.lodgedTransaction });
            }
        };

        // The processing class in words, article included, for the callouts that explain what it means for the
        // invoice. MB is a different arrangement on each channel, so it is named for the one in use.
        $ctrl.claimTypeLabel = function () {
            switch ($ctrl.eclipseConfig.claimTypeCode) {
                case 'AG': return 'An agreement';
                case 'SC': return 'A scheme';
                case 'PC': return 'A patient';
                case 'MO': return 'A Medicare only';
                case 'MB': return $ctrl.isOverseas() ? 'A billing agent' : 'A Medicare-benefit-only';
                default: return 'This';
            }
        };

        // --- Step 1: OPVW patient verification ---

        // Runs against the details currently on the patient record, so it is safe to re-run after a correction.
        $ctrl.runPatientVerification = function () {
            $ctrl.verificationLoading = true;
            $ctrl.verificationError = null;
            $ctrl.verification = null;
            $ctrl.medicareChanges = [];
            $ctrl.phiChanges = [];
            $ctrl.partialMatches = [];

            const request = angular.extend({
                patient_id: $ctrl.patient.id,
                provider_number: providerNumberParam($ctrl.eclipseConfig.providerNumber),
                service_date: toDateParam($ctrl.eclipseConfig.admissionDate) || toDateParam(todayDate())
            }, phiPolicySelector());

            TyroAPI.verifyEclipsePatient(request).then(function (response) {
                $ctrl.verification = response.data;

                const medicare = $ctrl.patient.medicare_details || {};
                const policy = $ctrl.phiPolicy() || {};
                // Precomputed: ng-repeat over a function that builds a new array every digest never settles.
                $ctrl.medicareChanges = accountDifferences($ctrl.verification.medicare_status, medicare.card_number, medicare.irn);
                $ctrl.phiChanges = accountDifferences($ctrl.verification.phi_status, policy.membership_number, policy.member_reference_number);
                // Carried through to lodgement rather than read off the panels above, which are two steps back
                // by the time the claim goes.
                $ctrl.partialMatches = buildPartialMatches(medicare, policy);
            }).catch(function (error) {
                $ctrl.verificationError = errorMessage(error, "Unable to verify the patient's account details. Please try again.");
            }).finally(function () {
                $ctrl.verificationLoading = false;
            });
        };

        // The "on record" vs "returned by the funder" rows for one account: where what came back disagrees
        // with what was sent. This is the list the practice reconciles, and - see isAccountVerified() - it is
        // also what decides whether the account reads as verified at all.
        function accountDifferences(status, currentNumber, currentReference) {
            const differences = [];
            if (!status) {
                return differences;
            }
            addDifference(differences, 'Membership / card number', currentNumber, status.suggested_membership_number);
            addDifference(differences, 'Reference number / card rank', currentReference, status.suggested_card_rank);
            addDifference(differences, 'First name', $ctrl.patient.firstName, status.suggested_first_name);
            addDifference(differences, 'Last name', $ctrl.patient.lastName, status.suggested_last_name);
            return differences;
        }

        // Only where the two actually disagree. The funder returns the account it matched whether or not
        // anything about it changed, so listing every field that came back would report a clean check as four
        // discrepancies and train the practice to scroll past the table that matters.
        function addDifference(differences, label, current, suggested) {
            if (suggested && !sameDetail(current, suggested)) {
                differences.push({ label: label, current: current, suggested: suggested });
            }
        }

        // Compared trimmed and case-insensitively: the funders echo values back in their own casing, and a row
        // saying SMITH differs from Smith is a discrepancy the practice cannot do anything about.
        function sameDetail(current, suggested) {
            const normalise = value => String(value === null || value === undefined ? '' : value).trim().toUpperCase();
            return normalise(current) === normalise(suggested);
        }

        // Verified means the funder matched the patient AND matched them on the details we sent.
        //
        // The flags alone do not say that. Services Australia reports a partial match as a match - "The
        // individual has been matched using the submitted data however differences were identified. Please
        // check the information returned and update your records." - with patientVerified set and the
        // corrected details attached. Read on the flags alone, a claim under the wrong name passes as a clean
        // check, and the practice lodges on the very details Medicare has just said are wrong.
        //
        // So what came back has a say too: an account the funder corrected is one that needs attention, which
        // is what the message asks the practice to do.
        //
        // The return code is read here as well as the flags, rather than trusting the backend to have turned
        // update_required on over it: a match made on different details is not a verified account whichever of
        // the two says so, and read only through the flag one deployment behind on that rule would put this
        // screen back to reporting a partial match as a clean check.
        $ctrl.isAccountVerified = function (status, differences) {
            return !!status && status.patient_verified === true && status.update_required !== true
                && !isPartialMatch(status)
                && (differences || []).length === 0;
        };

        // --- Partial match (return code 8005) ---

        // The sentence Services Australia sends with a partial match, said verbatim at lodgement. It is the
        // instruction the practice has to act on - the details Medicare holds differ from the ones the claim is
        // about to be lodged on - so it is quoted rather than paraphrased, and lives here rather than in the
        // template so the two places that raise it cannot drift apart.
        $ctrl.PARTIAL_MATCH_MESSAGE = 'The individual has been matched using the submitted data however ' +
            'differences were identified. Please check the information returned and update your records.';

        // Recognised by return code and, because the codes are Services Australia's to add to, by the sentence
        // they send with it - the same two tests the backend applies when it forces update_required on.
        const MATCHED_WITH_DIFFERENCES_CODES = ['8005'];

        function isPartialMatch(status) {
            if (!status) {
                return false;
            }
            if (MATCHED_WITH_DIFFERENCES_CODES.indexOf((status.status_code || '').trim()) !== -1) {
                return true;
            }
            return (status.status_text || '').toLowerCase().indexOf('differences were identified') !== -1;
        }

        // Everything the funder returned about the individual it matched, not only what disagrees with the
        // record: "please check the information returned" is the whole of what an 8005 asks for, and a partial
        // match whose corrected detail happens to be one the record already holds would otherwise raise an
        // alert with nothing underneath it. What differs is marked rather than filtered to.
        function returnedDetails(status, numberLabel, referenceLabel, currentNumber, currentReference) {
            return [
                { label: numberLabel, current: currentNumber, returned: status.suggested_membership_number },
                { label: referenceLabel, current: currentReference, returned: status.suggested_card_rank },
                { label: 'First name', current: $ctrl.patient.firstName, returned: status.suggested_first_name },
                { label: 'Last name', current: $ctrl.patient.lastName, returned: status.suggested_last_name }
            ].filter(function (detail) {
                return !!detail.returned;
            }).map(function (detail) {
                detail.differs = !sameDetail(detail.current, detail.returned);
                return detail;
            });
        }

        // A partial match is the one answer that reads as a pass and is not one: the account comes back
        // verified, and nothing but the return code beside it says the claim is about to be lodged on details
        // Medicare has already corrected. So it is followed all the way to lodgement, where the money moves,
        // rather than being left on the verification step the practice clicked past several minutes ago.
        //
        // Precomputed for the same reason the difference tables are: an ng-repeat over a function that builds a
        // new array every digest never settles.
        function buildPartialMatches(medicare, policy) {
            const matches = [];
            if (isPartialMatch($ctrl.verification.medicare_status)) {
                matches.push({
                    account: 'Medicare',
                    status_code: $ctrl.verification.medicare_status.status_code,
                    details: returnedDetails($ctrl.verification.medicare_status,
                        'Medicare card number', 'Patient reference number (IRN)',
                        medicare.card_number, medicare.irn)
                });
            }
            if (isPartialMatch($ctrl.verification.phi_status)) {
                matches.push({
                    account: 'The health fund',
                    status_code: $ctrl.verification.phi_status.status_code,
                    details: returnedDetails($ctrl.verification.phi_status,
                        'Membership number', 'Member reference number',
                        policy.membership_number, policy.member_reference_number)
                });
            }
            return matches;
        }

        $ctrl.hasPartialMatch = function () {
            return $ctrl.partialMatches.length > 0;
        };

        // Whether there is a Medicare card on the record to have been verified. OPVW checks the accounts the
        // patient actually holds, so a patient with a fund policy and no Medicare card comes back with a health
        // fund answer and nothing else - which is the complete answer for them, not a missing half of one.
        $ctrl.hasMedicareOnRecord = function () {
            const medicare = $ctrl.patient.medicare_details || {};
            return !!medicare.card_number;
        };

        // Which funder the patient's own record points to, offered on the funder buttons as a suggestion
        // rather than made for the practice: the record can say what cover the patient holds, but not whether
        // this visit was a hospital admission or a consultation, and that is what decides the channel.
        //
        // DVA first: a veteran's card pays for their treatment in and out of hospital, so any private cover on
        // the same record is rarely what is being claimed. A health fund policy next: private cover exists for
        // hospital treatment, which is what ECLIPSE lodges - though a consultation for the same patient still
        // goes to Medicare. Medicare last, as the default a card alone implies. Nothing at all when the record
        // carries none of the three, since a guess with nothing behind it would only be confidently wrong.
        $ctrl.hasDvaOnRecord = function () {
            const dva = ($ctrl.patient && $ctrl.patient.dva_details) || {};
            return !!dva.file_number;
        };

        $ctrl.hasHealthFundOnRecord = function () {
            const policies = ($ctrl.patient && $ctrl.patient.au_private_health_insurance) || [];
            return policies.some(policy => policy && (policy.health_fund_name || policy.membership_number));
        };

        // Whether any policy on the record is overseas visitor (OVHC) or overseas student (OSHC) cover rather
        // than domestic. Such a member holds no Medicare entitlement at all - that is what the overseas (OVSW)
        // channel exists for - so for them a missing Medicare card is the reason the claim goes down that
        // channel, not something stopping it being made.
        $ctrl.hasOverseasCoverOnRecord = function () {
            const policies = ($ctrl.patient && $ctrl.patient.au_private_health_insurance) || [];
            return policies.some(isOverseasPolicy);
        };

        // Domestic cover specifically, not "any policy that is not overseas": a policy whose cover type was
        // never recorded is refused on selection rather than claimed in-patient, so it is not one either.
        $ctrl.hasDomesticCoverOnRecord = function () {
            const policies = ($ctrl.patient && $ctrl.patient.au_private_health_insurance) || [];
            return policies.some(policy => policy && policy.overseas_cover_type === 'DOMESTIC');
        };

        // What the patient's record makes claimable at all, which is what the three buttons are gated on. Each
        // channel is refused up front rather than on the click, because what it needs is a detail on the
        // patient record and not something that can be fixed from here.
        //
        // ECLIPSE asks only for Medicare, not for a fund policy: a Medicare only claim is a legitimate ECLIPSE
        // claim, so a patient with a card and no private cover can still be claimed for down this channel.
        //
        // And the other way round for overseas cover. The in-patient (IMCW) channel splits its settlement with
        // Medicare, so it does need a card; the overseas (OVSW) channel does not, and an OVHC or OSHC member
        // holds no Medicare entitlement to record. Gating the button on a card they cannot have would leave the
        // overseas channel unreachable by the very patients it exists for. Which of the two a claim goes down is
        // still decided from the policy it is made against - see applySelectedPhiPolicy().
        $ctrl.canClaimMedicare = function () {
            return $ctrl.hasMedicareOnRecord();
        };

        $ctrl.canClaimDva = function () {
            return $ctrl.hasDvaOnRecord();
        };

        $ctrl.canClaimEclipse = function () {
            return $ctrl.hasMedicareOnRecord() || $ctrl.hasOverseasCoverOnRecord();
        };

        // Only ever names a channel that can actually be used. Marking a card the practice is not allowed to
        // press would be worse than marking none: it reads as the way forward and then refuses.
        $ctrl.suggestedFunder = function () {
            if ($ctrl.canClaimDva()) {
                return 'dva';
            }
            if ($ctrl.hasHealthFundOnRecord() && $ctrl.canClaimEclipse()) {
                return 'eclipse';
            }
            if ($ctrl.canClaimMedicare()) {
                return 'medicare';
            }
            return null;
        };

        // The line above the cards. Held here rather than as a stack of ng-ifs because the case that matters
        // most is the one with no suggestion to give: a patient holding domestic cover but no Medicare card has
        // details on record and still cannot be claimed for, and saying "nothing is recorded" there would send
        // the practice looking for the wrong thing. It also names the way out, since an overseas policy on the
        // same record would be claimable - a cover type left unrecorded is the commonest reason it is not.
        $ctrl.funderLede = function () {
            switch ($ctrl.suggestedFunder()) {
                case 'dva':
                    return 'This patient has DVA details on record, so they are likely a DVA patient. ' +
                        'Choose the one that matches the treatment.';
                case 'eclipse':
                    if (!$ctrl.hasMedicareOnRecord() && $ctrl.hasOverseasCoverOnRecord()) {
                        return 'This patient holds overseas visitor or student cover, so an ECLIPSE overseas ' +
                            'claim is likely. Choose the one that matches the treatment.';
                    }
                    return 'This patient holds private health cover, so an in-patient claim is likely. ' +
                        'Choose the one that matches the treatment.';
                case 'medicare':
                    return 'This patient has a Medicare card and no DVA or health fund details, so a Medicare ' +
                        'claim is likely. Choose the one that matches the treatment.';
            }
            if ($ctrl.hasHealthFundOnRecord()) {
                return 'This patient holds private health cover but has no Medicare card on record. Record ' +
                    'their Medicare details, or set the cover on that policy to overseas visitor (OVHC) or ' +
                    'student (OSHC) if that is what they hold.';
            }
            return 'No Medicare, DVA or health fund details are recorded for this patient, so there is ' +
                'nothing to claim against yet.';
        };

        // What each funder's card says the record holds for it, read alongside the tick or dash beside it: the
        // mark carries the yes or no, so the words only have to name the thing. Kept to one line - the note
        // sits on a rule across the foot of the card, and a second line lifts that rule out of step with the
        // cards either side of it.
        //
        // Named rather than ticked where there is a name to give: the fund a policy is with is what the
        // practice recognises, and it is also what tells them the record is the one they think it is.
        $ctrl.medicareRecordNote = function () {
            return $ctrl.hasMedicareOnRecord() ? 'Medicare card' : 'No Medicare card';
        };

        // Whether the ECLIPSE card says anything about Medicare at all. It needs a Medicare entitlement for an
        // in-patient claim and none whatever for an overseas one, so for a patient whose cover is OVHC or OSHC
        // and who holds no card there is nothing to report: the line is dropped rather than carrying a note
        // explaining its own irrelevance. The records block is sized for two lines on every card, so the ECLIPSE
        // card losing one leaves the rules and buttons level across the row.
        //
        // A card on the record is still reported, overseas cover or not - it is a fact about the patient, and
        // the same record can carry a domestic policy claimed in-patient alongside the overseas one.
        $ctrl.showsMedicareOnEclipse = function () {
            return $ctrl.hasMedicareOnRecord() || !$ctrl.hasOverseasCoverOnRecord();
        };

        $ctrl.dvaRecordNote = function () {
            return $ctrl.hasDvaOnRecord() ? 'DVA file number' : 'No DVA file number';
        };

        // What the ECLIPSE card claims, read off the cover this patient actually holds rather than stated
        // once for everybody. Domestic cover is claimed in-patient only; the overseas channel is the one that
        // takes out-of-hospital services as well, so for an overseas member the card that says "in-patient"
        // is turning them away from the channel that exists for them.
        //
        // A record carrying both cannot be narrowed here - which channel a claim goes down is decided from the
        // policy chosen inside the flow, see applySelectedPhiPolicy() - so it says what each one is good for.
        $ctrl.eclipseScopeNote = function () {
            return $ctrl.hasOverseasCoverOnRecord() ? 'In-patient or out-patient items' : 'In-patient items';
        };

        $ctrl.eclipseBlurb = function () {
            if (!$ctrl.hasOverseasCoverOnRecord()) {
                return 'Hospital admissions, claimed against Medicare and/or the health fund.';
            }
            if ($ctrl.hasDomesticCoverOnRecord()) {
                return 'Admissions on domestic cover, either setting on overseas cover.';
            }
            return 'Admissions or out-patient care, claimed against overseas cover.';
        };

        $ctrl.healthFundRecordNote = function () {
            const policies = (($ctrl.patient && $ctrl.patient.au_private_health_insurance) || [])
                .filter(policy => policy && (policy.health_fund_name || policy.membership_number));
            if (policies.length === 0) {
                return 'No health fund policy';
            }
            if (policies.length > 1) {
                return policies.length + ' health fund policies';
            }
            const fund = fundOf(policies[0]);
            return (fund ? fund.name : policies[0].health_fund_name) || 'Health fund policy';
        };

        // The ECLIPSE card carries a Medicare line as well as a fund one, because a domestic policy on its own
        // is not the whole story: an in-patient claim splits its settlement with Medicare, so the fund line can
        // read as a tick while the claim still cannot be made. Overseas cover is the exception, and there the
        // line is dropped instead - see showsMedicareOnEclipse().

        // The health fund leg is only reached when the Medicare details check out, so a missing phi_status is
        // itself a signal that the Medicare details need attention first.
        //
        // An overseas patient has no Medicare entitlement and so no Medicare account to verify - requiring one
        // would leave every overseas claim reading as "did not verify cleanly" no matter how good the health
        // fund's answer was, which is a warning that trains people to ignore warnings.
        $ctrl.isVerificationClean = function () {
            if (!$ctrl.verification) {
                return false;
            }
            if ($ctrl.isOverseas()) {
                return $ctrl.isAccountVerified($ctrl.verification.phi_status, $ctrl.phiChanges);
            }
            // A Medicare only patient has no fund account to verify, so requiring one would leave every such claim
            // reading as "did not verify cleanly" however good Medicare's answer was - the same reason the overseas
            // leg above does not ask for a Medicare account.
            if ($ctrl.medicareOnlyPatient) {
                return $ctrl.isAccountVerified($ctrl.verification.medicare_status, $ctrl.medicareChanges);
            }
            // And the mirror of it: a domestic patient can hold a fund policy without holding a Medicare card,
            // and there is then no Medicare leg for the check to have run. Requiring one would report every such
            // claim as unverified over an account the patient does not have.
            if (!$ctrl.hasMedicareOnRecord()) {
                return $ctrl.isAccountVerified($ctrl.verification.phi_status, $ctrl.phiChanges);
            }
            return $ctrl.isAccountVerified($ctrl.verification.medicare_status, $ctrl.medicareChanges) &&
                $ctrl.isAccountVerified($ctrl.verification.phi_status, $ctrl.phiChanges);
        };

        $ctrl.goToEclipseClaimDetails = function () {
            $ctrl.eclipseStep = 'CLAIM';
        };

        // The way back to the verification step, which is where a rejection on the patient's details is fixed:
        // it is the one place that says what the funder actually holds for them. The claim details are left
        // exactly as they are - nothing about them is wrong - and re-running the check is still the only route
        // on to lodgement, so nothing reaches Medicare without being checked again over the corrected details.
        $ctrl.backToVerification = function () {
            $ctrl.eclipseStep = 'VERIFY';
        };

        // --- Step 2: claim items, transaction, and the eligibility check on it ---

        // Fetched once and kept for the life of the widget. Held as the widget's own array rather than the
        // shared one, so seeding an item the list no longer holds does not leak into other screens' drop-downs.
        function loadClaimableItems() {
            if ($ctrl.claimableItems.length > 0) {
                return $q.resolve($ctrl.claimableItems);
            }

            $ctrl.claimableItemsLoading = true;
            $ctrl.claimableItemsError = null;

            return TyroClaimableItems.load().then(function (items) {
                $ctrl.claimableItems = items.slice();
                return $ctrl.claimableItems;
            }).catch(function (error) {
                $ctrl.claimableItemsError = errorMessage(error, "Unable to load the claimable item list. Enter the MBS item codes manually.");
            }).finally(function () {
                $ctrl.claimableItemsLoading = false;
            });
        }

        // Replaces the blank first row with the items already recorded against the patient. Their snapshotted
        // code, name and fee fill the row without the reference list, which is only needed to show the item in
        // the drop-down. Seeded once per widget, not once per open, so leaving the view and coming back does
        // not throw away rows the practice has since edited.
        function seedItemsFromPending() {
            const pending = $ctrl.pendingItems || [];
            if (pending.length === 0 || pendingItemsSeeded) {
                return;
            }
            pendingItemsSeeded = true;

            $ctrl.eclipseItems = pending.map(function (pendingItem) {
                const item = newEclipseItem();
                item.item_code = pendingItem.item_code;
                item.name = pendingItem.name;
                item.charge_amount = feeOrNull(pendingItem);
                item.selected = claimableItemFor(pendingItem);
                return item;
            });

            syncPresentingIllnessItemNumber();
        }

        // The presenting illness is all but always the principal procedure being claimed, so the first claim
        // item's code is offered as the MBS item number rather than re-keyed a few fields up. Only ever fills a
        // field that is blank or still holds what we last put there: once the practice types their own number,
        // editing the items below must not silently rewrite it. Left alone when no item carries a code yet,
        // rather than clearing a number that is still the best answer on the form.
        function syncPresentingIllnessItemNumber() {
            const current = $ctrl.eclipseConfig.presentingIllnessItemNumber;
            if (current && current !== autoFilledPresentingIllnessItemNumber) {
                return;
            }

            const withCode = $ctrl.eclipseItems.find(item => item.item_code);
            if (!withCode) {
                return;
            }

            $ctrl.eclipseConfig.presentingIllnessItemNumber = withCode.item_code;
            autoFilledPresentingIllnessItemNumber = withCode.item_code;
        }

        // Bound to the free-text item code field, which is what the form falls back to when the claimable item
        // list is unreadable - the drop-down goes through onClaimableItemSelected() instead.
        $ctrl.onItemCodeChanged = function () {
            syncPresentingIllnessItemNumber();
        };

        // Matched on id first then on code, so an item recorded before the practice added its own priced copy
        // still resolves. A since-deleted item resolves to nothing, so its snapshot is offered as the option -
        // otherwise the row renders as an empty drop-down over an item code the claim still carries.
        function claimableItemFor(pendingItem) {
            const match = $ctrl.claimableItems.find(option => option.tyro_claimable_item_id === pendingItem.tyro_claimable_item_id) ||
                $ctrl.claimableItems.find(option => option.item_code === pendingItem.item_code);
            if (match || $ctrl.claimableItems.length === 0) {
                return match || null;
            }

            const option = TyroClaimableItems.tokenise(angular.copy(pendingItem));
            $ctrl.claimableItems.push(option);
            return option;
        }

        function feeOrNull(item) {
            return (item && item.fee !== undefined && item.fee !== null) ? item.fee : null;
        }

        $ctrl.searchClaimableItems = function (search) {
            return TyroClaimableItems.search($ctrl.claimableItems, search);
        };

        $ctrl.claimableItemLabel = function (item) {
            return TyroClaimableItems.label(item);
        };

        // Copies the selection onto the fields the request is built from. Clearing the drop-down clears them
        // too, so a half-filled row cannot carry a stale item code into the claim.
        $ctrl.onClaimableItemSelected = function (item) {
            item.item_code = item.selected ? item.selected.item_code : '';
            item.name = item.selected ? item.selected.name : '';

            // Only a practice item carries a fee; a SYSTEM item leaves the charge to be keyed in.
            const fee = feeOrNull(item.selected);
            if (fee !== null) {
                item.charge_amount = fee;
            }

            syncPresentingIllnessItemNumber();
        };

        $ctrl.addEclipseItem = function () {
            $ctrl.eclipseItems.push(newEclipseItem());
        };

        $ctrl.removeEclipseItem = function (index) {
            $ctrl.eclipseItems.splice(index, 1);
            if ($ctrl.eclipseItems.length === 0) {
                $ctrl.addEclipseItem();
            }
            // Dropping the item the presenting illness was taken from moves it on to whatever now leads the
            // claim, so the number does not outlive the item it came from.
            syncPresentingIllnessItemNumber();
        };

        // Per-row disclosure. Kept on the row rather than in one shared flag so opening the options on one
        // item does not unfold every other line as well.
        $ctrl.toggleItemAdvanced = function (item) {
            item.advancedOpen = !item.advancedOpen;
        };

        // --- Claimant (patient claims only) ---
        //
        // Who settled the account and is owed the Medicare benefit back. Medicare will not take a patient claim
        // without one, and normally it is the patient themselves - which the server sends when nothing here is
        // filled in, so the ordinary claim is unaffected by any of this.
        //
        // It is filled in when the patient cannot be their own claimant. Most often that is a patient under 15,
        // who needs an adult named instead; occasionally it is a spouse or carer who paid the account. Folded
        // away behind the same toggle the service rows use for their ECLIPSE options, because most claims set
        // none of it, and the count on the button is what says a collapsed panel is asserting something.
        $ctrl.claimantOpen = false;

        // The claimant's date of birth, entered as three boxes rather than through a date picker.
        //
        // A birth date is the one date on this form that is decades old, and a calendar - native or Material -
        // is the worst possible way to reach one: it opens on this month and the practice pages back through
        // hundreds of them. Typing 02 03 1984 is three keystrokes and no navigation. It is also how CatTrax has
        // always taken a date of birth (see the DOB boxes on the patient registration form), and it reads as
        // dd/mm/yyyy left to right, which a native date input does not - that renders in the browser's locale,
        // and there is no way to make it show day-first.
        //
        // eclipseConfig.claimantDateOfBirth stays the single source of truth as an ISO yyyy-MM-dd string, which
        // is what the API takes; these boxes compose it - see onClaimantDobChanged().
        $ctrl.claimantDob = { DD: null, MM: null, YYYY: null };

        // The years the boxes accept. The same floor the registration form uses for a patient, and no later than
        // this year - a claimant born in the future is a typo, not a claimant.
        $ctrl.DOB_MIN_YEAR = 1900;
        $ctrl.currentYear = new Date().getFullYear();

        /**
         * Whether the claimant is identified by a Medicare card.
         *
         * In-patient only. An overseas claim is settled by the health fund alone and no part of it is processed
         * by Medicare, so a claimant on one holds no Medicare entitlement to be paid against - the same reason
         * the patient block on that channel carries no Medicare account either.
         *
         * Three things follow, and all three read this rather than testing the channel themselves: the card
         * number and IRN are not asked for, the claimant is identified by name and date of birth instead - which
         * is the whole of what the fund has to match them on, so the date of birth stops being optional - and
         * there is no verification to offer, OPVW being Medicare's own check of a Medicare card.
         */
        $ctrl.claimantTakesMedicareCard = function () {
            return !$ctrl.isOverseas();
        };

        // --- Verifying the claimant with Medicare (OPVW) ---
        //
        // The same check the patient gets on step 1, run against the claimant instead. Worth having for the same
        // reason and rather more: the claimant is typed off a card at the desk, there is no patient record to
        // cross-check it against, and a mismatch means Medicare declines to pay the benefit - discovered days
        // after the patient has settled the account in full, because a patient claim cannot be lodged until they
        // have.
        //
        // Advisory only, deliberately. It never blocks a lodgement: OPVW is Online *Patient* Verification, and
        // asking it about a claimant is an off-label use of the field that has not been confirmed against
        // Services Australia. If it turns out ECLIPSE refuses the request, this reports that it could not verify
        // and the claim goes exactly as it did before.
        $ctrl.claimantVerification = null;
        $ctrl.claimantVerifyLoading = false;
        $ctrl.claimantVerifyError = null;
        // Set when the practice has been shown that Medicare would not confirm the claimant and has chosen to
        // run the check regardless. Their call: the details may be right and Medicare's records stale, or the
        // claim may be worth checking anyway to see what the fund says.
        $ctrl.claimantVerifyOverridden = false;

        /**
         * Whether a Medicare card number is one Medicare could issue.
         *
         * Ten digits, the first of which is 2 to 6, and the ninth a check digit calculated from the eight before
         * it - weights 1,3,7,9 repeated, summed, modulo ten. The tenth digit is the card issue number and is not
         * part of the sum.
         *
         * Worth checking rather than merely counting digits: the check digit is what catches a transposition,
         * which is the mistake actually made when a number is read off a card and typed. Medicare would catch it
         * too, but only at lodgement, days later, on a patient claim whose account has already been settled in
         * full - and it answers a card it cannot match as one that was never supplied.
         */
        $ctrl.isMedicareNumberValid = function (cardNumber) {
            const digits = (cardNumber || '').replace(/\s/g, '');
            if (!/^\d{10}$/.test(digits) || !/^[2-6]/.test(digits)) {
                return false;
            }
            const weights = [1, 3, 7, 9, 1, 3, 7, 9];
            let sum = 0;
            for (let i = 0; i < 8; i++) {
                sum += Number(digits.charAt(i)) * weights[i];
            }
            return (sum % 10) === Number(digits.charAt(8));
        };

        // Shown against the field once enough has been typed to judge it, the way the provider number is.
        $ctrl.showClaimantMedicareError = function () {
            const value = ($ctrl.eclipseConfig.claimantMedicareNumber || '').trim();
            return value.length >= 10 && !$ctrl.isMedicareNumberValid(value);
        };

        $ctrl.canVerifyClaimant = function () {
            return $ctrl.claimantTakesMedicareCard()
                && !$ctrl.claimantVerifyLoading
                && !!asText($ctrl.eclipseConfig.claimantFirstName)
                && !!asText($ctrl.eclipseConfig.claimantLastName)
                && $ctrl.isMedicareNumberValid($ctrl.eclipseConfig.claimantMedicareNumber);
        };

        /**
         * Drops a verification the claimant's details no longer match.
         *
         * Verifying "Joanne Guardian, 2951234571" and then editing the card number leaves a green tick over a
         * claimant Medicare has never seen - which is worse than no tick at all, because the gate below reads it.
         * So any edit to the claimant clears it and the check has to be run again.
         */
        /**
         * Whether the claimant may be corrected in place on the quote this step is lodging.
         *
         * The recovery path for the case that stranded a practice: ECLIPSE refuses the claim over its claimant -
         * too young, or details Medicare will not match - after the patient has already settled the account.
         *
         * Re-running the eligibility check cannot be the fix. A check records a new quote, the new quote carries
         * no invoice, and the account would be raised and charged a second time while the paid one sat on a
         * superseded row. So the claimant is edited on the quote the money is already attached to.
         *
         * Offered while the claim has not gone. Once it is with the funder there is nothing here to rewrite.
         */
        $ctrl.canCorrectClaimant = function () {
            return $ctrl.requiresClaimant() && !!$ctrl.quotedTransactionId() && !$ctrl.lodgedTransaction;
        };

        $ctrl.claimantSaving = false;
        $ctrl.claimantSaveError = null;
        $ctrl.claimantSaved = false;

        /**
         * Saves the corrected claimant onto the quote, then re-reads what would stop the claim lodging.
         *
         * The server validates it exactly as the eligibility check would, so a correction cannot store something
         * the check itself would have refused - the age rule included, which is most likely what brought the
         * practice here.
         */
        $ctrl.saveCorrectedClaimant = function () {
            if (!$ctrl.canCorrectClaimant() || $ctrl.claimantSaving || $ctrl.claimantIssue()) {
                return;
            }
            const cfg = $ctrl.eclipseConfig;
            $ctrl.claimantSaving = true;
            $ctrl.claimantSaveError = null;
            $ctrl.claimantSaved = false;

            TyroAPI.updateQuoteClaimant($ctrl.quotedTransactionId(), $ctrl.patient.id, {
                patient_id: $ctrl.patient.id,
                claim_type_code: cfg.claimTypeCode,
                admission_date: toDateParam(cfg.admissionDate),
                claimant_first_name: cfg.claimantFirstName,
                claimant_last_name: cfg.claimantLastName,
                claimant_date_of_birth: toDateParam(cfg.claimantDateOfBirth),
                claimant_medicare_number: cfg.claimantMedicareNumber,
                claimant_medicare_irn: cfg.claimantMedicareIrn,
            }).then(function () {
                $ctrl.claimantSaved = true;
                $ctrl.lodgeError = null;
                // Back to lodgement when this was reached from it over a paid account: a new check is refused
                // there (see checkEclipseEligibility), so the claim details step would otherwise have no way on.
                if ($ctrl.eclipseStep === 'CLAIM' && $ctrl.accountHasBeenDealtWith() && $ctrl.isQuoteLodgeable()) {
                    $ctrl.eclipseStep = 'LODGE';
                }
                // The stored row has changed, so what would stop it lodging has to be asked again.
                $ctrl.refreshLodgementIssues();
            }).catch(function (error) {
                $ctrl.claimantSaveError = errorMessage(error, "The claimant could not be saved. Please try again.");
            }).finally(function () {
                $ctrl.claimantSaving = false;
            });
        };

        $ctrl.onClaimantChanged = function () {
            $ctrl.claimantVerification = null;
            $ctrl.claimantVerifyError = null;
            // An override was granted for the claimant as they stood. Edit them and it is a different claimant,
            // so it is asked again rather than carried over.
            $ctrl.claimantVerifyOverridden = false;
            $ctrl.claimantSaved = false;
            $ctrl.claimantSaveError = null;
        };

        $ctrl.verifyClaimant = function () {
            if (!$ctrl.canVerifyClaimant()) {
                return $q.resolve();
            }
            $ctrl.claimantVerifyLoading = true;
            $ctrl.claimantVerifyError = null;
            $ctrl.claimantVerification = null;

            const cfg = $ctrl.eclipseConfig;
            return TyroAPI.verifyEclipsePatient({
                patient_id: $ctrl.patient.id,
                provider_number: providerNumberParam(cfg.providerNumber),
                service_date: toDateParam(cfg.admissionDate) || toDateParam(todayDate()),
                claimant_first_name: cfg.claimantFirstName,
                claimant_last_name: cfg.claimantLastName,
                claimant_date_of_birth: toDateParam(cfg.claimantDateOfBirth),
                claimant_medicare_number: cfg.claimantMedicareNumber,
                claimant_medicare_irn: cfg.claimantMedicareIrn,
            }).then(function (response) {
                $ctrl.claimantVerification = response.data;
            }).catch(function (error) {
                $ctrl.claimantVerifyError = errorMessage(error,
                    "The claimant could not be verified with Medicare. The claim can still be lodged.");
            }).finally(function () {
                $ctrl.claimantVerifyLoading = false;
            });
        };

        // Only the Medicare leg is read: a claimant is who Medicare pays, not who a fund covers, so the request
        // carries no health fund account and phi_status has nothing in it.
        function claimantMedicareStatus() {
            return ($ctrl.claimantVerification && $ctrl.claimantVerification.medicare_status) || null;
        }

        /**
         * Whether Medicare accepted the claimant - which is not the same as matching them exactly.
         *
         * Services Australia answers a near miss with return code 8005: "Verification has been accepted however
         * details were not an exact match." That is an acceptance. The account is real and the benefit is
         * payable; what differs is a spelling it would like corrected, and it returns what it holds so the
         * practice can. Treating it as a failure would stop a claim Medicare has just said it will honour.
         *
         * So this is what the flow gates on, and claimantMatchedExactly() below is what decides whether there is
         * anything to tell the practice about.
         */
        $ctrl.claimantAccepted = function () {
            const medicare = claimantMedicareStatus();
            return !!medicare && (medicare.patient_verified === true || isPartialMatch(medicare));
        };

        /** Accepted with nothing to correct - no partial match, no update asked for. */
        $ctrl.claimantMatchedExactly = function () {
            const medicare = claimantMedicareStatus();
            return $ctrl.claimantAccepted() && !isPartialMatch(medicare) && medicare.update_required !== true;
        };

        /** Accepted, but Medicare holds something different from what was typed. */
        $ctrl.claimantMatchedWithDifferences = function () {
            return $ctrl.claimantAccepted() && !$ctrl.claimantMatchedExactly();
        };

        /**
         * Medicare's own sentence about the card, with its subject corrected.
         *
         * The claimant is sent in OPVW's patient slot - it is the only slot it has - so everything Medicare says
         * back is worded about "the patient". Read against a claimant panel that is exactly the wrong noun: it
         * sends the practice off to check the patient's card when it is the claimant's that differs.
         */
        $ctrl.claimantVerificationMessage = function () {
            const medicare = claimantMedicareStatus();
            const text = medicare ? (medicare.status_text || medicare.status_code || null) : null;
            return text ? String(text).replace(/Patient/g, 'Claimant').replace(/patient/g, 'claimant') : null;
        };

        // What Medicare holds where it disagrees with what was typed - the same "on record / returned by the
        // funder" comparison the patient check offers, which is the part that actually fixes the typo.
        //
        // Bound to an ng-repeat, so the same array is handed back while its contents are unchanged: fresh row
        // objects on every digest look like a change to ng-repeat each time, and the digest never settles.
        let claimantSuggestionsKey = null;
        let claimantSuggestionsCache = [];
        $ctrl.claimantSuggestions = function () {
            const rows = buildClaimantSuggestions();
            const key = angular.toJson(rows);
            if (key !== claimantSuggestionsKey) {
                claimantSuggestionsKey = key;
                claimantSuggestionsCache = rows;
            }
            return claimantSuggestionsCache;
        };

        function buildClaimantSuggestions() {
            const medicare = $ctrl.claimantVerification && $ctrl.claimantVerification.medicare_status;
            if (!medicare) {
                return [];
            }
            const cfg = $ctrl.eclipseConfig;
            return [
                { label: 'First name', typed: cfg.claimantFirstName, suggested: medicare.suggested_first_name },
                { label: 'Last name', typed: cfg.claimantLastName, suggested: medicare.suggested_last_name },
                { label: 'Medicare number', typed: cfg.claimantMedicareNumber, suggested: medicare.suggested_membership_number },
                { label: 'IRN', typed: cfg.claimantMedicareIrn, suggested: medicare.suggested_card_rank },
            ].filter(row => !!row.suggested && String(row.suggested).trim() !== String(row.typed || '').trim());
        }

        $ctrl.toggleClaimant = function () {
            // Nothing to fold away on a claim that has to carry a claimant - see claimantPanelOpen().
            if ($ctrl.claimantIsMandatory()) {
                return;
            }
            $ctrl.claimantOpen = !$ctrl.claimantOpen;
        };

        /**
         * Composes the three boxes into the ISO date the request carries, or clears it while the date is
         * incomplete or impossible.
         *
         * Round-tripped through a Date rather than trusted: 31/02/1984 passes every per-box range check there
         * is, and JavaScript rolls it forward to 2 March rather than rejecting it. Comparing the parts back
         * against what the Date actually holds is what catches that.
         */
        $ctrl.onClaimantDobChanged = function () {
            $ctrl.eclipseConfig.claimantDateOfBirth = composedClaimantDob() || '';
        };

        function composedClaimantDob() {
            const day = Number($ctrl.claimantDob.DD);
            const month = Number($ctrl.claimantDob.MM);
            const year = Number($ctrl.claimantDob.YYYY);

            if (!day || !month || !year) {
                return null;
            }
            if (year < $ctrl.DOB_MIN_YEAR || year > new Date().getFullYear()) {
                return null;
            }

            const composed = new Date(year, month - 1, day);
            if (composed.getFullYear() !== year || composed.getMonth() !== month - 1 || composed.getDate() !== day) {
                return null;
            }
            // Nobody claiming today was born tomorrow.
            if (composed > new Date()) {
                return null;
            }
            return $filter('date')(composed, 'yyyy-MM-dd');
        }

        // Whether anything has been typed into the three boxes, so a part-entered date is told apart from an
        // empty one. An empty date of birth is fine - it is optional on the claimant.
        function claimantDobStarted() {
            return $ctrl.claimantDob.DD !== null && $ctrl.claimantDob.DD !== undefined && $ctrl.claimantDob.DD !== ''
                || $ctrl.claimantDob.MM !== null && $ctrl.claimantDob.MM !== undefined && $ctrl.claimantDob.MM !== ''
                || $ctrl.claimantDob.YYYY !== null && $ctrl.claimantDob.YYYY !== undefined && $ctrl.claimantDob.YYYY !== '';
        }

        // What is wrong with the date of birth as it stands, or null. Started-but-not-composable comes first:
        // the boxes cannot say which part is at fault without guessing at what was meant.
        $ctrl.claimantDobIssue = function () {
            if (claimantDobStarted() && !composedClaimantDob()) {
                return "Enter the claimant's date of birth as day, month and year - for example 02 03 1984.";
            }
            return $ctrl.claimantAgeIssue();
        };

        /**
         * The youngest ECLIPSE will accept as a claimant: "The Claimant age must not be less than 14 years old."
         *
         * A child cannot claim their own Medicare benefit, and the commonest way this goes wrong is the patient's
         * own date of birth being entered as the claimant's - which is exactly the case a claimant exists to
         * solve, a patient too young to claim for themselves.
         */
        $ctrl.CLAIMANT_MIN_AGE = 14;

        /**
         * Whether the claimant is old enough, measured at the date of service rather than today: it is the age at
         * the time of the episode that ECLIPSE judges, and a claim can be lodged weeks after the surgery.
         *
         * A hard rule, unlike the verification below it. This is arithmetic ECLIPSE will do the same way, so a
         * claim that fails it is certain to be refused - there is nothing to override and nothing to be gained by
         * sending it.
         */
        $ctrl.claimantAgeIssue = function () {
            const dob = composedClaimantDob();
            if (!dob) {
                return null;
            }
            const age = ageAtDateOfService(dob);
            if (age === null || age >= $ctrl.CLAIMANT_MIN_AGE) {
                return null;
            }
            return "The claimant must be at least " + $ctrl.CLAIMANT_MIN_AGE + " years old - ECLIPSE refuses a "
                + "claim whose claimant is younger. This claimant would be " + age + " at the date of service. "
                + "A patient too young to claim for themselves needs an adult named here, not their own details.";
        };

        // Somebody's age on the day of the episode, or null when neither date can be read. Asked of the claimant
        // and of the patient, and both are judged at the date of service rather than today.
        function ageAtDateOfService(dobIso) {
            const serviceIso = toDateParam($ctrl.eclipseConfig.admissionDate) || toDateParam(todayDate());
            if (!serviceIso) {
                return null;
            }
            const dob = new Date(dobIso);
            const at = new Date(serviceIso);
            if (isNaN(dob.getTime()) || isNaN(at.getTime())) {
                return null;
            }
            let age = at.getFullYear() - dob.getFullYear();
            const months = at.getMonth() - dob.getMonth();
            if (months < 0 || (months === 0 && at.getDate() < dob.getDate())) {
                age = age - 1;
            }
            return age;
        }

        /**
         * The patient's own age at the date of service, or null where their date of birth cannot be read.
         *
         * Put through toDateParam() first because the patient record does not agree with the form about what a
         * date is: it holds a yyyy/MM/dd string, while every date on this form is a Date. Both come out of it in
         * a shape ageAtDateOfService() can read.
         */
        $ctrl.patientAgeAtService = function () {
            const patient = $ctrl.patient;
            if (!patient || !patient.dateOfBirth) {
                return null;
            }
            return ageAtDateOfService(toDateParam(patient.dateOfBirth));
        };

        /**
         * Whether the patient is too young to be their own claimant.
         *
         * The same floor the claimant is held to - ECLIPSE judges the claimant's age and does not care who it
         * belongs to, so a patient under it cannot be sent as their own claimant either. Leaving the panel empty
         * on such a claim sends the patient, and ECLIPSE refuses it.
         *
         * Null where the date of birth cannot be read, and that fails open: an unreadable date is not evidence
         * the patient is a child, and a claim must not be held closed over one. The patient verification on step
         * one is what deals with a patient record that is wrong.
         */
        $ctrl.patientTooYoungToClaim = function () {
            const age = $ctrl.patientAgeAtService();
            return age !== null && age < $ctrl.CLAIMANT_MIN_AGE;
        };

        /**
         * Whether a claimant has to be named rather than merely offered.
         *
         * The claimant is optional on almost every patient claim - empty means the patient claims for themselves,
         * which is the ordinary case. It stops being optional exactly when the patient cannot be their own
         * claimant, and the panel says so rather than letting the claim go and come back refused.
         */
        $ctrl.claimantIsMandatory = function () {
            return $ctrl.requiresClaimant() && $ctrl.patientTooYoungToClaim();
        };

        // A mandatory claimant with nothing entered against it yet. The panel explains this case in full at the
        // top, so the error line under the fields stays quiet for it and speaks only about what was typed.
        $ctrl.claimantMissingButRequired = function () {
            return $ctrl.claimantIsMandatory() && !anyClaimantDetail();
        };

        /**
         * Whether the claimant panel is showing its fields.
         *
         * Forced open where a claimant is mandatory: a field that must be filled in must not be behind a toggle,
         * and the collapsed summary reads "The patient is claiming for themselves" - which on this claim is the
         * one thing that cannot happen.
         */
        $ctrl.claimantPanelOpen = function () {
            return $ctrl.claimantOpen || $ctrl.claimantIsMandatory();
        };

        // How many claimant details have been entered, for the collapsed panel to report. Read on every digest,
        // so it stays a plain scan of the config.
        $ctrl.claimantFieldCount = function () {
            return claimantValues().filter(value => value !== '' && value !== null && value !== undefined).length;
        };

        // The claimant fields this claim actually has, which is not always all of them: an overseas claimant
        // holds no Medicare card, so the two card fields are neither shown nor sent there. Read by the count and
        // by anyClaimantDetail(), so a card number left on the config by a change of policy cannot make an empty
        // overseas panel read as half entered.
        function claimantValues() {
            const cfg = $ctrl.eclipseConfig;
            const values = [cfg.claimantFirstName, cfg.claimantLastName, cfg.claimantDateOfBirth];
            if ($ctrl.claimantTakesMedicareCard()) {
                values.push(cfg.claimantMedicareNumber, cfg.claimantMedicareIrn);
            }
            return values;
        }

        // Whether a claimant other than the patient has been named. A name is what decides it, because that is
        // what the server decides on too - and Medicare matches a claimant on name as well as card.
        $ctrl.hasNamedClaimant = function () {
            return !!(asText($ctrl.eclipseConfig.claimantFirstName) || asText($ctrl.eclipseConfig.claimantLastName));
        };

        // What a half-entered claimant is missing, or null when there is nothing wrong with it. Said on the
        // panel as the practice types rather than left to come back as a rejection from the check.
        // Anything at all typed into the claimant panel. Mirrors the server's own test - a card number or a date
        // on its own is as much a half-entered claimant as a lone name is, and the server refuses all of them, so
        // the panel has to ask about all of them rather than only noticing when a name appears.
        function anyClaimantDetail() {
            return claimantValues().some(value => !!asText(value)) || claimantDobStarted();
        }

        $ctrl.claimantIssue = function () {
            if (!anyClaimantDetail()) {
                // An empty panel sends the patient as their own claimant, which is fine until the patient is too
                // young to be one. Said here rather than left to the eligibility check, because this is what
                // eclipseClaimIssue() reads and it is what holds the check closed.
                if ($ctrl.claimantIsMandatory()) {
                    return "This patient is " + $ctrl.patientAgeAtService() + " at the date of service, and "
                        + "ECLIPSE refuses a claimant under " + $ctrl.CLAIMANT_MIN_AGE + ". Name the adult who "
                        + "settled the account and is owed the benefit - their details, not the patient's.";
                }
                return null;
            }
            if (!asText($ctrl.eclipseConfig.claimantFirstName) || !asText($ctrl.eclipseConfig.claimantLastName)) {
                return $ctrl.claimantTakesMedicareCard()
                    ? "A claimant needs both names, as they appear on their Medicare card."
                    : "A claimant needs both names, as the health fund holds them.";
            }
            if ($ctrl.claimantTakesMedicareCard()) {
                if (!asText($ctrl.eclipseConfig.claimantMedicareNumber)) {
                    return "A claimant needs their Medicare card number - it is the card the benefit is paid against.";
                }
                // Blocks the eligibility check, not just the lodgement. The check is what creates the quote the
                // claim is later promoted from, so a claimant whose card cannot be right is worth stopping before
                // a quote is built on them - and long before the patient has paid.
                if (!$ctrl.isMedicareNumberValid($ctrl.eclipseConfig.claimantMedicareNumber)) {
                    return "Medicare does not recognise " + asText($ctrl.eclipseConfig.claimantMedicareNumber)
                        + " as a card number. It is ten digits, and the ninth is a check digit calculated from the "
                        + "eight before it, so one mistyped or transposed digit invalidates the whole number.";
                }
            } else if (!$ctrl.eclipseConfig.claimantDateOfBirth && !claimantDobStarted()) {
                // Required here where it is optional on the in-patient claim, and for the reason it is optional
                // there: a Medicare claimant is matched on their card, and this one has none. Name and date of
                // birth are the whole of what the fund has to tell them apart by, so a claimant named without one
                // is a claimant the fund cannot pay. Asked only once something has been typed - an empty panel
                // still means the patient is claiming for themselves.
                return "A claimant on an overseas claim needs their date of birth. There is no Medicare card on "
                    + "this channel, so their name and date of birth are all the fund has to identify them by.";
            }
            // Verification is deliberately not one of these. It is asked for at the eligibility check, run there
            // if nobody has run it, and can be overridden - Medicare failing to match a claimant is a warning
            // worth stopping on, not a fact that makes the claim impossible. See checkEclipseEligibility().
            return $ctrl.claimantDobIssue();
        };

        // Emptied when the claim moves to a class that cannot carry a claimant. Cleared rather than just hidden,
        // for the reason the other class-specific fields are: sent on an AG, SC, MB or MO claim, Medicare refuses
        // it for a block the practice can no longer see, let alone clear.
        function clearClaimant() {
            const cfg = $ctrl.eclipseConfig;
            cfg.claimantFirstName = '';
            cfg.claimantLastName = '';
            cfg.claimantDateOfBirth = '';
            cfg.claimantMedicareNumber = '';
            cfg.claimantMedicareIrn = '';
            $ctrl.claimantDob = { DD: null, MM: null, YYYY: null };
            $ctrl.claimantVerification = null;
            $ctrl.claimantVerifyError = null;
            $ctrl.claimantOpen = false;
        }

        // Which attributes ECLIPSE accepts depends on the service type: the overrides and the attendance
        // counts are for general and specialist services, the specimen and exemption fields for pathology.
        // Shown the same way, so the form offers only what the claim can actually carry.
        $ctrl.isPathologyClaim = function () {
            return $ctrl.eclipseConfig.serviceTypeCode === 'P';
        };

        // Whether a row is asserting anything beyond the basics, so a collapsed row can say so rather than
        // hiding it. Read on every digest, so it stays a plain scan of the row.
        $ctrl.itemAdvancedCount = function (item) {
            if (!item) {
                return 0;
            }
            const set = [item.reference, item.service_time, item.time_duration, item.number_of_patients_seen,
                item.service_quantity, item.self_deemed_code, item.lsp_number, item.scp_id,
                item.accession_date, item.accession_time, item.collection_date, item.collection_time];
            let count = set.filter(value => value !== '' && value !== null && value !== undefined).length;
            [item.duplicate_service_override, item.multiple_procedure_override, item.aftercare_override,
                item.rule3_exempt, item.s4b3_exempt].forEach(function (flag) {
                if (flag) {
                    count = count + 1;
                }
            });
            return count;
        };

        function claimItems() {
            return $ctrl.eclipseItems.filter(item => item.item_code && isChargeable(item));
        }

        // What claimItems() takes a row for, named so the filter that drops a row and the check that reports
        // it cannot drift apart. Zero is a real amount rather than a missing one, but ECLIPSE has nothing to
        // assess against a nil charge, so it is not claimable either.
        function isChargeable(item) {
            return item.charge_amount !== null && item.charge_amount !== undefined && item.charge_amount > 0;
        }

        // A row the practice can see on the form but the claim would leave behind.
        $ctrl.isItemMissingCharge = function (item) {
            return !!item.item_code && !isChargeable(item);
        };

        // The earliest service on the claim, which is the date a referral has to have been valid on. Read off
        // every coded row rather than claimItems(), so a row still missing its charge is not silently excused
        // from the referral it will be claimed under.
        function earliestServiceDate() {
            return $ctrl.eclipseItems
                .filter(item => item.item_code)
                .map(item => toDateParam(item.service_date))
                .filter(Boolean)
                .sort()
                .shift() || null;
        }

        function formatDay(dateParam) {
            return $filter('date')(moment(dateParam, 'YYYY-MM-DD').toDate(), 'dd MMM yyyy');
        }

        // Both return primitives, so they are safe to bind directly.
        $ctrl.claimItemCount = function () {
            return claimItems().length;
        };

        $ctrl.claimItemsTotal = function () {
            return claimItems().reduce((total, item) => total + (item.charge_amount || 0), 0);
        };

        // Medipass rejects an episode whose admission date falls after any service date ("The Admission date
        // must not be after Date of Service"), costing a round trip and surfacing as a raw API error. Compared
        // on the YYYY-MM-DD the request carries, so it matches what Medipass sees.
        $ctrl.admissionAfterServiceDate = function () {
            const admission = toDateParam($ctrl.eclipseConfig.admissionDate);
            if (!admission) {
                return false;
            }
            return claimItems().some(function (item) {
                const serviceDate = toDateParam(item.service_date);
                return serviceDate && serviceDate < admission;
            });
        };

        // An in-patient claim is by definition an admission. An overseas claim is only one when the practice
        // says so, since it is the channel that also covers out-of-hospital services.
        $ctrl.requiresAdmissionDate = function () {
            return !$ctrl.isOverseas() || $ctrl.eclipseConfig.hospital;
        };

        // An agreement or scheme claim can carry a known gap, and the patient has to have been told what it would
        // be, so ECLIPSE mandates an IFC code on both; a scheme claim additionally will not accept verbal consent.
        // A no-gap claim still carries one - X, not obtained - since the code records how consent was handled
        // rather than whether there was a gap.
        $ctrl.requiresIfcIssueCode = function () {
            return $ctrl.eclipseConfig.claimTypeCode === 'AG' || $ctrl.eclipseConfig.claimTypeCode === 'SC';
        };

        $ctrl.availableIfcIssueCodes = function () {
            if ($ctrl.eclipseConfig.claimTypeCode === 'SC') {
                return $ctrl.IFC_ISSUE_CODES.filter(code => code.value !== 'V');
            }
            return $ctrl.IFC_ISSUE_CODES;
        };

        // A scheme claim is made under an approved gap cover scheme, which requires the provider to disclose
        // any financial interest in what the patient was given. No other claim type is asked for it.
        $ctrl.requiresFinancialInterestDisclosure = function () {
            return $ctrl.eclipseConfig.claimTypeCode === 'SC';
        };

        // Every ECLIPSE claim is invoiced at what the practice charged, whichever class it is lodged under: all four
        // permit a charge above what the funder pays, and the difference is the patient's gap. Charging exactly the
        // benefit is a price rather than a class, and needs nothing from the form - the gap simply comes out at nil.
        // So no predicate here decides what the invoice carries; what the class still decides is below.
        //
        // Whether the fund caps what may be charged above its benefit, which an agreement and a scheme claim are
        // both billed under. Over the cap - commonly $400-$500 an admission, and the fund's number rather than
        // Medicare's - the fund refuses the claim outright rather than trimming it, and it has to be relodged as MB
        // with the fund's top-up lost. Worth saying beside a gap, since nothing here knows the cap.
        $ctrl.gapIsCappedByTheFund = function () {
            const code = $ctrl.eclipseConfig.claimTypeCode;
            return code === 'AG' || code === 'SC';
        };

        // A Medicare only claim, which carries no fund membership, no fund payee id, and no informed financial
        // consent - there is no fund for any of them to belong to.
        $ctrl.isMedicareOnlyClaim = function () {
            return $ctrl.eclipseConfig.claimTypeCode === 'MO';
        };

        // A patient claim: the patient settles the account with the practice and claims the benefit back from the
        // funder themselves. It is the only class the funder does not pay the practice on, which is what the three
        // rules below it are about and why the invoice reads differently - see quotedPatientGap(), where the amount
        // owing is the whole charge rather than a gap.
        $ctrl.isPatientClaim = function () {
            return $ctrl.eclipseConfig.claimTypeCode === 'PC';
        };

        // Whether the account may be lodged as already settled: "Account Paid Indicator must not be set to Y when
        // Claim Type Code is set to AG, SC, MB or MO" - the patient claim is the only class it was ever accepted
        // on, because it is the only one the patient pays. Medicare rejects a Y on any other.
        //
        // No longer a declaration the practice makes. It now marks the classes whose accountPaidInd the server
        // derives from what was actually collected, which is the patient claim and only it - see
        // requiresAccountSettlement() for the step that collects it, and lodgeEclipseClaim() for what is sent.
        $ctrl.acceptsAccountPaid = function () {
            return $ctrl.isPatientClaim();
        };

        // Whether the claim must name a claimant - the person who settled the account and is owed the benefit
        // back: "claim[0].eclipse.inPatient.claimant - Must be set when Claim Type Code is set to PC."
        // Mirrors TyroProcessingClassType.requiresClaimant() server side. Every other class pays its benefit to
        // the provider or their agent, so there is nobody for a claimant to be and Medicare rejects the block.
        //
        // Both channels, because the class decides it rather than the channel: a patient claim pays whoever
        // settled the account rather than the practice, and an overseas claim is a patient claim as often as an
        // in-patient one is. What differs is how the claimant is identified - see claimantTakesMedicareCard().
        $ctrl.requiresClaimant = function () {
            return $ctrl.isPatientClaim();
        };

        // Whether the claim itself carries the patient's authority to submit it: "Submission Authority Indicator
        // must only be set when Claim Type Code is set to PC". On every other class the consent that travels is the
        // IFC code on the medical event, and the tick beside the items authorises the eligibility enquiry instead -
        // which every class's check carries, patient claim included.
        $ctrl.requiresSubmissionAuthority = function () {
            return $ctrl.isPatientClaim();
        };

        // Whether the claim may carry a fund payee id - the fund's own reference for the surgeon's agreement with
        // it: "Fund Payee Id must only be set when Claim Type Code is set to AG, SC or MB". A Medicare only claim
        // has no fund for one to belong to, and a patient claim is not paid to the surgeon at all, so neither takes
        // one and Medicare rejects one sent on either.
        $ctrl.acceptsFundPayeeId = function () {
            return !$ctrl.isMedicareOnlyClaim() && !$ctrl.isPatientClaim();
        };

        // Whether Medicare pays this claim's benefit to a billing agent rather than to the provider, which it
        // needs the agent's provider number and the patient's assignment of their benefit for. MB and MO both do:
        // "Billing Agent Provider Number must be supplied if Claim Type Code is MB or MO", and the assignment
        // "must be set to Y" on either. Mirrors TyroProcessingClassType.requiresBillingAgent() server side.
        $ctrl.requiresBillingAgent = function () {
            return $ctrl.eclipseConfig.claimTypeCode === 'MB' || $ctrl.isMedicareOnlyClaim();
        };

        // Whether the claim may carry an accident indicator at all: "Accident Indicator must only be supplied if
        // Claim Type Code is set to AG, SC, MB or PC" - so a Medicare only claim must not, and Medicare rejects one
        // that does rather than ignoring it.
        $ctrl.acceptsAccidentIndicator = function () {
            return !$ctrl.isMedicareOnlyClaim();
        };

        // Whether the claim being built runs an eligibility check at all. An ECLIPSE check asks one health fund
        // what it will pay towards an admission, so a Medicare only claim has nobody to ask - the server records the
        // episode of care instead and the claim lodges from that, which is the same flow with the question left out.
        $ctrl.runsEligibilityCheck = function () {
            return !$ctrl.isOverseas() && !$ctrl.isMedicareOnlyClaim();
        };

        // Lines charged under their MBS schedule fee, which is the one pricing mistake a quote will not correct.
        //
        // OEC works the benefits out from the lower of the charge and the schedule fee: charge at or above the fee and
        // the line is paid 75% of the fee by Medicare and 25% by the fund - more under an agreement or scheme - with
        // anything above that the patient's gap. Charge under the fee and the benefits are capped at the charge
        // instead, 75/25 of it, and the gap comes out nil. Nothing is refused and nothing reads as wrong; the claim
        // simply pays less than the same episode would have at the schedule fee, and the difference cannot be
        // recovered once it is lodged.
        //
        // So the two figures are put side by side rather than the charge being quietly raised. What a practice charges
        // is its own decision - and at the schedule fee the patient still owes nothing, which is what makes it worth
        // pointing out.
        $ctrl.itemsUnderScheduleFee = function () {
            const quote = $ctrl.eclipseQuote;
            if (!quote || !quote.services) {
                return [];
            }
            return quote.services.filter(function (service) {
                return service.schedule_fee > 0 && service.charge_amount > 0
                    && service.charge_amount < service.schedule_fee;
            });
        };

        // Only worth saying once a processing class has been picked, and only when there is a gap to talk about.
        // A patient claim always has one, and it is the whole charge: the fund rebates the patient, so nothing it
        // assesses reaches the practice's invoice. Whether it has been collected yet is the settlement step's
        // business, not this one's - see patientClaimAccount().
        $ctrl.hasQuotedPatientGap = function () {
            if ($ctrl.isPatientClaim()) {
                return $ctrl.claimItemsTotal() > 0;
            }
            return !!$ctrl.eclipseTotals && $ctrl.eclipseTotals.gap > 0;
        };

        // Drops a consent code the new claim type cannot carry, rather than sending Medicare a value it will
        // reject - verbal consent on a scheme claim, or any code on a claim type that takes none.
        $ctrl.onClaimTypeChanged = function () {
            const stillValid = $ctrl.availableIfcIssueCodes().some(code => code.value === $ctrl.eclipseConfig.ifcIssueCode);
            if (!$ctrl.requiresIfcIssueCode() || !stillValid) {
                $ctrl.eclipseConfig.ifcIssueCode = '';
            }
            // Cleared with the claim type, so the declaration is always one made about the claim in front of
            // them rather than a tick left over from a scheme claim they moved away from.
            if (!$ctrl.requiresFinancialInterestDisclosure()) {
                $ctrl.eclipseConfig.financialInterestDisclosure = false;
            }
            // Likewise the billing agent details, which only an MB or MO claim carries and which Medicare rejects
            // on any other class.
            if (!$ctrl.requiresBillingAgent()) {
                $ctrl.eclipseConfig.billingAgentProviderNumber = '';
                $ctrl.eclipseConfig.benefitAssignmentAuthorised = false;
            } else if (!$ctrl.eclipseConfig.billingAgentProviderNumber) {
                // Back onto a class that needs one: defaulted again rather than left empty by the clearing above.
                $ctrl.eclipseConfig.billingAgentProviderNumber = practiceBillingAgentId();
            }
            // And the accident indicator, which a Medicare only claim may not carry at all. Cleared rather than
            // just hidden: sent on one, Medicare rejects the claim for supplying a field that class has no room
            // for - and the practice would have no visible box to clear.
            if (!$ctrl.acceptsAccidentIndicator()) {
                $ctrl.eclipseConfig.accident = false;
                $ctrl.eclipseConfig.accidentDate = '';
            }
            // Moving off the patient claim leaves any account raised for it stranded: no other class is settled by
            // the patient, and lodging one would raise a second invoice. Discarded rather than left behind - and
            // only ever the unpaid ones, which is what the server enforces.
            if (!$ctrl.acceptsAccountPaid()) {
                $ctrl.discardPatientClaimAccount();
                // And the overseas declaration, which is only ever true of a patient claim.
                $ctrl.eclipseConfig.accountPaid = false;
            }
            // The claimant belongs to the patient claim alone - see clearClaimant().
            if (!$ctrl.requiresClaimant()) {
                clearClaimant();
            }
            // The fund payee id likewise: a class that cannot carry one is refused for sending it, and the field
            // is hidden by then.
            if (!$ctrl.acceptsFundPayeeId()) {
                $ctrl.eclipseConfig.fundPayeeId = '';
            }
        };

        // --- The patient's referrals, and prefilling the claim from one ---

        // Fetched once and kept for the life of the widget. The list spans both referral stores -
        // cataract referrals, every other type, and referrals uploaded through the documents module
        // - so it is the whole of what the patient has been referred under.
        //
        // "Once" with one exception: a referral created while the widget is open - most often by the
        // analyser recognising a file that was just uploaded - arrives as a push event, and the list
        // is re-read in the background so the picker offers it. No loading state is shown for that; the
        // picker simply has one more option the next time it is looked at.
        PatientRecordLiveUpdateService.subscribeReferrals($scope, {
            patientId: function () {
                return $ctrl.patient && $ctrl.patient.id;
            },
            reload: function () {
                refreshPatientReferralsQuietly();
            },
        });

        function refreshPatientReferralsQuietly() {
            if (!$ctrl.patient || !$ctrl.patient.id || !Session.hasPermission("CLINICAL_REFERRAL_VIEW")) {
                return;
            }
            ReferralsAPI.getPatientReferrals($ctrl.patient.id).then(function (response) {
                $ctrl.patientReferrals = sortedNewestFirst(response.data || []);
                referralsLoaded = true;
                refreshReferralOptions();
            }, function () {
                // Deliberately silent: the list the user already has stays as it is.
            });
        }

        function loadPatientReferrals() {
            if (referralsLoaded) {
                return $q.resolve($ctrl.patientReferrals);
            }
            // Front desk staff can't read referrals, so they claim without picking one.
            if (!Session.hasPermission("CLINICAL_REFERRAL_VIEW")) {
                return $q.resolve($ctrl.patientReferrals || []);
            }

            $ctrl.referralsLoading = true;
            $ctrl.referralsError = null;

            return ReferralsAPI.getPatientReferrals($ctrl.patient.id).then(function (response) {
                $ctrl.patientReferrals = sortedNewestFirst(response.data || []);
                referralsLoaded = true;
                refreshReferralOptions();
                return $ctrl.patientReferrals;
            }).catch(function (error) {
                $ctrl.referralsError = errorMessage(error, "Unable to load the patient's referrals. Enter the referral details manually.");
            }).finally(function () {
                $ctrl.referralsLoading = false;
            });
        }

        // A referral only prefills the claim if it carries the referring provider the claim has to
        // be made against - one recorded before those were captured has nothing to offer.
        $ctrl.referralHasProviderDetails = function (referral) {
            return !!providerIdOf(referral);
        };

        // Newest first, by the date the referral was issued. Ordered here rather than trusting what came back:
        // the list is stitched together from two referral stores, so its order is whatever they happened to be
        // read in - and "the latest referral", which is what the form prefills itself from, is only the latest
        // if something has actually sorted them. A referral with no date sorts last; it cannot be the latest.
        //
        // Ordering is presentation, so it is not allowed to fail the load: the referrals were fetched
        // successfully either way, and reporting "unable to load the patient's referrals" over a bad date would
        // hide every good referral behind one unreadable one and send the practice off to hand-key a provider
        // number they already hold.
        function sortedNewestFirst(referrals) {
            try {
                return referrals.slice().sort(function (a, b) {
                    const issuedA = referralIssueDate(a);
                    const issuedB = referralIssueDate(b);
                    if (!issuedA && !issuedB) return 0;
                    if (!issuedA) return 1;
                    if (!issuedB) return -1;
                    return issuedB - issuedA;
                });
            } catch (error) {
                console.error("Could not order the patient's referrals; leaving them as they arrived:", error);
                return referrals;
            }
        }

        // The drop-down's options: the referrals that can actually fill the form in, newest first, each with
        // its label and a key of its own.
        //
        // The key is the option's position, deliberately not anything read off the referral. A patient's list
        // can hold two referrals carrying the same referralSource AND referralId - live data does - and a
        // <select> whose options share a value cannot tell them apart: picking either selects the first, so
        // every other one silently does nothing. Position is unique whatever the data says.
        //
        // Rebuilt in place and holding precomputed labels, as ng-options over a function returning a new list
        // every digest would never settle.
        $ctrl.referralOptions = [];

        function refreshReferralOptions() {
            // The referral the drop-down was showing, carried across the rebuild. Positions move when a referral
            // arrives by push - a new one sorts first - so the old key would point at a different referral than
            // the one the form was filled from. Matched by content, as the re-read returns fresh objects.
            const shown = $ctrl.referralOptions.find(option => option.key === $ctrl.selectedReferralKey);
            const shownReferral = shown ? shown.referral : null;

            $ctrl.referralOptions.length = 0;
            $ctrl.patientReferrals
                .filter($ctrl.referralHasProviderDetails)
                .forEach(function (referral, index) {
                    $ctrl.referralOptions.push({
                        key: String(index),
                        label: $ctrl.referralLabel(referral),
                        referral: referral
                    });
                });

            const stillShown = shownReferral &&
                $ctrl.referralOptions.find(option => angular.equals(option.referral, shownReferral));
            $ctrl.selectedReferralKey = stillShown ? stillShown.key : null;
        }

        function providerIdOf(referral) {
            return (referral && referral.providerDetails && referral.providerDetails.providerId) || '';
        }

        // The referral period as this form's drop-down holds it. A referral carrying a period the
        // form does not offer is treated as carrying none, rather than selecting nothing silently.
        function referralPeriodOf(referral) {
            const stored = referral && referral.providerDetails && referral.providerDetails.referralPeriod;
            const period = $ctrl.REFERRAL_PERIODS.find(option => option.apiValue === stored);
            return period ? period.value : '';
        }

        // Medicare wants the reason for any referral period other than the standard one in the supporting text
        // on a service line, and holds the claim for manual assessment without it. Only a referral the practice
        // actually supplied is asked for it - an override or a self-deemed code describes no period at all.
        // Only a non-standard period carries a length - it is what "non-standard" has to quantify. Standard and
        // indefinite both describe themselves.
        $ctrl.requiresReferralPeriodMonths = function () {
            const cfg = $ctrl.eclipseConfig;
            return $ctrl.requiresReferral() && cfg.referralType === 'REFERRAL' && cfg.referralPeriod === 'N';
        };

        // A length ECLIPSE will take: whole months, 1 to 97. Refused here rather than sent, because a code with
        // no usable length behind it is one ECLIPSE cannot act on.
        $ctrl.referralPeriodMonthsValid = function () {
            const months = Number($ctrl.eclipseConfig.referralPeriodMonths);
            return $ctrl.eclipseConfig.referralPeriodMonths !== '' && Number.isInteger(months)
                    && months >= 1 && months <= 97;
        };

        $ctrl.requiresReferralSupportingText = function () {
            const cfg = $ctrl.eclipseConfig;
            return $ctrl.requiresReferral() && cfg.referralType === 'REFERRAL'
                    && !!cfg.referralPeriod && cfg.referralPeriod !== 'S';
        };

        // Whether that text has been given. It is entered on a claim item, because that is where ECLIPSE reads
        // it from - services[].serviceText - so any one item carrying it satisfies the referral.
        $ctrl.hasReferralSupportingText = function () {
            return claimItems().some(item => serviceTextOf(item));
        };

        // The period the form is holding, by name, for the messages that have to say which one it is. Distinct
        // from referralPeriodName(), which names the period on a stored referral rather than on the form.
        $ctrl.selectedReferralPeriodName = function () {
            const period = $ctrl.REFERRAL_PERIODS.find(option => option.value === $ctrl.eclipseConfig.referralPeriod);
            return period ? period.name.toLowerCase() : 'non-standard';
        };

        // The length recorded against a non-standard referral. Read off the referral rather than left to the
        // practice, because the referral is the document the figure is written on.
        function referralPeriodMonthsOf(referral) {
            const stored = referral && referral.providerDetails && referral.providerDetails.referralPeriodMonths;
            return (stored === null || stored === undefined || stored === '') ? '' : stored;
        }

        $ctrl.referralPeriodName = function (referral) {
            const period = $ctrl.REFERRAL_PERIODS.find(option => option.value === referralPeriodOf(referral));
            return period ? period.name : '-';
        };

        // The referral's issue date, as a Date at browser midnight - the same handling as
        // todayDate(), so the date input renders the day the referral was issued rather than
        // shifting it. A referral written by CatTrax itself carries a local date-time with no zone
        // on it and is already the practice's calendar date; one keyed in against an uploaded
        // referral carries a UTC instant, which is read in the practice's timezone.
        function referralIssueDate(referral) {
            // Coerced to a string before anything reads it as one. This runs over every referral the patient
            // has, not just the one being loaded, so a single row carrying its date in another shape - a number,
            // a Date - must come back as "no date" rather than throw and take the whole list down with it.
            // Anything unparseable lands on null below, which sorts last and prefills nothing.
            const raw = referral && (referral.dateOfReferralUTC || referral.dateCreated);
            const recorded = (raw === null || raw === undefined) ? '' : String(raw);
            if (!recorded) {
                return null;
            }

            const carriesZone = /(Z|[+-]\d{2}:?\d{2})$/.test(recorded);
            const day = carriesZone
                ? moment(recorded).tz(practiceTimezoneName()).format('YYYY-MM-DD')
                : recorded.slice(0, 10);

            // Parsed strictly. Loosely, moment reads the leftovers of an unexpected format as a real date -
            // a Date object stringifies to "Mon May 04 2026 ...", whose first ten characters it happily turns
            // into 2004-12-31 - and a silently wrong issue date is worse than none: it would pick the wrong
            // referral as the latest and put that date on the claim. Every legitimate input here is already
            // exactly YYYY-MM-DD by this point.
            const issued = moment(day, 'YYYY-MM-DD', true);
            return issued.isValid() ? issued.toDate() : null;
        }

        $ctrl.referralIssueDate = referralIssueDate;

        // Copies the referral onto the claim's referral fields, and records what it put there so a
        // later automatic prefill cannot overwrite an edit made on top of it. Always switches the
        // route to "referral held": a referral has been produced, so neither an override nor a
        // self-deemed code applies any more.
        function applyReferral(referral) {
            const cfg = $ctrl.eclipseConfig;

            cfg.referralType = 'REFERRAL';
            cfg.referringProviderNumber = providerIdOf(referral);
            cfg.referralDate = referralIssueDate(referral);

            // Left as it is when the referral carries no period, rather than clearing a value that
            // is still the best answer on the form.
            const period = referralPeriodOf(referral);
            if (period) {
                cfg.referralPeriod = period;
                // The length belongs to the period, so it travels with it: a non-standard referral carries the
                // months it was recorded with, and every other period carries none. Set together, or a referral
                // switched from non-standard to standard would keep a length that is no longer part of it.
                cfg.referralPeriodMonths = period === 'N' ? referralPeriodMonthsOf(referral) : '';
            }

            // Keeps the drop-down showing the referral the form actually holds, including the one seeded
            // automatically on open.
            const showing = $ctrl.referralOptions.find(option => option.referral === referral);
            $ctrl.selectedReferralKey = showing ? showing.key : null;
            autoFilledReferralFields = snapshotReferralFields();
        }

        function snapshotReferralFields() {
            const cfg = $ctrl.eclipseConfig;
            return {
                referringProviderNumber: cfg.referringProviderNumber,
                referralDate: toDateParam(cfg.referralDate),
                referralPeriod: cfg.referralPeriod,
                referralPeriodMonths: cfg.referralPeriodMonths
            };
        }

        // Whether the referral fields are still ours to fill: either untouched, or holding exactly
        // what we last put there. Once the practice types over any of them the referral on the form
        // is theirs, and only an explicit preload replaces it.
        function referralFieldsUntouched() {
            const cfg = $ctrl.eclipseConfig;

            if (!autoFilledReferralFields) {
                return !cfg.referringProviderNumber && !cfg.referralDate;
            }

            const current = snapshotReferralFields();
            return current.referringProviderNumber === autoFilledReferralFields.referringProviderNumber &&
                current.referralDate === autoFilledReferralFields.referralDate &&
                current.referralPeriod === autoFilledReferralFields.referralPeriod &&
                current.referralPeriodMonths === autoFilledReferralFields.referralPeriodMonths;
        }

        // Prefills from the most recent referral that carries the details a claim needs. The list
        // arrives newest first, so that is the first one holding a provider.
        function seedReferralFromLatest() {
            const latest = $ctrl.patientReferrals.find($ctrl.referralHasProviderDetails);
            if (!latest || !referralFieldsUntouched()) {
                return;
            }
            applyReferral(latest);
        }

        // Picking a referral is an explicit instruction to load it, so it replaces whatever is on the form,
        // edits included - it is the one action that says the practice wants this referral's details rather
        // than their own. Clearing the selection deliberately leaves the fields alone: they may have been
        // typed over on purpose, and blanking them would throw away work rather than undo a choice.
        $ctrl.onReferralSelected = function () {
            const option = $ctrl.referralOptions.find(candidate => candidate.key === $ctrl.selectedReferralKey);
            if (option) {
                applyReferral(option.referral);
            }
        };

        // Why the drop-down has nothing to offer, which is worth telling apart: no referrals at all is a
        // different problem from referrals that were recorded without the provider a claim needs.
        $ctrl.noLoadableReferralsReason = function () {
            if ($ctrl.referralsLoading || $ctrl.referralsError || $ctrl.referralOptions.length > 0) {
                return null;
            }
            return $ctrl.patientReferrals.length > 0
                ? "The referrals on file have no referring provider recorded, so enter the details below."
                : "No referrals on file for this patient, so enter the details below.";
        };

        // One line per referral for the drop-down that loads it: who wrote it and when, then the provider number
        // and validity the claim is actually made against. The label is all there is to tell two referrals
        // apart, so it carries what the old picker's table columns did, minus what a claim does not care about.
        $ctrl.referralLabel = function (referral) {
            if (!referral) {
                return '';
            }
            const issued = referralIssueDate(referral);
            const on = issued ? $filter('date')(issued, 'dd MMM yyyy') : 'an unrecorded date';
            const who = referral.referredByUserFullName ? referral.referredByUserFullName + ', ' : '';

            let label = who + on + ' \u2014 ' + providerIdOf(referral);
            const period = $ctrl.referralPeriodName(referral);
            if (period && period !== '-') {
                label += ' (' + period + ')';
            }
            return label;
        };

        // ECLIPSE declines a specialist claim (code 2030) unless every medical event carries a referral, a
        // referral override code or a self-deemed code on at least one of its services.
        $ctrl.requiresReferral = function () {
            return $ctrl.eclipseConfig.serviceTypeCode === 'S';
        };

        $ctrl.hasReferralDetails = function () {
            const cfg = $ctrl.eclipseConfig;
            if (cfg.referralType === 'OVERRIDE') {
                return !!cfg.referralOverrideCode;
            }
            if (cfg.referralType === 'SELF_DEEMED') {
                return !!cfg.selfDeemedCode;
            }
            return $ctrl.isProviderNumberValid(cfg.referringProviderNumber) && !!cfg.referralDate && !!cfg.referralPeriod;
        };

        // Whether the claim will actually carry a referral object, which is narrower than the practice
        // having picked the referral route. Only a specialist claim sends one, and only with a referring
        // provider on it - so this matches referralField(), because what the request carries is what
        // Medicare's rule is read against.
        $ctrl.claimCarriesReferralDetails = function () {
            const cfg = $ctrl.eclipseConfig;
            return $ctrl.requiresReferral() && cfg.referralType === 'REFERRAL' && !!cfg.referringProviderNumber;
        };

        // "Number of Patients Seen must not be set when Referral Details are set." A patient count describes a
        // group attendance; a referral names the one patient who was referred, and Medicare will not have a
        // service be both. It refuses the whole claim over it and locates the fault in its own terms -
        // "Error located in Medical Event 01, Service 0101" - which names nothing the practice can find on this
        // form, so the two are kept apart while the claim is still being built.
        //
        // Only a referral collides with it. An override or a self-deemed code sends no referral object at all
        // (the server sends a referralOverrideCode instead), so a claim taking either of those routes may
        // still count patients.
        $ctrl.itemPatientsSeenConflictsWithReferral = function (item) {
            return $ctrl.claimCarriesReferralDetails() && hasPatientsSeen(item);
        };

        // Set to a count Medicare would act on. The request drops a blank or a zero, so neither is a conflict.
        function hasPatientsSeen(item) {
            const seen = item ? item.number_of_patients_seen : null;
            return seen !== '' && seen !== null && seen !== undefined && Number(seen) > 0;
        }

        // A Medicare provider number is a 6 digit stem, a practice location character and a check digit derived
        // from the other seven. Medicare recomputes it and rejects the claim on a mismatch, so a mistyped digit
        // is worth catching here rather than a round trip later.
        const PROVIDER_LOCATION_CHARS = '0123456789ABCDEFGHJKLMNPQRTUVWXY';
        const PROVIDER_CHECK_CHARS = 'YXWTLKJHFBA';
        const PROVIDER_STEM_WEIGHTS = [3, 5, 8, 4, 2, 1];

        function providerCheckCharacter(value) {
            const stem = value.slice(0, 6);
            const locationValue = PROVIDER_LOCATION_CHARS.indexOf(value.charAt(6));
            if (!/^\d{6}$/.test(stem) || locationValue === -1) {
                return null;
            }
            const total = PROVIDER_STEM_WEIGHTS.reduce(function (sum, weight, index) {
                return sum + (parseInt(stem.charAt(index), 10) * weight);
            }, locationValue * 6);
            return PROVIDER_CHECK_CHARS.charAt(total % 11);
        }

        $ctrl.isProviderNumberValid = function (providerNumber) {
            const value = (providerNumber || '').trim().toUpperCase();
            if (value.length !== 8) {
                return false;
            }
            return providerCheckCharacter(value) === value.charAt(7);
        };

        // Only complain once a full-length number has been typed - flagging one still being entered is noise.
        $ctrl.showProviderNumberError = function (providerNumber) {
            const value = (providerNumber || '').trim();
            return value.length >= 8 && !$ctrl.isProviderNumberValid(value);
        };

        // The two ways a provider number fails, told apart. The wrong length is a slip the practice can see for
        // themselves; a full-length number that fails its check digit looks perfectly reasonable on the form,
        // so that one has to be explained rather than just flagged.
        function providerNumberMessage(providerNumber) {
            const value = (providerNumber || '').trim();
            return value.length !== 8
                ? "A provider number is eight characters: a 6 digit stem, a practice location character and a check digit. " + value + " is not."
                : "Medicare does not recognise " + value + " as a provider number. Its last character is a check digit calculated from the other seven, so one mistyped character invalidates the whole number.";
        }

        // When a referral stops covering services, as the YYYY-MM-DD the claim is compared on. A standard or
        // indefinite referral has no month count to work from - "standard" already means 12 months from a GP
        // but 3 from a specialist, and which one wrote it is not on this form - so neither is given an expiry
        // rather than guessing one and refusing a claim Medicare would have taken.
        function referralExpiry(issued, period) {
            const months = parseInt(period, 10);
            if (!months) {
                return null;
            }
            return moment(issued, 'YYYY-MM-DD').add(months, 'months').format('YYYY-MM-DD');
        }

        // Whether the referral on the form actually covers what is being claimed. The referral fields are
        // prefilled from the patient's most recent referral, which for a list booked months out is easily one
        // that has since run out - so the dates are read here rather than left to come back as a rejection
        // days later, by which time the referral is nobody's live concern.
        function referralValidityIssue() {
            const cfg = $ctrl.eclipseConfig;
            if (cfg.referralType !== 'REFERRAL') {
                return null;
            }

            const issued = toDateParam(cfg.referralDate);
            const claimed = earliestServiceDate() || toDateParam(cfg.admissionDate);
            if (!issued || !claimed) {
                return null;
            }

            if (issued > claimed) {
                return {
                    title: "Check Referral Date",
                    message: "The referral was issued on " + formatDay(issued) + ", after the service on " +
                        formatDay(claimed) + ". A service cannot be claimed against a referral written after it."
                };
            }

            const expiry = referralExpiry(issued, cfg.referralPeriod);
            if (expiry && claimed > expiry) {
                return {
                    title: "Referral Has Expired",
                    message: "The referral issued on " + formatDay(issued) + " ran out on " + formatDay(expiry) +
                        ", before the service on " + formatDay(claimed) + ". Load a referral that covers the " +
                        "service, or record an override or self-deemed code instead."
                };
            }
            return null;
        }

        // Whether the dates and the same-day indicator tell ECLIPSE one consistent story about the stay.
        // Compared on the YYYY-MM-DD the request carries, so this matches what Medicare sees.
        function episodeDatesIssue() {
            const cfg = $ctrl.eclipseConfig;
            const admission = toDateParam(cfg.admissionDate);
            const discharge = toDateParam(cfg.dischargeDate);

            if (discharge && admission && discharge < admission) {
                return {
                    title: "Check Dates",
                    message: "The discharge date is before the admission date. Correct whichever of the two is wrong."
                };
            }
            if (cfg.sameDay && discharge && admission && discharge !== admission) {
                return {
                    title: "Check Dates",
                    message: "This is marked as a same-day admission, but the discharge date is not the " +
                        "admission date. Untick same-day for an overnight stay, or correct the discharge date."
                };
            }
            // Medicare measures this against the day it receives the request, not the day the form was filled
            // in, so it is checked here on every attempt rather than once when the date was entered - the same
            // claim can be inside the window in the morning and outside it in the afternoon.
            if ($ctrl.admissionDateTooEarly()) {
                return {
                    title: "Admission Date Too Old",
                    message: "ECLIPSE will not accept an admission dated more than " +
                        $ctrl.CLAIM_BACKDATE_LIMIT_DAYS + " days before the day it receives the claim. This " +
                        "admission is older than that, so it cannot be claimed through ECLIPSE."
                };
            }
            // ECLIPSE refuses the check outright without this on an overnight stay: "Expected Length of Stay
            // must be supplied when Same Day Procedure Indicator is not set." The discharge date is no
            // substitute and is not accepted as one - a pre-op quote rarely has a discharge date, which is
            // exactly the case where the fund still has to be told how long the patient is expected to be in.
            if (!$ctrl.isOverseas() && !cfg.sameDay && !isWholeNumberOfNights(cfg.lengthOfStay)) {
                return {
                    title: "Length of Stay Required",
                    message: "This is not a same-day admission, so ECLIPSE requires the expected length of stay " +
                        "as a whole number of nights. Enter it, or tick same-day if the patient goes home the " +
                        "same day."
                };
            }
            // An overnight stay with no discharge date is deliberately not blocked. ECLIPSE takes the discharge
            // date as optional, and this form quotes surgery before it happens - a pre-op check genuinely does
            // not know when the patient will go home yet. The form asks for it instead (see the field's hint).
            return null;
        }

        // A length of stay ECLIPSE will take: whole nights, at least one. Rejected here rather than sent, because
        // the number is a declaration about the episode and Medicare answers a malformed one with the same
        // internal error as a missing one.
        function isWholeNumberOfNights(value) {
            if (value === null || value === undefined || value === '') {
                return false;
            }
            const nights = Number(value);
            return Number.isInteger(nights) && nights >= 1;
        }

        // The accident date is what makes the accident indicator mean anything, and ECLIPSE will not take one
        // without the other.
        function accidentIssue() {
            const cfg = $ctrl.eclipseConfig;
            if (!cfg.accident) {
                return null;
            }
            if (!cfg.accidentDate) {
                return {
                    title: "Accident Date Required",
                    message: "This claim is marked accident-related, so ECLIPSE requires the date the accident happened."
                };
            }

            const accidentDate = toDateParam(cfg.accidentDate);
            const admission = toDateParam(cfg.admissionDate);
            if (accidentDate && admission && accidentDate > admission) {
                return {
                    title: "Check Dates",
                    message: "The accident date is after the admission date, so the admission cannot have been " +
                        "for it. Correct whichever of the two is wrong."
                };
            }
            return null;
        }

        // A row the practice filled in half of. claimItems() takes only the rows carrying both a code and a
        // charge, so either half on its own drops off the claim - the practice bills four items, three are
        // claimed, and nothing says so. A SYSTEM item carries no fee to fill the charge in with, which is
        // exactly how a row ends up like this.
        function incompleteItemIssue() {
            const unpriced = $ctrl.eclipseItems.find($ctrl.isItemMissingCharge);
            if (unpriced) {
                return {
                    title: "Charge Amount Required",
                    message: unpriced.charge_amount === 0
                        ? "Item " + unpriced.item_code + " is charged at $0, which ECLIPSE has nothing to " +
                            "assess. Enter what the practice is charging for it, or remove the row."
                        : "Item " + unpriced.item_code + " has no charge amount, so it would not be claimed. " +
                            "Enter what the practice is charging for it, or remove the row."
                };
            }

            const uncoded = $ctrl.eclipseItems.find(item => !item.item_code && isChargeable(item));
            if (uncoded) {
                return {
                    title: "Item Code Required",
                    message: "A claim item carries a charge of " + $filter('currency')(uncoded.charge_amount) +
                        " with no MBS item code, so it would not be claimed. Pick the item, or remove the row."
                };
            }
            return null;
        }

        // The one thing standing between this form and a claim, named. Both entry points report it rather than
        // disabling their button: with this many conditional requirements, a dead button whose cause is spread
        // across three collapsed sections is not actionable. Ordered as the form reads, so the first complaint
        // is the topmost problem.
        $ctrl.eclipseClaimIssue = function () {
            const cfg = $ctrl.eclipseConfig;

            if (!cfg.claimTypeCode) {
                return { title: "Processing Class Required", message: "Select the claim's processing class (agreement, scheme, patient claim, billing agent or Medicare only)." };
            }
            if (!cfg.serviceTypeCode) {
                return { title: "Service Type Required", message: "Select the service type this claim is for." };
            }
            if (!cfg.facilityId) {
                return { title: "Facility ID Required", message: "Enter the hospital or day surgery facility ID the admission took place at. It can be defaulted for every claim by setting the Tyro facility ID on your practice." };
            }
            if (!cfg.providerNumber) {
                return {
                    title: "Principal Provider Required",
                    message: "Enter the principal provider number the claim is lodged under. It is the provider " +
                        "the benefit is paid to, and ECLIPSE will not take a claim without one."
                };
            }
            // The same check the referring and billing agent numbers already get. Worth having here because of
            // how Medicare answers a number it cannot match: rather than calling it malformed, its schema drops
            // the field and the request comes back saying the number "must be supplied" - which sends the
            // practice looking for an empty field when the one on the form has something in it.
            if (!$ctrl.isProviderNumberValid(cfg.providerNumber)) {
                return { title: "Check Principal Provider ID", message: providerNumberMessage(cfg.providerNumber) };
            }
            // Optional, and blank means the principal provider rendered the service. But anything typed into it
            // is checked like any other number, because ECLIPSE reads it as the servicing provider on the
            // medical event and refuses the event the same way.
            if (cfg.servicingProviderNumber && !$ctrl.isProviderNumberValid(cfg.servicingProviderNumber)) {
                return { title: "Check Servicing Provider ID", message: providerNumberMessage(cfg.servicingProviderNumber) };
            }
            if ($ctrl.isAccountReferenceTooLong()) {
                return {
                    title: "Account Reference Too Long",
                    message: ($ctrl.isPatientClaim()
                        ? "A patient claim takes at most " + $ctrl.accountReferenceLimit() + " characters of " +
                            "account reference, where the other classes take " + $ctrl.ACCOUNT_REFERENCE_MAX_LENGTH + ". "
                        : "A claim takes at most " + $ctrl.accountReferenceLimit() + " characters of account reference. ") +
                        "Shorten it, or clear it to fall back to the patient ID."
                };
            }
            // An overseas claim only describes an admission when there was one - it is the one channel that
            // also covers out-of-hospital services, so the dates are conditional on that answer.
            if ($ctrl.requiresAdmissionDate() && !cfg.admissionDate) {
                return { title: "Admission Date Required", message: "Enter the date the patient was admitted." };
            }
            if ($ctrl.requiresAdmissionDate()) {
                const datesIssue = episodeDatesIssue();
                if (datesIssue) {
                    return datesIssue;
                }
            }
            const accidentDateIssue = accidentIssue();
            if (accidentDateIssue) {
                return accidentDateIssue;
            }
            const overseas = overseasClaimIssue();
            if (overseas) {
                return overseas;
            }
            // The presenting illness is what an eligibility check is quoted against, so it belongs to the claims
            // that run one. An overseas claim does not - the fund assesses the claim itself, and the schema carries
            // no such field - and neither does a Medicare only claim, which has no fund to check with at all.
            if (!$ctrl.isOverseas() && !$ctrl.isMedicareOnlyClaim() &&
                !(cfg.presentingIllnessType === 'item' && !!cfg.presentingIllnessItemNumber) &&
                !(cfg.presentingIllnessType === 'code' && !!cfg.presentingIllnessCode)) {
                return { title: "Presenting Illness Required", message: "Enter the presenting illness, either as an MBS item number or as an illness code." };
            }
            // Medicare directs an MB or MO benefit to an agent, and will not do so without knowing who to and
            // that the patient agreed to it. Both channels, since both carry MB.
            if ($ctrl.requiresBillingAgent() && !cfg.billingAgentProviderNumber) {
                return {
                    title: "Billing Agent Required",
                    message: "A " + cfg.claimTypeCode + " claim directs the benefit to a billing agent, so their " +
                        "provider number is required."
                };
            }
            // The agent's number is a Medicare provider number like any other on this form, and the same mistake
            // gets made in it: a letter O typed where the practice location is a zero. Medicare's location
            // characters deliberately exclude O, I, S and Z for exactly that reason (see PROVIDER_LOCATION_CHARS),
            // so such a number is not merely mis-keyed, it cannot exist. Caught here rather than by Medicare, which
            // sees it as a claim to reject.
            if ($ctrl.requiresBillingAgent() && !$ctrl.isProviderNumberValid(cfg.billingAgentProviderNumber)) {
                return {
                    title: "Check Billing Agent Provider Number",
                    message: cfg.billingAgentProviderNumber.trim().length !== 8
                        ? "A provider number is eight characters: a 6 digit stem, a practice location character and a check digit. " + cfg.billingAgentProviderNumber + " is not."
                        : "Medicare does not recognise " + cfg.billingAgentProviderNumber + " as a provider number. The 7th character is the practice location and the 8th a check digit calculated from the first seven, so one mistyped character invalidates the whole number - most often a letter O typed for a zero, which Medicare does not use as a location character at all."
                };
            }
            if ($ctrl.requiresBillingAgent() && !cfg.benefitAssignmentAuthorised) {
                return {
                    title: "Benefit Assignment Required",
                    message: "A " + cfg.claimTypeCode + " claim requires the patient to have authorised assignment of " +
                        "their benefit to that billing agent, which Medicare will only accept as authorised."
                };
            }
            // "Submission Authority Indicator must only be set when Claim Type Code is set to PC" - and Medicare
            // will not take a patient claim without it. The same tick records the eligibility enquiry's authority
            // on every other class, so it is only a blocking requirement here.
            if ($ctrl.requiresSubmissionAuthority() && !cfg.consent) {
                return {
                    title: "Submission Authority Required",
                    message: "A patient claim is submitted on the patient's behalf, so Medicare requires their " +
                        "authority to submit it. Confirm it below, or lodge this under another processing class."
                };
            }
            if ($ctrl.requiresIfcIssueCode() && !cfg.ifcIssueCode) {
                return { title: "Informed Financial Consent Required", message: "Medicare requires informed financial consent on agreement and scheme claims. Select how it was obtained." };
            }
            if ($ctrl.requiresFinancialInterestDisclosure() && !cfg.financialInterestDisclosure) {
                return {
                    title: "Financial Interest Disclosure Required",
                    message: "A scheme claim is made under a gap cover scheme, which requires the provider to have disclosed any financial interest they hold in what the patient was given. Medicare will only accept a scheme claim as disclosed, so confirm the disclosure was made or lodge this as another claim type."
                };
            }
            if ($ctrl.requiresReferral() && !$ctrl.hasReferralDetails()) {
                return referralIssue();
            }
            if ($ctrl.requiresReferral()) {
                const validityIssue = referralValidityIssue();
                if (validityIssue) {
                    return validityIssue;
                }
            }
            // Reported before "no items at all": a form with one half-filled row has items on it as far as the
            // practice is concerned, and being told to add one would be answering a question they did not ask.
            const incompleteItem = incompleteItemIssue();
            if (incompleteItem) {
                return incompleteItem;
            }
            if (claimItems().length === 0) {
                return { title: "Claim Items Required", message: "Add at least one MBS item with a charge amount." };
            }
            if ($ctrl.requiresReferralPeriodMonths() && !$ctrl.referralPeriodMonthsValid()) {
                return {
                    title: "Referral Length Required",
                    message: "A non-standard referral has to say how many months it runs for, as a whole number " +
                        "from 1 to 97. Enter it, or pick a standard or indefinite period instead."
                };
            }
            // The server can only warn about this once the claim has gone, so it is asked for while the claim
            // is still being built.
            if ($ctrl.requiresReferralSupportingText() && !$ctrl.hasReferralSupportingText()) {
                return {
                    title: "Supporting Text Required",
                    message: "Medicare wants the reason for any referral period other than the standard one " +
                        "(this one is " + $ctrl.selectedReferralPeriodName() + "). Enter it as the service text " +
                        "on one of the claim items."
                };
            }
            const groupItem = claimItems().find($ctrl.itemPatientsSeenConflictsWithReferral);
            if (groupItem) {
                return {
                    title: "Patients Seen Not Allowed With A Referral",
                    message: "Medicare will not take a patient count on a service claimed under a referral: a " +
                        "referral names the one patient who was referred, so the two contradict each other. " +
                        "Clear Patients Seen on item " + groupItem.item_code + " - a specialist claim cannot " +
                        "drop its referral, so the count is the half that has to go."
                };
            }
            const overlongItem = claimItems().find($ctrl.isServiceTextTooLong);
            if (overlongItem) {
                return {
                    title: "Service Text Too Long",
                    message: "ECLIPSE accepts at most " + $ctrl.SERVICE_TEXT_MAX_LENGTH + " characters of service text on an item. Shorten the text on item " +
                        overlongItem.item_code + "."
                };
            }
            if (!cfg.consent) {
                return { title: "Consent Required", message: "Patient consent must be obtained before running an eligibility check or lodging a claim." };
            }
            if ($ctrl.requiresAdmissionDate() && $ctrl.admissionAfterServiceDate()) {
                return { title: "Check Dates", message: "The admission date cannot be after the service date of a claim item. Adjust the admission date or the item's service date." };
            }
            // The same floor as the admission date, and ECLIPSE reports the two together: "Date of Service must
            // not be more than 7 days prior to the date request is received."
            const staleItem = claimItems().find($ctrl.itemServiceDateTooEarly);
            if (staleItem) {
                return {
                    title: "Service Date Too Old",
                    message: "ECLIPSE will not accept a service dated more than " +
                        $ctrl.CLAIM_BACKDATE_LIMIT_DAYS + " days before the day it receives the claim. Item " +
                        staleItem.item_code + " is dated earlier than that."
                };
            }
            // A claimant the practice started and did not finish. Medicare matches one on their name and their
            // card together, so a half-entered claimant is refused - and refused by a message that names a field
            // several sections up the form from the button that was pressed.
            const claimantIssue = $ctrl.claimantIssue();
            if (claimantIssue) {
                return { title: "Claimant Incomplete", message: claimantIssue };
            }
            return null;
        };

        // The rules that only apply to an overseas claim, each one a rejection Medicare would otherwise send
        // back days later. Returns null on an in-patient claim, which none of them govern.
        function overseasClaimIssue() {
            if (!$ctrl.isOverseas()) {
                return null;
            }
            const cfg = $ctrl.eclipseConfig;

            // "If Referral Override Code is set to H (Hospital) then Hospital Indicator must be set to Y."
            if (cfg.referralType === 'OVERRIDE' && cfg.referralOverrideCode === 'H' && !cfg.hospital) {
                return {
                    title: "Check Hospital Indicator",
                    message: "A hospital in-patient referral override says the referral was made in hospital, so the " +
                        "claim has to be lodged as an in-hospital service too. Change the setting, or pick a different " +
                        "reason for having no referral."
                };
            }
            return null;
        }

        // Which part of the chosen referral route is missing. ECLIPSE declines a specialist claim (code 2030)
        // without one of the three, so this is a hard requirement.
        function referralIssue() {
            const cfg = $ctrl.eclipseConfig;
            if (cfg.referralType === 'OVERRIDE') {
                return { title: "Referral Override Required", message: "Select why this specialist claim carries no referral." };
            }
            if (cfg.referralType === 'SELF_DEEMED') {
                return { title: "Self-Deemed Code Required", message: "Select whether the service was self-deemed or a substituted service." };
            }
            if (!cfg.referringProviderNumber) {
                return { title: "Referring Provider Required", message: "Enter the referring provider's number, or record an override or self-deemed code instead." };
            }
            // Reports any unusable number, including one still half typed - unlike the field's inline warning,
            // which waits for eight characters because it is read while typing.
            if (!$ctrl.isProviderNumberValid(cfg.referringProviderNumber)) {
                return {
                    title: "Check Referring Provider Number",
                    message: cfg.referringProviderNumber.trim().length !== 8
                        ? "A provider number is eight characters: a 6 digit stem, a practice location character and a check digit. " + cfg.referringProviderNumber + " is not."
                        : "Medicare does not recognise " + cfg.referringProviderNumber + " as a provider number. Its last character is a check digit calculated from the other seven, so one mistyped character invalidates the whole number."
                };
            }
            if (!cfg.referralDate) {
                return { title: "Referral Date Required", message: "Enter the date the referral was issued." };
            }
            if (!cfg.referralPeriod) {
                return { title: "Referral Period Required", message: "Select how long the referral is valid for." };
            }
            return null;
        }

        // Runs the OECW check. The backend persists the quote as an ECLIPSE_ELIGIBILITY transaction and returns
        // its transaction_id, so every check records one row - we never create the transaction ourselves.
        $ctrl.checkEclipseEligibility = function () {
            const issue = $ctrl.eclipseClaimIssue();
            if (issue) {
                return showValidationError(issue.title, issue.message);
            }

            // A check records its own quote and supersedes the one above it. Where that quote's account has been
            // paid, superseding it would leave the money on a discarded row and the new quote would raise a
            // second invoice for the same surgery - so it is refused, and the claimant correction below is
            // offered instead, which is what a refused claim on a paid account actually needs.
            if ($ctrl.accountHasBeenDealtWith()) {
                return showValidationError("This account has been paid",
                    "The patient has already settled the account against this quote, so a new eligibility check "
                    + "cannot replace it - the payment would be left on the superseded quote and this surgery "
                    + "would be invoiced twice. Correct the claimant above and lodge again, or resolve the "
                    + "payment in your accounting system before starting a new claim.");
            }

            // The claimant is checked with Medicare on the way through, so nobody has to remember to press the
            // button - most will not, and the point of verifying is lost if it only happens when asked.
            //
            // Not a gate. Medicare declining to match a claimant is worth stopping on, but it is not proof the
            // details are wrong: its records can be stale, and the practice has the card in front of them. So
            // they are told, and they decide.
            if ($ctrl.requiresClaimant() && $ctrl.hasNamedClaimant()
                    && !$ctrl.claimantAccepted() && !$ctrl.claimantVerifyOverridden) {
                return $ctrl.verifyClaimant().finally(function () {
                    // Accepted is enough to go on, differences and all - see claimantAccepted(). What Medicare
                    // would like corrected is shown on the panel rather than held over the check.
                    if ($ctrl.claimantAccepted()) {
                        return runEclipseEligibilityCheck();
                    }
                    askToCheckUnverifiedClaimant();
                });
            }

            return runEclipseEligibilityCheck();
        };

        /**
         * Offers the check anyway when Medicare would not confirm the claimant.
         *
         * The dialog's second button is the override: pressing it records the choice and runs the check, so the
         * practice is not asked again for the same claimant. Dismissing leaves them on the form with the
         * claimant panel open to correct.
         */
        function askToCheckUnverifiedClaimant() {
            const runAnyway = function () {
                if ($scope.generalModal) {
                    $scope.generalModal.hide();
                }
                $scope.negativeButton = null;
                $scope.onNegativeButtonClick = null;
                $ctrl.claimantVerifyOverridden = true;
                runEclipseEligibilityCheck();
            };

            const detail = $ctrl.claimantVerifyError
                || $ctrl.claimantVerificationMessage()
                || "Medicare did not confirm this claimant's card.";

            showValidationError("Claimant not verified",
                detail + " Check the claimant's name, date of birth and card number against their Medicare card. "
                + "You can run the check anyway - Medicare's records are not always current, and a claim it "
                + "declined to match may still be paid.",
                { label: "Run Check Anyway", onClick: runAnyway });
        }

        function runEclipseEligibilityCheck() {

            // Read before the state below is cleared: this check replaces the quote above it, and the row that
            // quote was recorded against has to go with it.
            const supersededTransactionId = $ctrl.eclipseQuote ? $ctrl.eclipseQuote.transaction_id : null;

            $ctrl.eclipseLoading = true;
            $ctrl.eclipseError = null;
            $ctrl.eclipseQuote = null;
            $ctrl.eclipseTotals = null;

            // A re-check produces its own transaction, so anything the previous quote led to belongs to a record
            // this one will not promote. Left behind, the lodgement step would show a "claim lodged" banner for
            // a claim unrelated to the quote above it.
            $ctrl.lodgedTransaction = null;
            $ctrl.lodgeError = null;

            TyroAPI.checkEclipseEligibility(buildEligibilityRequest()).then(function (response) {
                $ctrl.eclipseQuote = response.data;
                $ctrl.eclipseTotals = summariseQuote(response.data);

                // Only once the replacement exists: a failed check leaves the practice with the quote they
                // already had rather than nothing at all.
                discardSupersededQuote(supersededTransactionId);

                // An assessed quote is the whole input to lodgement, so the flow moves on. Anything else has
                // nothing to lodge against and stays here, where the details can be corrected and the check
                // re-run: a check still being assessed, one the funder refused, and one it never answered
                // because it could not be reached. Moving on over a refusal was what left the practice on the
                // lodgement step with a live Lodge button whose only possible outcome was the server refusing
                // to promote a rejected quote.
                if ($ctrl.isQuoteLodgeable()) {
                    $ctrl.eclipseStep = 'LODGE';
                }
            }).catch(function (error) {
                $ctrl.eclipseError = errorMessage(error, $ctrl.runsEligibilityCheck()
                    ? "Unable to complete the ECLIPSE eligibility check. Please try again."
                    : "Unable to record the claim details. Please try again.");
            }).finally(function () {
                $ctrl.eclipseLoading = false;
            });
        }

        // Every check records its own transaction server side, so the quote a re-check replaces has to be
        // discarded or both sit in the claims list as lodgeable quotes for the same episode of care, with
        // nothing but their timestamps to tell them apart - and lodging both claims the surgery twice.
        //
        // Cleanup rather than an action the practice asked for, so a failure is logged and left: a quote that
        // cannot be discarded (one the funder has already rejected, say) is not lodgeable either, so it cannot
        // become the duplicate this is here to prevent.
        function discardSupersededQuote(transactionId) {
            if (!transactionId) {
                return;
            }
            TyroAPI.deleteTransaction(transactionId, $ctrl.patient.id).catch(function (error) {
                console.error("Failed to discard the superseded ECLIPSE eligibility quote " + transactionId + ":", error && error.status);
            });
        }

        // Accepted but not yet assessed, so there is nothing to show or lodge against. The transaction row
        // exists either way; only the assessment is outstanding.
        $ctrl.isQuotePending = function () {
            return !!$ctrl.eclipseQuote && $ctrl.eclipseQuote.status === 'PENDING';
        };

        // The funder's final no, in the two vocabularies ECLIPSE answers in - the documented OECW status
        // strings, and Medipass's own verdict on the claim and the invoice. Deliberately the same list the
        // server maps to a REJECTED transaction, because that is what decides whether it will promote the
        // quote: any outcome refused there must be refused here too, or the screen offers a lodgement that
        // cannot happen.
        const REJECTED_QUOTE_STATUSES = ['HEALTH_FUND_REJECTED', 'MEDICARE_REJECTED', 'REJECTED', 'DECLINED'];
        // Not a no, but not an answer either: the funder or Medicare could not be reached. Nothing was
        // assessed, so there is no quote to lodge - this one is simply run again.
        const UNAVAILABLE_QUOTE_STATUSES = ['HEALTH_FUND_UNAVAILABLE', 'MEDICARE_UNAVAILABLE'];

        function quoteStatusIn(statuses) {
            return !!$ctrl.eclipseQuote
                && statuses.indexOf(($ctrl.eclipseQuote.status || '').toUpperCase()) !== -1;
        }

        $ctrl.isQuoteRejected = function () {
            return quoteStatusIn(REJECTED_QUOTE_STATUSES);
        };

        $ctrl.isQuoteUnavailable = function () {
            return quoteStatusIn(UNAVAILABLE_QUOTE_STATUSES);
        };

        // Which of Medicare and the health fund said no, so the callout names the one to go and argue with
        // rather than leaving the practice to guess from the message.
        $ctrl.quoteRejectedBy = function () {
            const status = ($ctrl.eclipseQuote && $ctrl.eclipseQuote.status || '').toUpperCase();
            if (status.indexOf('MEDICARE') === 0) {
                return 'Medicare';
            }
            if (status.indexOf('HEALTH_FUND') === 0) {
                return 'The health fund';
            }
            return 'The funder';
        };

        // A quote is lodgeable when it is an answer, and an answer of yes. Everything else keeps the flow on
        // the claim details, which is the only place any of it can be fixed.
        $ctrl.isQuoteLodgeable = function () {
            return !!$ctrl.eclipseQuote && !$ctrl.isQuotePending()
                && !$ctrl.isQuoteRejected() && !$ctrl.isQuoteUnavailable();
        };

        $ctrl.quotedTransactionId = function () {
            return ($ctrl.eclipseQuote && !$ctrl.isQuotePending()) ? $ctrl.eclipseQuote.transaction_id : null;
        };

        // --- Settling the patient's account, before a patient (PC) claim is lodged ---
        //
        // The patient claim is the one class the patient pays: the fund rebates them directly, so no benefit ever
        // reaches the practice's invoice and the whole charge is theirs. ECLIPSE carries that as accountPaidInd,
        // and it used to be a tick box on this screen - a declaration about money that nothing checked.
        //
        // Now the account is raised here, the payment is taken against it here, and accountPaidInd is worked out
        // server side from what was actually collected. Lodging is refused until something has been - see
        // AuTyroEclipseServiceImpl.applyCollectedAccountPaid(), which is the real gate; the button below only
        // reflects it.

        // The invoice raised for the account, with its Xero figures attached. Null until it is raised.
        $ctrl.patientClaimInvoice = null;
        // What the terminal component is pointed at. A stable object rather than a function: the binding is
        // one-way, and an expression rebuilding an object every digest never settles.
        $ctrl.patientClaimTerminalTarget = null;
        $ctrl.accountLoading = false;
        $ctrl.accountError = null;
        // Set while the account is being watched for a terminal payment Tyro has yet to post, and after that
        // watch has given up - see pollAccountForPayment(). Roughly fifteen seconds of watching, which is well
        // past the second or two the webhook normally takes.
        $ctrl.accountConfirming = false;
        const ACCOUNT_CONFIRM_ATTEMPTS = 8;
        const ACCOUNT_CONFIRM_INTERVAL_MS = 2000;
        let accountPollPromise = null;
        let destroyed = false;
        // 'TERMINAL' charges a Tyro machine; 'MANUAL' records a payment taken another way. The terminal leads
        // because that is how an account is settled with the patient at the desk - where the practice has the
        // Tyro terminal feature. Without it there is only the manual form.
        $ctrl.hasTyroTerminal = function () {
            return Session.hasTyroTerminal();
        };
        $ctrl.accountPaymentMode = $ctrl.hasTyroTerminal() ? 'TERMINAL' : 'MANUAL';
        $ctrl.manualPayment = { value: null, account: null, reference_type: null };

        // Whether this claim can have its account settled here, before it is lodged. Not a requirement - see
        // lodgeBlockedByAccount() - but the option, and the one class it makes sense on: the patient claim is
        // paid by the patient rather than the fund.
        //
        // In-patient only: an overseas claim is created by its own lodgement, so there is no quote here to raise
        // an invoice against.
        $ctrl.requiresAccountSettlement = function () {
            return $ctrl.isPatientClaim() && !$ctrl.isOverseas() && !$ctrl.lodgedTransaction;
        };

        // What the account comes to - the full charge, which is what a patient claim is invoiced at.
        $ctrl.patientClaimAccountTotal = function () {
            return $ctrl.claimItemsTotal();
        };

        $ctrl.accountAmountPaid = function () {
            const invoice = $ctrl.patientClaimInvoice;
            return (invoice && invoice.amount_paid) ? invoice.amount_paid : 0;
        };

        // What Xero says is still owing. An invoice with nothing recorded against it reports its total, the same
        // way the claims screen reads a freshly raised one.
        $ctrl.accountAmountDue = function () {
            const invoice = $ctrl.patientClaimInvoice;
            if (!invoice) {
                return $ctrl.patientClaimAccountTotal();
            }
            if (invoice.amount_due === undefined || invoice.amount_due === null) {
                return invoice.total || 0;
            }
            return invoice.amount_due;
        };

        $ctrl.accountIsSettledInFull = function () {
            return !!$ctrl.patientClaimInvoice && $ctrl.accountAmountPaid() > 0 && $ctrl.accountAmountDue() <= 0.005;
        };

        // The gate the lodge button reads: the account settled in full, not merely paid something towards.
        //
        // A patient claim goes to Medicare with accountPaidInd, and the only value it can honestly carry is Y -
        // the practice takes the whole charge at the desk and the patient claims the benefit back. A part-paid
        // account would lodge as N, which tells Medicare the patient still owes the practice while the practice
        // has already sent the claim, and leaves a balance nothing in CatTrax then chases.
        $ctrl.accountHasBeenDealtWith = function () {
            return $ctrl.accountAmountPaid() > 0 && $ctrl.accountAmountDue() <= 0.005;
        };

        // Whether the lodge button is held closed by an unsettled account. Only a patient claim can be: on every
        // other class the funder settles to the practice and there is nothing to collect first.
        //
        // The button is disabled rather than left live to fail, because the server refuses this lodgement anyway -
        // see applyCollectedAccountPaid() - and a button that goes off to fetch a refusal is a worse way to say
        // "not yet" than one that is plainly not ready. The reason rides on the button's own title.
        /**
         * Whether the claim's details may still be changed from the lodgement step.
         *
         * Two reasons they may not, and both end in the same place - a claim that no longer matches what was
         * quoted, paid for, or both:
         *
         *   - A quote resumed from the claims list was never loaded into the form. Only what the lodgement step
         *     shows and gates on is rebuilt (see resumeQuotedClaim), because lodgement promotes the stored row
         *     server side and does not read the form. Going "back" from here would open a form with almost
         *     nothing in it, and re-checking would replace a complete quote with that.
         *
         *   - The claim is away with the funder. It is being assessed as it was submitted, and an invoice was
         *     raised against it as it went, so changing the items or the episode now would leave a settled
         *     invoice attached to a claim for something else.
         *
         * A claim the funder refused is neither of those. Nothing further is coming for it, and no invoice stands
         * against it - the server does not raise one for a REJECTED or FAILED lodgement, and voids the one an
         * overseas claim was lodged with if a later report refuses it. So the form is exactly where such a claim
         * should go, and asking here that nothing had been lodged made the one button that offers it unreachable:
         * that button also asks that the claim was refused, which cannot be true without a lodged claim to have
         * been refused.
         */
        $ctrl.canEditClaimDetails = function () {
            if ($ctrl.resumedFromClaimsList) {
                return false;
            }
            return !$ctrl.lodgedTransaction || $ctrl.lodgedClaimWasRefused();
        };

        // Why the form is closed, for the note that sits where the button was.
        $ctrl.claimDetailsLockedReason = function () {
            if ($ctrl.canEditClaimDetails()) {
                return null;
            }
            return "This quote was opened from the claims list to be finished, so its details are not loaded "
                + "here and cannot be changed.";
        };

        // Nothing holds the lodge button closed on account of the money any more.
        //
        // ECLIPSE permits accountPaidInd=Y only on a patient claim but never requires it: N is a valid patient
        // claim whose account is still outstanding. Requiring payment first was CatTrax's own rule and it cost
        // more than it saved - a claim refused after the money was taken left the practice holding a payment
        // against a claim that never went. Lodging first risks nothing, because on this class the patient owes
        // the full charge whatever the funder decides.
        //
        // Kept as a function rather than deleted: the lodge button still asks, and a later rule about the
        // account has one obvious place to live.
        $ctrl.lodgeBlockedByAccount = function () {
            return false;
        };

        // Anything the server said would stop this claim. Held apart from the account gate so the button can say
        // which of the two it is waiting on.
        $ctrl.lodgementIssues = [];
        $ctrl.lodgementIssuesLoading = false;

        $ctrl.lodgeBlockedByIssues = function () {
            return $ctrl.lodgementIssuesLoading || ($ctrl.lodgementIssues || []).length > 0;
        };

        // Raises and finalises the invoice, which is what makes the account payable: Xero refuses a payment
        // against a draft. Idempotent server side, so this doubles as the refresh after a payment lands.
        $ctrl.raisePatientClaimAccount = function () {
            const quotedTransactionId = $ctrl.quotedTransactionId();
            if (!quotedTransactionId || $ctrl.accountLoading) {
                return $q.resolve();
            }

            $ctrl.accountLoading = true;
            $ctrl.accountError = null;

            return TyroAPI.raisePatientClaimInvoice(quotedTransactionId, $ctrl.patient.id).then(function (response) {
                $ctrl.patientClaimInvoice = response.data.invoice || null;
                $ctrl.accountInvoiceRemoved = false;
                // Rebuilt only when the invoice it points at changes, so the terminal component is not torn down
                // and remounted - with a fresh payment transaction each time - by an account refresh.
                const invoiceId = response.data.invoice_id;
                if (!$ctrl.patientClaimTerminalTarget || $ctrl.patientClaimTerminalTarget.invoice_id !== invoiceId) {
                    $ctrl.patientClaimTerminalTarget = {
                        invoice_id: invoiceId,
                        transaction_id: response.data.transaction_id,
                    };
                }
                // Seeded to what is still owing, which is what the practice is nearly always collecting.
                $ctrl.manualPayment.value = $ctrl.accountAmountDue();
                if (!$ctrl.manualPayment.account && $ctrl.accountPaymentId) {
                    $ctrl.manualPayment.account = $ctrl.accountPaymentId;
                }
            }).catch(function (error) {
                $ctrl.accountError = errorMessage(error, "The patient's account could not be raised. Please try again.");
            }).finally(function () {
                $ctrl.accountLoading = false;
            });
        };

        // Voids an unpaid account for a claim that is not going to be lodged as a patient claim any more. Quiet
        // about failure: the server refuses a paid one, and that refusal is not something the practice asked for
        // or can act on here - they changed a drop-down.
        $ctrl.discardPatientClaimAccount = function () {
            const quotedTransactionId = $ctrl.quotedTransactionId();
            // Nothing raised, so nothing to void. Checked before the state is cleared, because this runs on every
            // claim-type change and most of those never went near a patient claim.
            const hadAccount = !!$ctrl.patientClaimInvoice;

            $ctrl.patientClaimInvoice = null;
            $ctrl.patientClaimTerminalTarget = null;
            $ctrl.accountError = null;
            $ctrl.accountInvoiceRemoved = false;
            $ctrl.manualPayment = { value: null, account: null, reference_type: null };

            if (!quotedTransactionId || !hadAccount) {
                return;
            }
            TyroAPI.discardPatientClaimInvoice(quotedTransactionId, $ctrl.patient.id).catch(function (error) {
                console.error("Could not discard the patient claim account:", error && error.status);
            });
        };

        // The terminal has reported back, however it was left. The account is re-read rather than assumed: the
        // payment is posted onto the invoice by Tyro's webhook, so what matters is what Xero now says, and a
        // charge the patient approved a moment before the window closed still counts.
        /**
         * The terminal has reported back, so the account is watched until the payment lands on it.
         *
         * It is Tyro that posts a terminal payment onto the invoice, by webhook, not this browser - so the moment
         * the SDK closes is the moment the payment is least likely to be there yet. Re-reading once, which is
         * what this did, therefore usually came back unpaid and left the practice looking at a disabled Lodge
         * button with nothing to say that waiting a second would fix it.
         *
         * So it re-reads on a short cycle and stops the moment the money appears. The practice sees "confirming",
         * then a settled account - rather than an explanation of who posts what, and a link to press.
         */
        $ctrl.onAccountTerminalComplete = function (status, payload, paymentTaken) {
            // Nothing was charged - the operator closed the Tyro window, or the charge was cancelled at the
            // terminal. There is no webhook coming, so watching for one would be fifteen seconds of "confirming"
            // over an account nobody paid into. The account is left exactly as it was, with the check by hand
            // offered in case the terminal and the SDK disagreed about what happened.
            if (!paymentTaken) {
                $ctrl.accountConfirming = false;
                return;
            }
            pollAccountForPayment(ACCOUNT_CONFIRM_ATTEMPTS);
        };

        function pollAccountForPayment(attemptsLeft) {
            $ctrl.accountConfirming = true;

            $ctrl.raisePatientClaimAccount().finally(function () {
                // A read still in flight when the widget closed must not schedule another one.
                if (destroyed) {
                    return;
                }
                if ($ctrl.accountHasBeenDealtWith()) {
                    $ctrl.accountConfirming = false;
                    return;
                }
                if (attemptsLeft <= 1) {
                    // Given up on rather than spun on forever: the payment may simply have been abandoned at the
                    // terminal, which looks identical from here. The panel offers a check by hand from then on.
                    $ctrl.accountConfirming = false;
                    return;
                }
                accountPollPromise = $timeout(function () {
                    pollAccountForPayment(attemptsLeft - 1);
                }, ACCOUNT_CONFIRM_INTERVAL_MS);
            });
        }

        // Stops the cycle following the practice around after they have left the step.
        $ctrl.$onDestroy = function () {
            destroyed = true;
            if (accountPollPromise) {
                $timeout.cancel(accountPollPromise);
                accountPollPromise = null;
            }
        };

        // The account as last read is what the manual form validates against, so a payment landing on it from
        // elsewhere - the terminal's webhook, another user - would leave the form offering a balance Xero no longer
        // holds. Re-read when that happens rather than waiting for the server to refuse the payment.
        InvoiceLiveUpdateService.watchInvoice($scope, {
            patientId: function () {
                return $ctrl.patient && $ctrl.patient.id;
            },
            invoice: function () {
                return $ctrl.patientClaimInvoice;
            },
            onChanged: function (fresh) {
                if (fresh) {
                    $ctrl.raisePatientClaimAccount();
                    return;
                }
                // Deleted outright. Not re-raised from here: that would put a new account on the patient that
                // nobody asked for.
                $ctrl.accountInvoiceRemoved = true;
                $ctrl.accountError = "This account was removed elsewhere. Reopen the claim before recording a payment.";
            },
        });

        $ctrl.useManualAccountPayment = function () {
            $ctrl.accountPaymentMode = 'MANUAL';
            $ctrl.manualPayment.value = $ctrl.accountAmountDue();
        };

        $ctrl.useTerminalAccountPayment = function () {
            if (!$ctrl.hasTyroTerminal()) return;
            $ctrl.accountPaymentMode = 'TERMINAL';
        };

        $ctrl.isManualPaymentValid = function () {
            return !!$ctrl.patientClaimInvoice
                && !$ctrl.accountInvoiceRemoved
                && $ctrl.manualPayment.value > 0
                && $ctrl.manualPayment.value <= $ctrl.accountAmountDue()
                && !!$ctrl.manualPayment.account
                && !!$ctrl.manualPayment.reference_type;
        };

        // Records a payment taken another way - cash at the desk, a bank transfer that arrived last week, a card
        // run on a standalone machine. This is the route for an account already settled before the patient
        // arrived, which is what the old tick box was really claiming: the difference is that the money is now
        // recorded against the invoice rather than merely asserted.
        $ctrl.recordManualAccountPayment = function () {
            if (!$ctrl.isManualPaymentValid() || $ctrl.accountLoading) {
                return;
            }

            $ctrl.accountLoading = true;
            $ctrl.accountError = null;

            BillingAPI.updatePayment($ctrl.patient.id, $ctrl.patientClaimInvoice.invoice_id, {
                amount: $ctrl.manualPayment.value,
                account_id: $ctrl.manualPayment.account,
                reference_type: $ctrl.manualPayment.reference_type,
                date: $filter("date")(new Date(), "yyyy-MM-dd"),
            }).then(function () {
                // Re-read rather than adjusted locally, so what the gate sees is what Xero holds.
                $ctrl.accountLoading = false;
                $ctrl.raisePatientClaimAccount();
            }).catch(function (error) {
                $ctrl.accountError = errorMessage(error, "The payment could not be recorded. Please try again.");
                $ctrl.accountLoading = false;
            });
        };

        // --- Step 3: lodge the in-patient claim (IMCW) ---

        // The claim is lodged by promoting the quote, as the quote is the only thing carrying the episode of
        // care to the server: POST /api/tyro/transaction takes no admission date, referral or presenting
        // illness, so a claim built straight off this form would reach Medicare describing nothing but its item
        // codes - and a specialist claim with no referral is rejected with ECLIPSE code 2030. Hence this step
        // is only reachable from a completed check, and re-checks rather than trusting how it was reached.

        // The way back out of lodgement, whether the practice changed their mind about what is on the claim or
        // Medicare rejected it - a rejection is nearly always a detail on the form, a code it will not take or
        // a date it disagrees with.
        //
        // The form is left exactly as it was, so this lands on the claim details with everything still filled
        // in and ready to be corrected. Nothing needs undoing: a lodgement that failed persisted nothing, and
        // the stored row is still the eligibility quote either way. Coming back here is also the only route to
        // lodgement, so an edit cannot reach Medicare without the check being re-run over it - and that re-run
        // supersedes the quote this step was showing.
        $ctrl.backToClaimDetails = function () {
            $ctrl.lodgeError = null;
            $ctrl.goToEclipseClaimDetails();
        };

        // The way out of a refusal, and the same way out on both channels: correct what the funder objected to
        // and send it again. The refused claim is left standing as its own record - it is what was sent and what
        // came back, and a rejection is as much a financial record as a payment - so the quote it was promoted
        // from is dropped rather than reused. Dropping it is also what stops a second lodgement of the same
        // promoted row, which the server refuses anyway.
        //
        // Where it lands differs only because the two channels are shaped differently, not because they behave
        // differently. In-patient returns to the claim details with a check to re-run over them, since lodgement
        // there promotes a quote. Overseas returns to the same step, which is the step it lodges from - there is
        // no check to re-run and no quote to have dropped, so the nulls below are no-ops on that channel and the
        // form opens with Lodge Overseas Claim on it.
        $ctrl.correctRefusedClaim = function () {
            $ctrl.lodgedTransaction = null;
            $ctrl.lodgeError = null;
            $ctrl.eclipseQuote = null;
            $ctrl.eclipseTotals = null;
            $ctrl.goToEclipseClaimDetails();
        };

        /**
         * Whether pressing lodge now would send this patient claim as unpaid.
         *
         * Part paid counts: accountPaidInd is Y only on an account settled in full, so anything short of that
         * goes to Medicare as N and takes the unpaid pathway with it. See accountIsSettledInFull().
         *
         * Patient claims only, and in-patient ones at that. Every other class is settled to the practice by the
         * funder, so there is nothing to have collected first; an overseas claim raises its invoice as it lodges,
         * so there is no account in existence to have settled.
         */
        $ctrl.lodgingUnpaidPatientClaim = function () {
            return $ctrl.requiresAccountSettlement() && !$ctrl.accountIsSettledInFull();
        };

        /**
         * Explains the two pathways before an unpaid patient claim goes, and lets the practice pick one.
         *
         * Both are legitimate and the practice may genuinely want either, so this is a choice rather than a
         * block - but it is a choice about where the funder sends the money, and from the lodge button the two
         * look identical. Lodged paid, the rebate goes to the patient's bank account and the account is already
         * settled. Lodged unpaid, the funder posts a cheque made out to the provider to the patient, who has to
         * bring it in.
         *
         * Asked on every attempt rather than remembered: it is a question about money that has or has not been
         * taken, and the answer can change between one press and the next.
         */
        function askToLodgeUnpaidPatientClaim() {
            $scope.confirmLodgeUnpaid = function () {
                hideUnpaidPatientClaimModal();
                sendEclipseLodgement();
            };
            $scope.cancelLodgeUnpaid = hideUnpaidPatientClaimModal;

            $scope.unpaidClaimModal = $modal({
                scope: $scope,
                templateUrl: "app/src/js/components/tyroMedipassWidget/unpaid-patient-claim-modal.html",
                show: true,
                backdrop: "static",
                keyboard: false
            });
        }

        function hideUnpaidPatientClaimModal() {
            if ($scope.unpaidClaimModal) {
                $scope.unpaidClaimModal.hide();
                $scope.unpaidClaimModal = null;
            }
        }

        // One click, the same as the eligibility check on the step before: validate, then send. Promotes the
        // quote in place, so the claim reuses that transaction row - and the episode of care stored with it -
        // rather than adding a second. The claim then settles asynchronously by webhook.
        $ctrl.lodgeEclipseClaim = function () {
            // No assessed quote means there is nothing to promote, and the claim details are what produce one.
            // A refused or unanswered one is nothing to promote either - the server will not do it - so this
            // says so in the one place that can, rather than sending a lodgement out to be refused.
            const quotedTransactionId = $ctrl.quotedTransactionId();
            if (!quotedTransactionId || !$ctrl.isQuoteLodgeable()) {
                return $ctrl.goToEclipseClaimDetails();
            }

            // eclipseClaimIssue() validates the form, so it is only asked where there is a form. A resumed quote
            // has none - the row is lodged from the database and only the lodgement step's own state was rebuilt -
            // and running it there failed on fields the form would have held but lodgement never sends. The
            // presenting illness is the clearest case: ECLIPSE wants it on the eligibility check and the
            // in-patient claim body does not carry it at all, so a quote that already passed a check has
            // satisfied it by definition. On a resumed quote the server's own pre-check is the authority; it read
            // the stored row as this step opened. See refreshLodgementIssues().
            if (!$ctrl.resumedFromClaimsList) {
                const issue = $ctrl.eclipseClaimIssue();
                if (issue) {
                    return showValidationError(issue.title, issue.message);
                }
            }

            // Asked last, once everything that would stop this claim outright has been cleared: an unpaid
            // patient claim is a decision about where the funder sends the money, and there is no sense putting
            // that to somebody whose claim was never going to go.
            if ($ctrl.lodgingUnpaidPatientClaim()) {
                return askToLodgeUnpaidPatientClaim();
            }

            return sendEclipseLodgement();
        };

        // The lodgement itself, with every precondition already answered. Split out so the unpaid patient claim
        // dialog can resume it: answering that question is the last thing between the button and the send.
        function sendEclipseLodgement() {
            const quotedTransactionId = $ctrl.quotedTransactionId();

            $ctrl.lodgeLoading = true;
            $ctrl.lodgeError = null;

            // accountPaidInd is not sent on a patient claim: the server derives it from what was actually
            // collected against the account raised above, which is the only answer worth putting to Medicare.
            // Every other class sends false, which is the only value those classes accept.
            TyroAPI.submitEclipseEligibilityClaim(quotedTransactionId, $ctrl.patient.id,
                $ctrl.acceptsAccountPaid() ? null : false).then(function (response) {
                $ctrl.lodgedTransaction = response.data;
                closeIfLodgementNeedsNothingFurther();
            }).catch(function (error) {
                $ctrl.lodgeError = errorMessage(error, "The claim could not be lodged. Please try again.");
            }).finally(function () {
                $ctrl.lodgeLoading = false;
            });
        }

        // --- Overseas (OVSW) lodgement ---

        // One step, not three. There is no quote to promote because there is no eligibility check to run, so
        // this validates and lodges in a single call - which is also why it, unlike the in-patient path, sends
        // the whole episode of care itself rather than relying on a persisted quote to carry it.
        $ctrl.lodgeOverseasClaim = function () {
            const issue = $ctrl.eclipseClaimIssue();
            if (issue) {
                return showValidationError(issue.title, issue.message);
            }

            $ctrl.lodgeLoading = true;
            $ctrl.lodgeError = null;

            TyroAPI.submitOverseasClaim(buildOverseasRequest()).then(function (response) {
                $ctrl.lodgedTransaction = response.data;
                $ctrl.eclipseStep = 'LODGE';
                closeIfLodgementNeedsNothingFurther();
            }).catch(function (error) {
                $ctrl.lodgeError = errorMessage(error, "The overseas claim could not be lodged. Please try again.");
            }).finally(function () {
                $ctrl.lodgeLoading = false;
            });
        };

        // What the fund's report status means for the practice, in the terms the ECLIPSE documentation uses.
        // The same list the server labels the processing report PDF with, so the two never disagree about what a
        // status is called.
        $ctrl.overseasReportStatusLabel = function (status) {
            switch (status) {
                case 'SUCCESS': return 'Submitted - passed initial checks';
                case 'PENDING': return 'Lodged, awaiting the health fund';
                case 'RECEIVED': return 'Received by the health fund';
                case 'HEALTH_FUND_VERIFIED': return 'Health fund verification complete';
                case 'HEALTH_FUND_ASSESSING': return 'Being assessed by the health fund';
                case 'HEALTH_FUND_UNPROCESSED': return 'Lodged with the health fund, not yet processed';
                case 'COMPLETE': return 'Assessment complete';
                case 'HEALTH_FUND_REJECTED': return 'Rejected by the health fund';
                case 'HEALTH_FUND_UNAVAILABLE': return 'Health fund unavailable';
                case 'MEDICARE_REJECTED': return 'Rejected by Medicare';
                case 'MEDICARE_UNAVAILABLE': return 'Medicare unavailable';
                case 'REQUESTED_DELETE': return 'Deletion requested';
                // Medipass's own verdict on a lodgement the gateway refused before any fund saw it.
                case 'REJECTED':
                case 'DECLINED': return 'Refused on submission';
                default: return status;
            }
        };

        function buildOverseasRequest() {
            const cfg = $ctrl.eclipseConfig;
            const inHospital = !!cfg.hospital;

            return angular.extend({
                patient_id: $ctrl.patient.id,
                provider_number: providerNumberParam($ctrl.eclipseConfig.providerNumber),
                // Only when the practice names someone else. Blank means the principal provider rendered it,
                // which the server fills in - sending the same number twice would say nothing extra.
                servicing_provider_number: providerNumberParam(cfg.servicingProviderNumber),
                account_payment_id: $ctrl.accountPaymentId,
                account_items_id: $ctrl.accountItemsId,
                claim_type_code: cfg.claimTypeCode,
                service_type_code: cfg.serviceTypeCode,
                facility_id: cfg.facilityId,
                // "Fund Payee Id must only be set when Claim Type Code is set to AG, SC or MB" - a Medicare only
                // claim has no fund for one to belong to, and a patient claim is not paid to the surgeon at all.
                fund_payee_id: ($ctrl.acceptsFundPayeeId() && cfg.fundPayeeId) ? cfg.fundPayeeId : null,
                // Only meaningful on a billing agent claim, and rejected on the others.
                billing_agent_provider_number: $ctrl.requiresBillingAgent() ? providerNumberParam(cfg.billingAgentProviderNumber) : null,
                benefit_assignment_authorised: $ctrl.requiresBillingAgent() && cfg.benefitAssignmentAuthorised,
                hospital: inHospital,
                // The admission the claim describes, which an out-of-hospital claim does not have one of.
                admission_date: inHospital ? toDateParam(cfg.admissionDate) : null,
                discharge_date: inHospital ? toDateParam(cfg.dischargeDate) : null,
                compensation_claim: cfg.compensationClaim,
                accident: cfg.accident,
                accident_date: cfg.accident ? toDateParam(cfg.accidentDate) : null,
                account_reference_id: cfg.accountReferenceId || String($ctrl.patient.id),
                invoice_reference: cfg.invoiceReference || null,
                // Only a patient claim can be lodged as already settled, and on this channel it is a declaration
                // the practice makes rather than a figure read off an invoice: an overseas claim is created by
                // its own lodgement, so there is no account to have collected against before it goes. Ticked,
                // the server lodges Y and settles the invoice it raises in full; left clear, N is the truthful
                // answer and the claims screen prompts for the payment afterwards, the way it does for a gap.
                // Guarded on the class the way the form is, so a stale tick cannot reach Medicare after the
                // class was changed.
                account_paid: $ctrl.acceptsAccountPaid() && !!cfg.accountPaid,
                // The patient's authority to submit on their behalf. Sent on every class because the row it lands
                // on is what the claim is built from; the server puts it on the wire only where the class takes it
                // - "Submission Authority Indicator must only be set when Claim Type Code is set to PC".
                submission_authority: cfg.consent,
                ifc_issue_code: $ctrl.requiresIfcIssueCode() ? cfg.ifcIssueCode : null,
                financial_interest_disclosure: $ctrl.requiresFinancialInterestDisclosure() && cfg.financialInterestDisclosure,
                referring_provider_number: providerNumberParam(referralField('REFERRAL', cfg.referringProviderNumber)),
                referral_date: toDateParam(referralField('REFERRAL', cfg.referralDate)),
                referral_period: referralField('REFERRAL', cfg.referralPeriod),
                // Only on a non-standard period. ECLIPSE takes a length only against the code that has to
                // declare one, so the other two send none rather than a nought.
                referral_period_months: $ctrl.requiresReferralPeriodMonths()
                    ? (Number(cfg.referralPeriodMonths) || null) : null,
                referral_override_code: referralField('OVERRIDE', cfg.referralOverrideCode),
                self_deemed_code: referralField('SELF_DEEMED', cfg.selfDeemedCode),
                sender_contact_name: cfg.senderContactName || null,
                sender_contact_phone: cfg.senderContactPhone || null,
                sender_contact_email: cfg.senderContactEmail || null,
                // Who settled the account and is owed the fund's benefit back, sent on the patient claim alone
                // the way the eligibility check sends it. Without the card fields, which the overseas request has
                // no room for: a claimant on this channel holds no Medicare card.
                claimant_first_name: $ctrl.requiresClaimant() ? (cfg.claimantFirstName || null) : null,
                claimant_last_name: $ctrl.requiresClaimant() ? (cfg.claimantLastName || null) : null,
                claimant_date_of_birth: $ctrl.requiresClaimant() ? toDateParam(cfg.claimantDateOfBirth) : null,
                services: claimItems().map(item => ({
                    item_code: item.item_code,
                    name: item.name,
                    // An out-of-hospital claim has no admission date to fall back on, so the service date is
                    // the only date such a line ever carries.
                    service_date: toDateParam(item.service_date) || (inHospital ? toDateParam(cfg.admissionDate) : null),
                    charge_amount: item.charge_amount,
                    service_text: serviceTextOf(item) || null,
                    reference: item.reference || null,
                    service_time: item.service_time || null,
                    time_duration: (item.time_duration === '' || item.time_duration === null) ? null : Number(item.time_duration),
                    number_of_patients_seen: (item.number_of_patients_seen === '' || item.number_of_patients_seen === null)
                        ? null : Number(item.number_of_patients_seen),
                    duplicate_service_override: !!item.duplicate_service_override,
                    multiple_procedure_override: !!item.multiple_procedure_override,
                    aftercare_override: !!item.aftercare_override,
                    service_quantity: (item.service_quantity === '' || item.service_quantity === null) ? null : Number(item.service_quantity),
                    self_deemed_code: item.self_deemed_code || null,
                    lsp_number: item.lsp_number || null,
                    scp_id: item.scp_id || null,
                    rule3_exempt: !!item.rule3_exempt,
                    s4b3_exempt: !!item.s4b3_exempt,
                    accession_date: toDateParam(item.accession_date),
                    accession_time: item.accession_time || null,
                    collection_date: toDateParam(item.collection_date),
                    collection_time: item.collection_time || null
                }))
            }, phiPolicySelector());
        }

        // A provider number as the request should carry it. Trimmed and upper-cased, because the form is
        // validated on the trimmed value: a number with a stray space passes every check above and then reaches
        // Medicare as one it cannot match, which it reports as no number at all rather than as a bad one.
        function providerNumberParam(value) {
            return (value || '').trim().toUpperCase() || null;
        }

        // A referral field only travels when it belongs to the chosen route, and only for a specialist claim -
        // the other service types carry no referral requirement.
        function referralField(forType, value) {
            const cfg = $ctrl.eclipseConfig;
            return ($ctrl.requiresReferral() && cfg.referralType === forType && value) ? value : null;
        }

        function buildEligibilityRequest() {
            const cfg = $ctrl.eclipseConfig;

            return angular.extend({
                patient_id: $ctrl.patient.id,
                provider_number: providerNumberParam($ctrl.eclipseConfig.providerNumber),
                // Only when the practice names someone else. Blank means the principal provider rendered it,
                // which the server fills in - sending the same number twice would say nothing extra.
                servicing_provider_number: providerNumberParam(cfg.servicingProviderNumber),
                // Sent with the check, not the lodgement: the check creates the transaction row, and the
                // webhooks that later raise and pay the Xero invoice carry nothing saying which accounts the
                // practice chose.
                account_payment_id: $ctrl.accountPaymentId,
                account_items_id: $ctrl.accountItemsId,
                claim_type_code: cfg.claimTypeCode,
                service_type_code: cfg.serviceTypeCode,
                facility_id: cfg.facilityId,
                // "Fund Payee Id must only be set when Claim Type Code is set to AG, SC or MB" - a Medicare only
                // claim has no fund for one to belong to, and a patient claim is not paid to the surgeon at all.
                fund_payee_id: ($ctrl.acceptsFundPayeeId() && cfg.fundPayeeId) ? cfg.fundPayeeId : null,
                // Where Medicare pays the benefit on an MB or MO claim, and the patient's assignment of it. Sent
                // here as well as on the claim because this is the row the claim is lodged from: dropped, the
                // lodgement has neither, and Medicare refuses either class without both.
                billing_agent_provider_number: $ctrl.requiresBillingAgent() ? providerNumberParam(cfg.billingAgentProviderNumber) : null,
                benefit_assignment_authorised: $ctrl.requiresBillingAgent() && cfg.benefitAssignmentAuthorised,
                admission_date: toDateParam(cfg.admissionDate),
                discharge_date: toDateParam(cfg.dischargeDate),
                same_day: cfg.sameDay,
                // Only on an overnight stay. ECLIPSE takes a length of stay only where the same-day indicator is
                // not set, so a same-day check sends none rather than a nought.
                length_of_stay: cfg.sameDay ? null : (Number(cfg.lengthOfStay) || null),
                pre_existing_ailment: cfg.preExistingAilment,
                emergency_admission: cfg.emergencyAdmission,
                compensation_claim: cfg.compensationClaim,
                // "Accident Indicator must only be supplied if Claim Type Code is set to AG, SC, MB or PC", so a
                // Medicare only claim carries none - not even an N.
                accident: $ctrl.acceptsAccidentIndicator() && cfg.accident,
                accident_date: ($ctrl.acceptsAccidentIndicator() && cfg.accident) ? toDateParam(cfg.accidentDate) : null,
                presenting_illness_item_number: cfg.presentingIllnessType === 'item' ? cfg.presentingIllnessItemNumber : null,
                presenting_illness_code: cfg.presentingIllnessType === 'code' ? cfg.presentingIllnessCode : null,
                // Mandatory on the check whatever the class - it is the patient authorising the practice to ask
                // the fund about their cover. On the claim promoted from this row it is the patient claim's alone,
                // which the server decides rather than this.
                submission_authority: cfg.consent,
                // No account paid here. Whether the patient has settled the account is not known when the quote is
                // taken - the quote is run before the surgery and the claim goes after it - so it is asked on the
                // lodge step and travels with the lodgement instead. See lodgeEclipseClaim().
                account_reference_id: cfg.accountReferenceId || String($ctrl.patient.id),
                // Stored on the quote so the claim promoted from it lodges the same reference the practice
                // reconciles the settlement against.
                invoice_reference: cfg.invoiceReference || null,
                // The claimant, where the practice named one. Sent only on the class that can carry one, and
                // sent as null otherwise so a claimant typed against a patient claim and then moved to another
                // class cannot reach Medicare. All null is the ordinary case: the server then sends the patient
                // as their own claimant.
                claimant_first_name: $ctrl.requiresClaimant() ? (cfg.claimantFirstName || null) : null,
                claimant_last_name: $ctrl.requiresClaimant() ? (cfg.claimantLastName || null) : null,
                claimant_date_of_birth: $ctrl.requiresClaimant() ? toDateParam(cfg.claimantDateOfBirth) : null,
                claimant_medicare_number: $ctrl.requiresClaimant() ? (cfg.claimantMedicareNumber || null) : null,
                claimant_medicare_irn: $ctrl.requiresClaimant() ? (cfg.claimantMedicareIrn || null) : null,
                ifc_issue_code: $ctrl.requiresIfcIssueCode() ? cfg.ifcIssueCode : null,
                financial_interest_disclosure: $ctrl.requiresFinancialInterestDisclosure() && cfg.financialInterestDisclosure,
                // Only one route is ever sent; the others go as null so ECLIPSE is not handed a referral and an
                // override that contradict each other.
                referring_provider_number: providerNumberParam(referralField('REFERRAL', cfg.referringProviderNumber)),
                referral_date: toDateParam(referralField('REFERRAL', cfg.referralDate)),
                referral_period: referralField('REFERRAL', cfg.referralPeriod),
                // Only on a non-standard period. ECLIPSE takes a length only against the code that has to
                // declare one, so the other two send none rather than a nought.
                referral_period_months: $ctrl.requiresReferralPeriodMonths()
                    ? (Number(cfg.referralPeriodMonths) || null) : null,
                referral_override_code: referralField('OVERRIDE', cfg.referralOverrideCode),
                self_deemed_code: referralField('SELF_DEEMED', cfg.selfDeemedCode),
                services: claimItems().map(item => ({
                    item_code: item.item_code,
                    // Carried through so the quote, and the claim promoted from it, read back with a description
                    // rather than a bare MBS number.
                    name: item.name,
                    charge_amount: item.charge_amount,
                    service_date: toDateParam(item.service_date) || toDateParam(cfg.admissionDate),
                    // The STUB code / supporting text this line is assessed against. Null rather than an empty
                    // string when left blank, so the stored quote does not carry text it would then lodge.
                    service_text: serviceTextOf(item) || null,
                    reference: item.reference || null,
                    service_time: item.service_time || null,
                    time_duration: (item.time_duration === '' || item.time_duration === null) ? null : Number(item.time_duration),
                    number_of_patients_seen: (item.number_of_patients_seen === '' || item.number_of_patients_seen === null)
                        ? null : Number(item.number_of_patients_seen),
                    duplicate_service_override: !!item.duplicate_service_override,
                    multiple_procedure_override: !!item.multiple_procedure_override,
                    aftercare_override: !!item.aftercare_override,
                    service_quantity: (item.service_quantity === '' || item.service_quantity === null) ? null : Number(item.service_quantity),
                    self_deemed_code: item.self_deemed_code || null,
                    lsp_number: item.lsp_number || null,
                    scp_id: item.scp_id || null,
                    rule3_exempt: !!item.rule3_exempt,
                    s4b3_exempt: !!item.s4b3_exempt,
                    accession_date: toDateParam(item.accession_date),
                    accession_time: item.accession_time || null,
                    collection_date: toDateParam(item.collection_date),
                    collection_time: item.collection_time || null
                }))
            }, phiPolicySelector());
        }

        // Rolls the per-item estimates up into the figures quoted to the patient.
        function summariseQuote(quote) {
            if (!quote || !quote.services || quote.services.length === 0) {
                return null;
            }
            return quote.services.reduce(function (totals, service) {
                totals.charge += service.charge_amount || 0;
                totals.healthFundBenefit += service.health_fund_benefit || 0;
                totals.medicareBenefit += service.medicare_benefit || 0;
                totals.gap += service.gap || 0;
                return totals;
            }, { charge: 0, healthFundBenefit: 0, medicareBenefit: 0, gap: 0 });
        }

        /**
         * Whether the eligibility check came back with anything to say about the patient's cover.
         *
         * The assessment panel is worth its space when the fund answered - the excess and co-payment are money
         * the patient owes on top of the gap, a possible pre-existing ailment can void the benefit, and an
         * exclusion means there is none. It is worth nothing when the fund returned a code and no detail, which
         * is a heading over an empty table.
         *
         * The code itself is deliberately not counted: it is shown on the quote's own summary either way, and on
         * its own it is not detail about the cover.
         */
        $ctrl.hasAssessmentDetail = function () {
            const quote = $ctrl.eclipseQuote;
            if (!quote) {
                return false;
            }
            return isPresent(quote.assessment_text)
                || isPresent(quote.financial)
                || !!quote.potential_pea
                || isPresent(quote.excess_amount)
                || isPresent(quote.co_payment_amount)
                || isPresent(quote.benefit_limitations)
                || isPresent(quote.exclusion_description)
                || isPresent(quote.table_name)
                || isPresent(quote.fund_explanation);
        };

        // Present meaning "the fund sent this", which false and nought both are - a non-financial member and a
        // nil excess are answers, not absences.
        function isPresent(value) {
            return value !== null && value !== undefined && value !== '';
        }

        $ctrl.assessmentLabel = function (code) {
            if (code === 'A') return 'Accepted';
            if (code === 'W') return 'Warning';
            if (code === 'R') return 'Rejected';
            if (code === 'C') return 'Complete';
            return code;
        };

        // Whether the fund's processing report came back with a name that is not the one the claim was
        // submitted under. Compared case- and whitespace-insensitively, so "HARRIS" against "Harris" is not
        // reported as a discrepancy the practice then has to dismiss.
        $ctrl.fundNameDiffers = function () {
            const lodged = $ctrl.lodgedTransaction;
            if (!lodged || (!lodged.fund_verified_first_name && !lodged.fund_verified_last_name)) {
                return false;
            }
            return !sameName(lodged.fund_verified_first_name, $ctrl.patient.firstName)
                || !sameName(lodged.fund_verified_last_name, $ctrl.patient.lastName);
        };

        function sameName(a, b) {
            // A name the report simply did not carry is not a discrepancy.
            if (!a) return true;
            return ((a || '').trim().toLowerCase()) === ((b || '').trim().toLowerCase());
        }

        // Whether the report has said anything per-item yet. The table is worth showing only once it has -
        // before that it is a grid of blanks that reads as though the fund paid nothing.
        $ctrl.hasAssessedItems = function () {
            const items = $ctrl.lodgedTransaction && $ctrl.lodgedTransaction.transaction_items;
            return !!items && items.some(item => item.benefit_amount != null || item.explanation_code || item.status);
        };

        // Each funder's part of the lodged claim's benefit, added up over its items, or null where neither has
        // been recorded. The benefit shown is the two together - what an agreement or scheme claim pays the
        // practice, the fund passing Medicare's on - while Tyro Health Online lists them apart, so a $450 claim
        // there reads as two $225 assessments. Split here too, so the two screens agree on sight.
        $ctrl.lodgedBenefitBreakdown = function () {
            const items = ($ctrl.lodgedTransaction && $ctrl.lodgedTransaction.transaction_items) || [];
            let medicare = null;
            let healthFund = null;
            items.forEach(function (item) {
                if (item.medicare_benefit_amount != null) medicare = (medicare || 0) + item.medicare_benefit_amount;
                if (item.health_fund_benefit_amount != null) healthFund = (healthFund || 0) + item.health_fund_benefit_amount;
            });
            if (medicare === null && healthFund === null) return null;
            return {
                medicare: medicare === null ? null : roundToCents(medicare),
                healthFund: healthFund === null ? null : roundToCents(healthFund)
            };
        };

        $ctrl.handleTransactionComplete = function (status, payload) {
            $ctrl.funderSelected = false;
            // The transaction could not be created, so the SDK never opened. Kept on the funder choice with the
            // reason, rather than handed to the host as though a claim had finished.
            if (status === 'CREATE_FAILED') {
                return showValidationError("Transaction Not Started",
                    "The claim could not be started with Tyro. Please try again.");
            }
            if ($ctrl.onComplete) {
                $ctrl.onComplete({ status: status, payload: payload });
            }
        };
    }
]);
